export type Locale = "de" | "en" | "da" | "ar" | "uk" | "ru" | "vi" | "es";

/**
 * Display order in the language switcher (two rows: DE EN DA AR, then UA RU
 * VI; operator, 03.10.2026). `flag` is the short code shown next to the flag:
 * Ukrainian shows "UA", since "UK" reads as the United Kingdom (the URL prefix
 * and hreflang stay the language code, /uk).
 */
export const LOCALES: { code: Locale; label: string; flag: string }[] = [
  { code: "de", label: "Deutsch", flag: "DE" },
  { code: "en", label: "English", flag: "EN" },
  { code: "da", label: "Dansk", flag: "DA" },
  { code: "ar", label: "العربية", flag: "AR" },
  { code: "uk", label: "Українська", flag: "UA" },
  { code: "ru", label: "Русский", flag: "RU" },
  { code: "vi", label: "Tiếng Việt", flag: "VI" },
  { code: "es", label: "Español", flag: "ES" },
];

/** Every locale code, German first. */
export const LOCALE_CODES: readonly Locale[] = ["de", "en", "da", "ar", "uk", "ru", "vi", "es"];

/** Locales served under a URL prefix (/en, /da, /ar, /uk, /ru, /vi, /es); German has none. */
export const PREFIXED_LOCALES: readonly Exclude<Locale, "de">[] = ["en", "da", "ar", "uk", "ru", "vi", "es"];

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALE_CODES as readonly string[]).includes(value);
}

/** Right-to-left scripts. */
export function localeDir(locale: Locale): "rtl" | "ltr" {
  return locale === "ar" ? "rtl" : "ltr";
}

/**
 * The three languages the legal texts, the contract PDF and the operator's
 * tools exist in. Arabic, Ukrainian, Russian and Vietnamese readers get the
 * English version there: contract and procedure language stay German, and an
 * unreviewed translation of a legal text could create obligations (decision of
 * 03.10.2026).
 */
export type BaseLocale = "de" | "en" | "da";
export function baseLocale(locale: Locale): BaseLocale {
  return locale === "de" || locale === "da" ? locale : "en";
}

/**
 * BCP 47 tag for Intl date and number formatting. Arabic keeps Western digits
 * (0–9), as everywhere else on the site.
 */
export function intlTag(locale: Locale): string {
  switch (locale) {
    case "en": return "en-GB";
    case "da": return "da-DK";
    case "ar": return "ar-u-nu-latn";
    case "uk": return "uk-UA";
    case "ru": return "ru-RU";
    case "vi": return "vi-VN";
    case "es": return "es-ES";
    default: return "de-DE";
  }
}

/**
 * Euro amount as written on the site: "€649.99" in English and Arabic,
 * "€649,99" in German, Danish, Ukrainian, Russian, Vietnamese and Spanish.
 */
export function formatEuro(amount: number, locale: Locale): string {
  const fixed = amount.toFixed(2);
  return `€${locale === "en" || locale === "ar" ? fixed : fixed.replace(".", ",")}`;
}

/**
 * Noun form for a count. `one` and `other` exist in every language; Ukrainian
 * and Russian also need `few` (2–4, 22–24 …) and `many` (5–20, 25–30 …),
 * Arabic `two`, `few` (3–10) and `many` (11–99). A missing form falls back to
 * `other`.
 */
export type PluralForms = { one: string; other: string; two?: string; few?: string; many?: string };
export function pluralForm(locale: Locale, n: number, forms: PluralForms): string {
  const rule = new Intl.PluralRules(intlTag(locale)).select(n) as keyof PluralForms | "zero";
  return (rule !== "zero" && forms[rule]) || (rule === "one" ? forms.one : forms.other);
}

/** Fallback for `t()`/`tAny()` when a key is missing in the active locale.
 *  German is the most complete locale, so it is shown instead of an empty string. */
export const DEFAULT_LOCALE: Locale = "de";

/**
 * Locale for first-time visitors whose language cannot be determined (no
 * cookie, no URL prefix, no matching Accept-Language, no geo hit). Resolution
 * order is cookie, geo header, Accept-Language, then this value.
 */
export const INITIAL_LOCALE: Locale = "de";

/**
 * Normalizes an arbitrary language string ("de-DE", "en-US", an Accept-Language
 * tag …) to one of the UI locales.
 *
 * Pass `"de"` explicitly as fallback when resolving `couples.language`, so
 * legacy records without that field keep receiving German communication.
 *
 * Pure utility with no server APIs, so it is usable on client and server.
 */
export function normalizeLocale(
  lang?: string | null,
  fallback: Locale = INITIAL_LOCALE,
): Locale {
  if (!lang) return fallback;
  const lower = lang.toLowerCase();
  if (lower.startsWith("en")) return "en";
  if (lower.startsWith("da")) return "da";
  if (lower.startsWith("de")) return "de";
  if (lower.startsWith("ar")) return "ar";
  if (lower.startsWith("uk")) return "uk";
  if (lower.startsWith("ru")) return "ru";
  if (lower.startsWith("vi")) return "vi";
  if (lower.startsWith("es")) return "es";
  return fallback;
}

/**
 * All user-facing copy. Tone is factual and restrained: no percentage claims,
 * no money-back or guarantee promises — such statements would have to be
 * verifiable and contractually backed (UWG).
 */
export const translations = {
  // ─────────────────────────────────────────── COMMON / HEADER / NAV
  common: {
    /** Alt-text suffix for stock/mood images, so they never read as a real
     *  customer photo or implied testimonial. */
    moodImage: {
      de: "Stimmungsbild",
      en: "Mood image",
      da: "Stemningsbillede",
      ar: "صورة تعبيرية",
      uk: "Ілюстративне фото",
      ru: "Атмосферное фото",
      vi: "Ảnh minh họa",
      es: "Imagen ilustrativa",
    },
    languageLabel: {
      de: "Sprache",
      en: "Language",
      da: "Sprog",
      ar: "اللغة",
      uk: "Мова",
      ru: "Язык",
      vi: "Ngôn ngữ",
      es: "Idioma",
    },
    menuOpen: {
      de: "Menü öffnen",
      en: "Open menu",
      da: "Åbn menu",
      ar: "فتح القائمة",
      uk: "Відкрити меню",
      ru: "Открыть меню",
      vi: "Mở menu",
      es: "Abrir menú",
    },
    menuClose: {
      de: "Menü schliessen",
      en: "Close menu",
      da: "Luk menu",
      ar: "إغلاق القائمة",
      uk: "Закрити меню",
      ru: "Закрыть меню",
      vi: "Đóng menu",
      es: "Cerrar menú",
    },
    close: {
      de: "Schliessen",
      en: "Close",
      da: "Luk",
      ar: "إغلاق",
      uk: "Закрити",
      ru: "Закрыть",
      vi: "Đóng",
      es: "Cerrar",
    },
    // aria-label of the header's main navigation (desktop, second row).
    mainNav: {
      de: "Hauptnavigation",
      en: "Main navigation",
      da: "Hovednavigation",
      ar: "التنقل الرئيسي",
      uk: "Головна навігація",
      ru: "Основная навигация",
      vi: "Điều hướng chính",
      es: "Navegación principal",
    },
    navCheck: {
      de: "Eignungs-Check",
      en: "Eligibility check",
      da: "Egnethedstjek",
      ar: "فحص الأهلية",
      uk: "Перевірка можливості шлюбу",
      ru: "Бесплатная проверка",
      vi: "Kiểm tra điều kiện",
      es: "Comprobación de requisitos",
    },
    navPricing: {
      de: "Preise & Services",
      en: "Pricing & Services",
      da: "Priser & services",
      ar: "الأسعار والخدمات",
      uk: "Ціни та послуги",
      ru: "Цены и услуги",
      vi: "Giá và dịch vụ",
      es: "Precios y servicios",
    },
    navAbout: {
      de: "Über uns",
      en: "About us",
      da: "Om os",
      ar: "من نحن",
      uk: "Про нас",
      ru: "О нас",
      vi: "Về chúng tôi",
      es: "Sobre nosotros",
    },
    navRatgeber: {
      de: "Blog",
      en: "Blog",
      da: "Blog",
      ar: "الدليل",
      uk: "Блог",
      ru: "Справочник",
      vi: "Cẩm nang",
      es: "Blog",
    },
    // The header's one action (desktop bar and mobile menu). Leads to
    // /anfrage, where the package is chosen before the checkout. The free
    // check is not repeated there: the hero and the phone's bar carry it.
    navBook: {
      de: "Jetzt buchen",
      en: "Book now",
      da: "Book nu",
      ar: "احجزوا الآن",
      uk: "Забронювати",
      ru: "Забронировать",
      vi: "Đặt ngay",
      es: "Reservar ahora",
    },
    navContact: {
      de: "Kontakt",
      en: "Contact",
      da: "Kontakt",
      ar: "تواصلوا معنا",
      uk: "Контакти",
      ru: "Контакты",
      vi: "Liên hệ",
      es: "Contacto",
    },
    // Header chat status. The "open" line may only show while
    // isSupportOpen() is true — it promises a person at the other end.
    chatOpenKicker: {
      de: "Wir sind jetzt für euch da",
      en: "We're open — come on in",
      da: "Vi er her nu – kig indenfor",
      ar: "نحن متاحون الآن، تفضّلوا",
      uk: "Ми на зв’язку — пишіть",
      ru: "Мы на связи — пишите",
      vi: "Chúng tôi đang trực tuyến — mời các bạn nhắn tin",
      es: "Ahora estamos aquí para ustedes",
    },
    chatClosedKicker: {
      de: "Ab 8 Uhr wieder für euch da",
      en: "Back at 8 am (Swiss time)",
      da: "Vi er tilbage kl. 8",
      ar: "نعود في الساعة 8 صباحًا (بتوقيت سويسرا)",
      uk: "Знову на зв’язку о 8:00 (за швейцарським часом)",
      ru: "Снова на связи с 8:00 (по швейцарскому времени)",
      vi: "Trở lại lúc 8:00 (giờ Thụy Sĩ)",
      es: "De vuelta a las 8:00 (hora suiza)",
    },
    chatHours: {
      de: "Mo–So 8–22 Uhr",
      en: "Mon–Sun 8 am–10 pm",
      da: "Man–søn kl. 8–22",
      ar: "من الاثنين إلى الأحد، 8 صباحًا–10 مساءً",
      uk: "Пн–Нд 8:00–22:00",
      ru: "Пн–Вс 8:00–22:00",
      vi: "Thứ Hai–Chủ nhật 8:00–22:00",
      es: "Lun–dom 8:00–22:00",
    },
    // Short status next to the header's WhatsApp badge, open hours only.
    chatOnline: {
      de: "Jetzt online",
      en: "We're open",
      da: "Vi er online",
      ar: "نحن متاحون",
      uk: "Зараз онлайн",
      ru: "Сейчас на связи",
      vi: "Chúng tôi đang trực tuyến",
      es: "En línea ahora",
    },
    navExperiences: {
      de: "Testimonials",
      en: "Testimonials",
      da: "Testimonials",
      ar: "آراء العملاء",
      uk: "Відгуки",
      ru: "Отзывы",
      vi: "Cảm nhận khách hàng",
      es: "Testimonios",
    },
    email: {
      de: "E-Mail",
      en: "Email",
      da: "E-mail",
      ar: "البريد الإلكتروني",
      uk: "Електронна пошта",
      ru: "E-mail",
      vi: "Email",
      es: "Correo",
    },
  },

  // ─────────────────────────────────────────── ANNOUNCEMENT-BAR
  announcement: {
    leadMagnet: {
      de: "Kostenloser Check: Könnt ihr in Dänemark heiraten?",
      en: "Free check: can you marry in Denmark?",
      da: "Gratis tjek: Kan I blive gift i Danmark?",
      ar: "فحص مجاني: هل يمكنكم الزواج في الدنمارك؟",
      uk: "Безкоштовна перевірка: чи можете ви одружитися в Данії?",
      ru: "Бесплатная проверка: можете ли вы пожениться в Дании?",
      vi: "Kiểm tra miễn phí: hai bạn có thể kết hôn tại Đan Mạch không?",
      es: "Comprobación gratuita: ¿pueden casarse en Dinamarca?",
    },
  },

  // ─────────────────────────────────────────── HERO
  hero: {
    eyebrow: {
      de: "Schweizer Service · DACH + international",
      en: "Swiss service · DACH + international",
      da: "Schweizisk service · DACH + internationalt",
      ar: "خدمة سويسرية · DACH + دوليًا",
      uk: "Сервіс зі Швейцарії · країни DACH + міжнародно",
      ru: "Сервис из Швейцарии · DACH + другие страны",
      vi: "Dịch vụ Thụy Sĩ · DACH + quốc tế",
      es: "Servicio suizo · DACH + internacional",
    },
    // The headline: a plain first line and the italic accent (headlineB).
    // headlineC/D stay empty; EditorialSections still reads all three.
    // The accent uses what couples search for (Google suggestions,
    // 11.10.2026): "schnell", "ohne Ehefähigkeitszeugnis"; "fast", "for
    // foreigners" (no residence in Denmark needed).
    headlineA: {
      de: "Heiraten in Dänemark –",
      en: "Getting married in Denmark –",
      da: "Bryllup i Danmark –",
      ar: "الزواج في الدنمارك –",
      uk: "Одруження в Данії —",
      ru: "Пожениться в Дании —",
      vi: "Kết hôn tại Đan Mạch –",
      es: "Casarse en Dinamarca –",
    },
    headlineB: {
      de: "schnell und ohne Ehefähigkeitszeugnis.",
      en: "fast, even if you don't live there.",
      da: "hurtigt, også for internationale par.",
      ar: "بسرعة، حتى لو لم تكونوا مقيمين هناك.",
      uk: "швидко, навіть якщо ви там не живете.",
      ru: "быстро, даже если вы там не живёте.",
      vi: "nhanh chóng, kể cả khi không sống ở đó.",
      es: "rápido, aunque no vivan allí.",
    },
    headlineC: { de: "", en: "", da: "", ar: "", uk: "", ru: "", vi: "", es: "" },
    headlineD: { de: "", en: "", da: "", ar: "", uk: "", ru: "", vi: "", es: "" },
    lead: {
      // The \n characters are real line breaks, rendered with
      // whitespace-pre-line in the hero. Claim "no authority visits", never
      // "no bureaucracy" — the bureaucracy exists, we only take it over.
      de: "[[In 3–6\u00A0Wochen zum Ja-Wort.]]\nFestpreis. Keine Behördengänge.\nWir übernehmen den Papierkram.",
      en: "[[Say yes in 3–6\u00A0weeks.]]\nFixed price. No authority visits.\nWe handle the paperwork for foreign and binational couples.",
      da: "[[Sig ja om 3–6\u00A0uger.]]\nFast pris. Ingen myndighedsbesøg.\nVi klarer papirarbejdet.",
      ar: "[[قولوا «نعم» خلال 3–6 أسابيع.]]\nسعر ثابت. دون زيارات للدوائر الرسمية.\nنتولّى المعاملات الورقية عن الأزواج الأجانب والأزواج من جنسيتين مختلفتين.",
      uk: "[[Скажіть «так» за 3–6\u00A0тижнів.]]\nФіксована ціна. Жодних походів до установ.\nМи беремо на себе документи для іноземних пар і пар різних громадянств.",
      ru: "[[Скажите «да» через 3–6 недель.]]\nФиксированная цена. Никаких походов по ведомствам.\nМы берём на себя бумажную работу для иностранных пар и пар с разным гражданством.",
      vi: "[[Nói lời đồng ý sau 3–6 tuần.]]\nGiá cố định. Không phải đến cơ quan chức năng.\nChúng tôi lo thủ tục giấy tờ cho các cặp đôi nước ngoài và khác quốc tịch.",
      es: "[[El “sí, quiero” en 3–6 semanas.]]\nPrecio fijo. Sin visitas a oficinas públicas.\nNos encargamos del papeleo para parejas extranjeras y binacionales.",
    },
    ctaPrimary: {
      de: "Kostenloser 60-Sekunden-Check",
      en: "Free 60-second check",
      da: "Gratis 60-sekunders tjek",
      ar: "الفحص المجاني خلال 60 ثانية",
      uk: "Безкоштовна перевірка за 60 секунд",
      ru: "Бесплатная проверка за 60 секунд",
      vi: "Kiểm tra miễn phí trong 60 giây",
      es: "Comprobación gratuita de 60 segundos",
    },
    // Alt text of the first hero photo (real customers, published with consent).
    firstPhotoAlt: {
      de: "Hai & Elsa nach ihrer Trauung in Dänemark",
      en: "Hai & Elsa after their wedding in Denmark",
      da: "Hai & Elsa efter deres vielse i Danmark",
      ar: "Hai وElsa بعد زواجهما في الدنمارك",
      uk: "Hai & Elsa після свого весілля в Данії",
      ru: "Hai и Elsa после регистрации брака в Дании",
      vi: "Hai & Elsa sau đám cưới tại Đan Mạch",
      es: "Hai y Elsa después de su boda en Dinamarca",
    },
    // Hero price lines. These prices must stay identical to the ones on /preise.
    priceEssential: {
      de: "Essential €249,99",
      en: "Essential €249.99",
      da: "Essential €249,99",
      ar: "Essential €249.99",
      uk: "Essential €249,99",
      ru: "Essential €249,99",
      vi: "Essential €249,99",
      es: "Essential €249,99",
    },
    priceEssentialNote: {
      de: "zzgl. Behördengebühr",
      en: "plus authority fee",
      da: "plus myndighedsgebyr",
      ar: "إضافةً إلى الرسوم الرسمية",
      uk: "плюс державний збір",
      ru: "плюс государственный сбор",
      vi: "chưa gồm lệ phí nhà nước",
      es: "más la tasa oficial",
    },
    priceComplete: {
      de: "Complete €649,99",
      en: "Complete €649.99",
      da: "Complete €649,99",
      ar: "Complete €649.99",
      uk: "Complete €649,99",
      ru: "Complete €649,99",
      vi: "Complete €649,99",
      es: "Complete €649,99",
    },
    priceCompleteNote: {
      de: "all-in inkl. Behördengebühr",
      en: "all-in incl. authority fee",
      da: "all-in inkl. myndighedsgebyr",
      ar: "شامل كل شيء، بما فيه الرسوم الرسمية",
      uk: "усе включено, з державним збором",
      ru: "всё включено, с государственным сбором",
      vi: "trọn gói, đã gồm lệ phí nhà nước",
      es: "todo incluido, con la tasa oficial",
    },
    // Third hero price line (24.10.2026): the change of municipality, for couples
    // who already have the approval; links to /gemeindewechsel.
    priceSwitch: {
      de: "Gemeindewechsel €149,99",
      en: "Municipality change €149.99",
      da: "Kommuneskift €149,99",
      ar: "تغيير البلدية €149.99",
      uk: "Зміна комуни €149,99",
      ru: "Смена коммуны €149,99",
      vi: "Đổi kommune €149,99",
      es: "Cambio de municipio €149,99",
    },
    priceSwitchNote: {
      de: "Genehmigung da, kein Termin?",
      en: "approved, but no date?",
      da: "godkendt, men ingen dato?",
      ar: "موافقة دون موعد؟",
      uk: "дозвіл є, дати немає?",
      ru: "разрешение есть, даты нет?",
      vi: "có giấy phép, chưa có ngày?",
      es: "¿con aprobación y sin fecha?",
    },
    // Review line under the hero button (reviewStats.ts): no counts.
    ratingAllFive: {
      de: "Nur 5-Sterne-Bewertungen",
      en: "Only 5-star reviews",
      da: "Kun 5-stjernede anmeldelser",
      ar: "تقييمات 5 نجوم فقط",
      uk: "Лише 5-зіркові відгуки",
      ru: "Только отзывы на 5 звёзд",
      vi: "Chỉ toàn đánh giá 5 sao",
      es: "Solo reseñas de 5 estrellas",
    },
    ratingOnGoogle: {
      de: "Unsere Bewertungen auf Google",
      en: "Our reviews on Google",
      da: "Vores anmeldelser på Google",
      ar: "تقييماتنا على Google",
      uk: "Наші відгуки в Google",
      ru: "Наши отзывы в Google",
      vi: "Đánh giá về chúng tôi trên Google",
      es: "Nuestras reseñas en Google",
    },
    ratingOnTrustpilot: {
      de: "Unsere Bewertungen auf Trustpilot",
      en: "Our reviews on Trustpilot",
      da: "Vores anmeldelser på Trustpilot",
      ar: "تقييماتنا على Trustpilot",
      uk: "Наші відгуки на Trustpilot",
      ru: "Наши отзывы на Trustpilot",
      vi: "Đánh giá về chúng tôi trên Trustpilot",
      es: "Nuestras reseñas en Trustpilot",
    },
  },

  /** Above-the-fold clarification of who does what. */
  rolesRibbon: {
    a: { de: "Wir bereiten vor", en: "We prepare", da: "Vi forbereder", ar: "نحن نحضّر", uk: "Ми готуємо", ru: "Мы готовим", vi: "Chúng tôi chuẩn bị", es: "Nosotros preparamos" },
    b: { de: "Ihr unterschreibt", en: "You sign", da: "I underskriver", ar: "أنتم توقّعون", uk: "Ви підписуєте", ru: "Вы подписываете", vi: "Các bạn ký", es: "Ustedes firman" },
    c: { de: "Bis zur Heiratsurkunde", en: "To the certificate", da: "Til vielsesattesten", ar: "حتى الشهادة", uk: "До свідоцтва про шлюб", ru: "До свидетельства о браке", vi: "Nhận giấy chứng nhận", es: "Hasta el certificado de matrimonio" },
  },

  /** Thin section dividers; the text is optional. */
  editorial: {
    part1: { de: "Ablauf", en: "How it works", da: "Forløb", ar: "كيف تسير العملية", uk: "Як це працює", ru: "Как это работает", vi: "Cách thức hoạt động", es: "Cómo funciona" },
    part2: { de: "Leistung", en: "Service", da: "Ydelse", ar: "الخدمة", uk: "Послуги", ru: "Услуги", vi: "Dịch vụ", es: "Servicio" },
    part3: { de: "Fragen", en: "Questions", da: "Spørgsmål", ar: "الأسئلة", uk: "Запитання", ru: "Вопросы", vi: "Câu hỏi", es: "Preguntas" },
    part4: { de: "Erstkontakt", en: "First contact", da: "Første kontakt", ar: "أول تواصل", uk: "Перший контакт", ru: "Первый контакт", vi: "Liên hệ đầu tiên", es: "Primer contacto" },
    part5: { de: "Paar-Stimmen", en: "Couple stories", da: "Parstemmer", ar: "قصص الأزواج", uk: "Історії пар", ru: "Истории пар", vi: "Chuyện của các cặp đôi", es: "Voces de parejas" },
    part6: {
      de: "Behörden und Transparenz",
      en: "Authorities and clarity",
      da: "Myndigheder og overblik",
      ar: "الجهات الرسمية والوضوح",
      uk: "Органи влади та прозорість",
      ru: "Ведомства и прозрачность",
      vi: "Cơ quan chức năng và sự rõ ràng",
      es: "Autoridades y transparencia",
    },
    part7: { de: "Pakete", en: "Packages", da: "Pakker", ar: "الباقات", uk: "Пакети", ru: "Пакеты", vi: "Các gói", es: "Paquetes" },
  },

  /** Mission section: digital platform plus human review. */
  whyMission: {
    eyebrow: {
      de: "Persönlich begleitet · von Hand geprüft · fair im Vertrag",
      en: "Personally guided · checked by hand · fairly priced",
      da: "Personligt vejledt · gennemgået i hånden · fair i kontrakten",
      ar: "مرافقة شخصية · مراجعة يدوية · سعر عادل",
      uk: "Особистий супровід · ручна перевірка · чесна ціна",
      ru: "Личное сопровождение · ручная проверка · честная цена",
      vi: "Đồng hành trực tiếp · kiểm tra thủ công · giá hợp lý",
      es: "Acompañamiento personal · revisión a mano · contrato justo",
    },
    title: {
      de: "So kombinieren wir Tempo, Sorgfalt und einen fairen Festpreis",
      en: "How we combine speed, care and a fair fixed price",
      da: "Sådan kombinerer vi tempo, omhu og en fair fastpris",
      ar: "كيف نجمع بين السرعة والعناية وسعر ثابت عادل",
      uk: "Як ми поєднуємо швидкість, ретельність і чесну фіксовану ціну",
      ru: "Как мы сочетаем скорость, тщательность и честную фиксированную цену",
      vi: "Cách chúng tôi kết hợp tốc độ, sự chu đáo và mức giá cố định hợp lý",
      es: "Así combinamos rapidez, cuidado y un precio fijo justo",
    },
    body: {
      de: "Wir arbeiten persönlich, kein Grossbüro mit Hotline. Das heisst: Ihr habt eine Person, die euch persönlich kennt, die jedes Dokument selbst prüft und für die ihr keine Nummer seid. Genau so machen wir Hochzeit in Dänemark günstiger und persönlicher zugleich.",
      en: "We work personally, not a call-centre agency. That means one person who knows you personally, reviews every document by hand, and for whom you're not a ticket number. That's how we make weddings in Denmark cheaper and more personal at the same time.",
      da: "Vi arbejder personligt, ikke et stort bureau med hotline. Det betyder: I har én person, som kender jer personligt, som selv tjekker hvert dokument, og for hvem I ikke er et nummer. Sådan gør vi bryllup i Danmark både billigere og mere personligt.",
      ar: "نعمل بشكل شخصي، ولسنا وكالة بمركز اتصال. وهذا يعني شخصًا واحدًا يعرفكم شخصيًا، ويراجع كل وثيقة يدويًا، ولستم بالنسبة إليه مجرد رقم تذكرة. وهكذا نجعل الزواج في الدنمارك أقل تكلفة وأكثر طابعًا شخصيًا في الوقت نفسه.",
      uk: "Ми працюємо особисто, а не як агенція з кол-центром. Це означає: одна людина, яка знає вас особисто, власноруч перевіряє кожен документ і для якої ви не номер у черзі. Саме так ми робимо весілля в Данії водночас дешевшим і більш особистим.",
      ru: "Мы работаем лично, а не как агентство с колл-центром. Это значит: у вас есть человек, который знает вас лично, сам проверяет каждый документ и для которого вы не номер заявки. Так мы делаем свадьбу в Дании одновременно дешевле и более личной.",
      vi: "Chúng tôi làm việc trực tiếp với các bạn, không phải một công ty kiểu tổng đài. Nghĩa là có một người biết rõ các bạn, tự tay kiểm tra từng giấy tờ, và với người đó các bạn không phải là một con số trên phiếu yêu cầu. Nhờ vậy, chúng tôi giúp việc kết hôn tại Đan Mạch vừa tiết kiệm hơn, vừa gần gũi hơn.",
      es: "Trabajamos de forma personal, no somos una gran oficina con línea de atención. Eso significa: tienen una persona que los conoce personalmente, que revisa ella misma cada documento y para la que ustedes no son un número. Así hacemos que casarse en Dinamarca sea más económico y, a la vez, más personal.",
    },
    pillarDigital: {
      de: "Eine feste Ansprechperson, von Anfang bis zur Urkunde.",
      en: "One personal contact, from first call to your certificate.",
      da: "Én fast kontaktperson, fra første opkald til vielsesattesten.",
      ar: "جهة اتصال شخصية واحدة، من أول مكالمة حتى شهادتكم.",
      uk: "Одна особиста контактна особа — від першої розмови до вашого свідоцтва.",
      ru: "Одно контактное лицо — от первого звонка до свидетельства.",
      vi: "Một người phụ trách riêng, từ cuộc gọi đầu tiên đến khi các bạn có giấy chứng nhận.",
      es: "Una persona de contacto fija, desde el inicio hasta el certificado.",
    },
    pillarHuman: {
      de: "Jedes Dokument wird von uns gelesen, bevor es zur Behörde geht.",
      en: "We read every document before it goes to the authority.",
      da: "Vi læser hvert dokument, før det sendes til myndigheden.",
      ar: "نقرأ كل وثيقة قبل أن تصل إلى الجهة الرسمية.",
      uk: "Ми читаємо кожен документ, перш ніж він потрапить до органу влади.",
      ru: "Мы читаем каждый документ, прежде чем он попадёт в ведомство.",
      vi: "Chúng tôi đọc từng giấy tờ trước khi gửi đến cơ quan chức năng.",
      es: "Leemos cada documento antes de enviarlo a la autoridad.",
    },
    pillarFair: {
      de: "Festpreis im Vertrag. Was drinsteht, bekommt ihr, keine Aufschläge.",
      en: "Fixed price in the contract. What's written is what you get, no add-ons.",
      da: "Fast pris i kontrakten. Det, der står, får I, ingen tillæg.",
      ar: "سعر ثابت في العقد. ما هو مكتوب هو ما تحصلون عليه، دون إضافات.",
      uk: "Фіксована ціна в договорі. Що написано, те й отримуєте, без доплат.",
      ru: "Фиксированная цена в договоре. Что написано, то и получаете, без доплат.",
      vi: "Giá cố định trong hợp đồng. Những gì được ghi là những gì các bạn nhận được, không có phụ phí.",
      es: "Precio fijo en el contrato. Reciben lo que está escrito, sin recargos.",
    },
    badgeDigital: {
      de: "Persönliche Begleitung",
      en: "Personal guidance",
      da: "Personlig vejledning",
      ar: "مرافقة شخصية",
      uk: "Особистий супровід",
      ru: "Личное сопровождение",
      vi: "Đồng hành trực tiếp",
      es: "Acompañamiento personal",
    },
    card1Title: {
      de: "Eine Person, die euer Hochzeits-Anliegen kennt",
      en: "One person who knows your wedding by name",
      da: "Én person, som kender jeres bryllup ved navn",
      ar: "شخص واحد يعرف زواجكم عن قرب",
      uk: "Одна людина, яка знає вас на ім’я",
      ru: "Один человек, который знает вас и вашу свадьбу",
      vi: "Một người nắm rõ đám cưới của các bạn",
      es: "Una persona que conoce su caso",
    },
    card1Body: {
      de: "Ihr habt eine feste Ansprechperson, die euch vom ersten Gespräch bis zur Urkunde begleitet. Keine Hotline, keine wechselnden Sachbearbeiter, eine Person, die euch und euren Fall kennt.",
      en: "You get one dedicated contact who walks with you from the first conversation to your certificate. No hotline, no rotating case workers, one person who knows you and your story.",
      da: "I får én fast kontaktperson, som følger jer fra første samtale til vielsesattesten. Ingen hotline, ingen skiftende sagsbehandlere, én person, som kender jer og jeres historie.",
      ar: "تحصلون على شخص واحد مخصّص للتواصل يرافقكم من أول محادثة حتى شهادتكم. لا خط ساخن، ولا موظفون يتبدّلون، بل شخص واحد يعرفكم ويعرف قصتكم.",
      uk: "Ви отримуєте одну постійну контактну особу, яка супроводжує вас від першої розмови до свідоцтва. Жодної гарячої лінії, жодних менеджерів, що постійно змінюються, — одна людина, яка знає вас і вашу історію.",
      ru: "У вас есть одно личное контактное лицо, которое сопровождает вас от первого разговора до свидетельства. Никакой горячей линии и сменяющихся сотрудников — один человек, который знает вас и вашу историю.",
      vi: "Các bạn có một người phụ trách riêng đồng hành từ buổi trao đổi đầu tiên đến khi có giấy chứng nhận. Không đường dây nóng, không thay đổi người xử lý, chỉ một người hiểu các bạn và câu chuyện của các bạn.",
      es: "Tienen una persona de contacto fija que los acompaña desde la primera conversación hasta el certificado. Sin línea de atención, sin gestores que cambian, una persona que los conoce a ustedes y conoce su caso.",
    },
    card2Title: {
      de: "Wir prüfen jedes Dokument persönlich",
      en: "We check every document by hand",
      da: "Vi tjekker hvert dokument personligt",
      ar: "نراجع كل وثيقة يدويًا",
      uk: "Ми перевіряємо кожен документ вручну",
      ru: "Мы проверяем каждый документ вручную",
      vi: "Chúng tôi tự tay kiểm tra từng giấy tờ",
      es: "Revisamos cada documento personalmente",
    },
    card2Body: {
      de: "Bevor irgendetwas zur Behörde geht, lesen wir jeden Scan, jede Angabe und jede Apostille. Fehler fangen wir hier ab, nicht erst in der Ablehnung sechs Wochen später.",
      en: "Before anything goes to the authority, we read every scan, every detail, every apostille. We catch mistakes here, not in a rejection letter six weeks later.",
      da: "Før noget sendes til myndigheden, læser vi hver scanning, hver oplysning, hver apostille. Vi fanger fejl her, ikke i et afvisningsbrev seks uger senere.",
      ar: "قبل أن يصل أي شيء إلى الجهة الرسمية، نقرأ كل نسخة ممسوحة، وكل تفصيل، وكل أبوستيل. نكتشف الأخطاء هنا، لا في خطاب رفض بعد ستة أسابيع.",
      uk: "Перш ніж щось потрапить до органу влади, ми читаємо кожен скан, кожну деталь, кожен апостиль. Ми виправляємо помилки тут, а не після листа з відмовою через шість тижнів.",
      ru: "Прежде чем что-либо попадёт в ведомство, мы читаем каждый скан, каждую деталь, каждый апостиль. Мы находим ошибки здесь, а не в письме с отказом шесть недель спустя.",
      vi: "Trước khi bất cứ thứ gì được gửi đến cơ quan chức năng, chúng tôi đọc từng bản scan, từng chi tiết, từng chứng nhận Apostille. Chúng tôi phát hiện sai sót ngay ở bước này, chứ không phải qua một thư từ chối sáu tuần sau.",
      es: "Antes de que algo llegue a la autoridad, leemos cada escaneo, cada dato y cada apostilla. Detectamos los errores aquí, no en un rechazo seis semanas después.",
    },
    card3Title: {
      de: "Festpreis. Im Vertrag. Schwarz auf weiss.",
      en: "Fixed price. In the contract. In writing.",
      da: "Fast pris. I kontrakten. Sort på hvidt.",
      ar: "سعر ثابت. في العقد. كتابيًا.",
      uk: "Фіксована ціна. У договорі. Письмово.",
      ru: "Фиксированная цена. В договоре. В письменном виде.",
      vi: "Giá cố định. Trong hợp đồng. Bằng văn bản.",
      es: "Precio fijo. En el contrato. Por escrito.",
    },
    card3Body: {
      de: "€649,99 Service-Festpreis für unser Complete-Paket. Die AFL-Behördengebühr ist enthalten. Apostille ist nicht im Paket, falls nötig, bucht ihr unseren Apostille-Service (€149,99) als Add-on. Keine Aufschläge, keine versteckten Posten, was im Vertrag steht, gilt.",
      en: "€649.99 service flat fee for our Complete package. The AFL government fee is included. Apostille is not in the package, if needed, you book our Apostille Service (€149.99) as an add-on. No surcharges, no hidden line items, what's in the contract is what counts.",
      da: "€649,99 service-fastpris for vores Complete-pakke. AFL-myndighedsgebyret er inkluderet. Apostille er ikke i pakken, har I brug for det, bestiller I vores Apostille-Service (€149,99) som tillæg. Ingen tillæg fra os, ingen skjulte poster, det i kontrakten gælder.",
      ar: "رسوم ثابتة للخدمة قدرها €649.99 لباقة Complete. ورسوم AFL الحكومية مشمولة. أما الأبوستيل فغير مشمول في الباقة؛ وعند الحاجة تحجزون خدمة الأبوستيل لدينا (€149.99) كخدمة إضافية. لا رسوم إضافية، ولا بنود خفية، وما في العقد هو ما يُعتمد.",
      uk: "€649,99 — фіксована вартість послуг у пакеті Complete. Державний збір AFL включено. Апостиль у пакет не входить: за потреби ви замовляєте нашу послугу апостилю (€149,99) як додаткову. Жодних доплат, жодних прихованих статей витрат — діє те, що записано в договорі.",
      ru: "€649,99 — фиксированная стоимость услуг в пакете Complete. Государственный сбор AFL включён. Апостиль в пакет не входит: при необходимости вы заказываете нашу услугу апостиля (€149,99) дополнительно. Никаких доплат и скрытых позиций — действует то, что написано в договоре.",
      vi: "Phí dịch vụ trọn gói €649,99 cho gói Complete. Đã gồm lệ phí nhà nước AFL. Apostille không nằm trong gói; nếu cần, các bạn đặt thêm dịch vụ Apostille của chúng tôi (€149,99). Không phụ thu, không khoản mục ẩn, những gì ghi trong hợp đồng mới là căn cứ.",
      es: "€649,99 de precio fijo del servicio para nuestro paquete Complete. La tasa oficial de la AFL está incluida. La apostilla no forma parte del paquete; si la necesitan, reservan nuestro servicio de apostilla (€149,99) como servicio adicional. Sin recargos, sin partidas ocultas: lo que dice el contrato es lo que vale.",
    },
  },

  // ─────────────────────────────────────────── NUMBERS-STRIP
  numbers: {
    eyebrow: {
      de: "Worauf ihr euch verlassen könnt",
      en: "What you can count on",
      da: "Det I kan regne med",
      ar: "ما يمكنكم الاعتماد عليه",
      uk: "На що ви можете розраховувати",
      ru: "На что вы можете рассчитывать",
      vi: "Những điều các bạn có thể tin cậy",
      es: "Con qué pueden contar",
    },
    headlineA: {
      de: "Zahlen, die wir",
      en: "Numbers we put",
      da: "Tal, vi skriver",
      ar: "أرقام نُثبتها",
      uk: "Цифри, які ми",
      ru: "Цифры, которые мы",
      vi: "Những con số chúng tôi ghi",
      es: "Cifras que dejamos",
    },
    headlineHighlight: {
      de: "schwarz auf weiss",
      en: "in writing",
      da: "sort på hvidt",
      ar: "كتابيًا",
      uk: "чорним по білому",
      ru: "письменно",
      vi: "bằng văn bản",
      es: "por escrito",
    },
    headlineB: {
      de: "in den Vertrag schreiben.",
      en: "directly into your contract.",
      da: "direkte i kontrakten.",
      ar: "مباشرةً في عقدكم.",
      uk: "вписуємо прямо у ваш договір.",
      ru: "закрепляем прямо в вашем договоре.",
      vi: "thẳng vào hợp đồng.",
      es: "en su contrato.",
    },
    statCouples: {
      de: "Begleitete Paare",
      en: "Couples guided",
      da: "Par vejledt",
      ar: "أزواج رافقناهم",
      uk: "Пар супроводжено",
      ru: "Сопровождённых пар",
      vi: "Cặp đôi đã được đồng hành",
      es: "Parejas acompañadas",
    },
    statNationalities: {
      de: "Nationalitäten",
      en: "Nationalities",
      da: "Nationaliteter",
      ar: "جنسيات",
      uk: "Національностей",
      ru: "Гражданств",
      vi: "Quốc tịch",
      es: "Nacionalidades",
    },
    statRating: {
      de: "Ø Bewertung",
      en: "Avg. rating",
      da: "Gns. bedømmelse",
      ar: "متوسط التقييم",
      uk: "Середня оцінка",
      ru: "Средняя оценка",
      vi: "Điểm đánh giá trung bình",
      es: "Valoración media",
    },
    statExperience: {
      de: "Jahre Erfahrung",
      en: "Years of experience",
      da: "Års erfaring",
      ar: "سنوات الخبرة",
      uk: "Років досвіду",
      ru: "Лет опыта",
      vi: "Năm kinh nghiệm",
      es: "Años de experiencia",
    },
    partnerTitle: {
      de: "Anerkennung und Partner",
      en: "Recognition and partners",
      da: "Anerkendelse og partnere",
      ar: "التقدير والشركاء",
      uk: "Визнання та партнери",
      ru: "Признание и партнёры",
      vi: "Ghi nhận và đối tác",
      es: "Reconocimiento y colaboradores",
    },
    // HomeTrustBand stats. These describe our method, not results, so they stay
    // verifiable; never replace them with performance or success figures.
    stat1Label: {
      de: "Manuell geprüft",
      en: "Manually reviewed",
      da: "Manuelt gennemgået",
      ar: "مراجعة يدوية",
      uk: "Перевірено вручну",
      ru: "Проверено вручную",
      vi: "Kiểm tra thủ công",
      es: "Revisado a mano",
    },
    // Title of the first figure in the HomeTrustBand grid (number + label).
    stat1Title: {
      de: "100\u00A0% manuell geprüft",
      en: "100% manually reviewed",
      da: "100\u00A0% manuelt gennemgået",
      ar: "مراجعة يدوية بنسبة 100%",
      uk: "100% перевірено вручну",
      ru: "100% ручная проверка",
      vi: "100% kiểm tra thủ công",
      es: "100 % revisado a mano",
    },
    stat1Sub: {
      de: "Jeder Scan, jeder Antrag, ein Mensch liest mit",
      en: "Every scan and every application is read by a person, not just software",
      da: "Hver scanning, hver ansøgning, et menneske læser med",
      ar: "كل نسخة ممسوحة وكل طلب يقرؤه إنسان، لا برنامج فقط",
      uk: "Кожен скан і кожну заяву читає людина, а не лише програма",
      ru: "Каждый скан и каждое заявление читает человек, а не только программа",
      vi: "Mọi bản scan và mọi hồ sơ đều được người thật đọc, không chỉ phần mềm",
      es: "Una persona lee cada escaneo y cada solicitud",
    },
    stat2Suffix: {
      de: "Wochen",
      en: "weeks",
      da: "uger",
      ar: "أسابيع",
      uk: "тижнів",
      ru: "недель",
      vi: "tuần",
      es: "semanas",
    },
    // The advertised 3-6 week range must always be accompanied by the official
    // Familieretshuset's own processing time in the sub-line, otherwise the claim is unsourced.
    stat2Label: {
      de: "Bei vollständigen Unterlagen",
      en: "With complete documents",
      da: "Med komplette dokumenter",
      ar: "مع اكتمال الوثائق",
      uk: "З повним пакетом документів",
      ru: "При полном комплекте документов",
      vi: "Với giấy tờ đầy đủ",
      es: "Con documentos completos",
    },
    stat2Sub: {
      de: "Ab vollständigen Unterlagen, je nach Kommune. Das Familieretshuset entscheidet vollständige Anträge in der Regel binnen 5\u00A0Arbeitstagen; danach zählt der Termin der Kommune (Tønder und Aabenraa schalten Termine 60\u00A0Tage vorher frei, Ærø 10\u00A0Wochen, Kopenhagen bis zu 4\u00A0Monate). Stand Oktober 2026.",
      en: "From complete documents, depending on the municipality. Familieretshuset usually decides complete applications within 5\u00A0working days; after that, the municipality's date is what counts (Tønder and Aabenraa release dates 60\u00A0days ahead, Ærø 10\u00A0weeks, Copenhagen up to 4\u00A0months). As of October 2026.",
      da: "Fra komplette dokumenter, afhængigt af kommunen. Familieretshuset afgør komplette ansøgninger som regel inden for 5\u00A0arbejdsdage; derefter afhænger det af kommunens ledige tider (Tønder og Aabenraa frigiver tider 60\u00A0dage før, Ærø 10\u00A0uger, København op til 4\u00A0måneder). Pr. oktober 2026.",
      ar: "من اكتمال الوثائق، بحسب البلدية. تبتّ Familieretshuset عادةً في الطلبات المكتملة خلال 5 أيام عمل؛ وبعد ذلك يتوقف الأمر على موعد البلدية (في Tønder وAabenraa تُتاح المواعيد للحجز قبل 60 يومًا، وفي Ærø بعد 10 أسابيع، وفي كوبنهاغن حتى 4 أشهر). بحسب الوضع في أكتوبر 2026.",
      uk: "Від моменту, коли документи повні, залежно від комуни (муніципалітету). Familieretshuset (Агентство сімейного права Данії) зазвичай розглядає повні заяви протягом 5\u00A0робочих днів; після цього все залежить від дати в комуні: у Теннері (Tønder) та Обенро (Aabenraa) дати відкривають за 60\u00A0днів, на Ере (Ærø) — 10\u00A0тижнів, у Копенгагені — до 4\u00A0місяців. Станом на жовтень 2026 р.",
      ru: "С момента, когда все документы собраны, в зависимости от коммуны. Familieretshuset по полным заявлениям обычно принимает решение в течение 5 рабочих дней; дальше всё зависит от даты в коммуне: в Тённере (Tønder) и Обенро (Aabenraa) даты открывают за 60\u00A0дней, на Эрё (Ærø) — 10 недель, в Копенгагене — до 4 месяцев. По состоянию на октябрь 2026 г.",
      vi: "Tính từ khi có đủ giấy tờ, tùy theo chính quyền địa phương (kommune). Familieretshuset thường ra quyết định đối với hồ sơ đầy đủ trong vòng 5 ngày làm việc; sau đó, ngày trống của kommune là yếu tố quyết định (Tønder và Aabenraa mở lịch đặt trước 60 ngày, Ærø 10 tuần, Copenhagen tới 4 tháng). Tính đến tháng 10/2026.",
      es: "Desde que los documentos están completos, según el municipio. Familieretshuset suele resolver las solicitudes completas en 5 días hábiles; después cuenta la fecha del municipio (Tønder y Aabenraa abren las fechas con 60 días de antelación, Ærø con 10 semanas, Copenhague con hasta 4 meses). Datos de octubre de 2026.",
    },
    stat3Days: {
      de: "Mo–So",
      en: "Mon–Sun",
      da: "Man–Søn",
      ar: "الاثنين–الأحد",
      uk: "Пн–Нд",
      ru: "Пн–Вс",
      vi: "T2–CN",
      es: "Lun–dom",
    },
    stat3Label: {
      de: "Persönlich erreichbar",
      en: "Personally reachable",
      da: "Personligt tilgængelig",
      ar: "متاحون شخصيًا",
      uk: "Особисто на зв’язку",
      ru: "Лично на связи",
      vi: "Liên hệ trực tiếp",
      es: "Atención personal",
    },
    stat3Sub: {
      de: "WhatsApp-Antwort meist innerhalb weniger Stunden",
      en: "WhatsApp replies usually land within a few hours, weekends included",
      da: "WhatsApp-svar normalt inden for få timer",
      ar: "تصلكم ردودنا عبر WhatsApp عادةً خلال بضع ساعات، بما في ذلك عطلات نهاية الأسبوع",
      uk: "Відповіді у WhatsApp зазвичай надходять протягом кількох годин, зокрема й у вихідні",
      ru: "Ответы в WhatsApp обычно приходят в течение нескольких часов, в том числе по выходным",
      vi: "Tin nhắn WhatsApp thường được trả lời trong vài giờ, kể cả cuối tuần",
      es: "Respuesta por WhatsApp, normalmente en pocas horas",
    },
    // Trust row: provable facts only (Stripe really processes payments, SSL is
    // active, the withdrawal right is in the T&C, the company sits in Baden).
    // Do not add review-platform logos while no real reviews exist there.
    trustHeading: {
      de: "Sicher buchen und bezahlen",
      en: "Book and pay securely",
      da: "Book og betal sikkert",
      ar: "احجزوا وادفعوا بأمان",
      uk: "Бронюйте й оплачуйте безпечно",
      ru: "Безопасное бронирование и оплата",
      vi: "Đặt và thanh toán an toàn",
      es: "Reservar y pagar con seguridad",
    },
    trustStripe: {
      de: "Zahlung über Stripe",
      en: "Payments via Stripe",
      da: "Betaling via Stripe",
      ar: "المدفوعات عبر Stripe",
      uk: "Оплата через Stripe",
      ru: "Платежи через Stripe",
      vi: "Thanh toán qua Stripe",
      es: "Pago a través de Stripe",
    },
    trustSsl: {
      de: "SSL-verschlüsselt",
      en: "SSL encrypted",
      da: "SSL-krypteret",
      ar: "مشفّر بتقنية SSL",
      uk: "SSL-шифрування",
      ru: "Шифрование SSL",
      vi: "Mã hóa SSL",
      es: "Cifrado SSL",
    },
    trustWithdrawal: {
      de: "14\u00A0Tage Widerrufsrecht",
      en: "14-day right of withdrawal",
      da: "14 dages fortrydelsesret",
      ar: "حق الانسحاب من العقد خلال 14 يومًا",
      uk: "14 днів на відмову від договору",
      ru: "Право на отказ в течение 14 дней",
      vi: "Quyền hủy hợp đồng trong 14 ngày",
      es: "14 días de derecho de desistimiento",
    },
    trustSwiss: {
      de: "Schweizer Unternehmen · Baden (CH)",
      en: "Swiss company · Baden (CH)",
      da: "Schweizisk virksomhed · Baden (CH)",
      ar: "شركة سويسرية · Baden (CH)",
      uk: "Швейцарська компанія · Баден (CH)",
      ru: "Швейцарская компания · Баден (CH)",
      vi: "Công ty Thụy Sĩ · Baden (CH)",
      es: "Empresa suiza · Baden (CH)",
    },
    // An invitation to review, never a rating claim: without real reviews any
    // implied rating would be misleading.
    trustReviewPrefix: {
      de: "Bewertet uns auf",
      en: "Review us on",
      da: "Anmeld os på",
      ar: "قيّمونا على",
      uk: "Залиште відгук на",
      ru: "Оцените нас:",
      vi: "Đánh giá chúng tôi trên",
      es: "Valórennos en",
    },
    // Heading of the trust marquee. For the wording to be true, the row may
    // only list platforms that really support us (payments, our own channels,
    // our review page).
    trustFindUs: {
      de: "Wo ihr uns findet",
      en: "Where to find us",
      da: "Her finder I os",
      ar: "أين تجدوننا",
      uk: "Де нас знайти",
      ru: "Где нас найти",
      vi: "Tìm chúng tôi tại",
      es: "Dónde encontrarnos",
    },
    trustStripeNote: {
      de: "Zahlungsabwicklung",
      en: "Payment processing",
      da: "Betalingsafvikling",
      ar: "معالجة المدفوعات",
      uk: "Обробка платежів",
      ru: "Обработка платежей",
      vi: "Xử lý thanh toán",
      es: "Procesamiento de pagos",
    },
    // Invitation line in the trust cards; the star badge replaces it once real
    // reviews exist.
    trustReviewCta: {
      de: "Bewertungen lesen",
      en: "Read reviews",
      da: "Læs anmeldelser",
      ar: "اقرأوا التقييمات",
      uk: "Читати відгуки",
      ru: "Читать отзывы",
      vi: "Xem đánh giá",
      es: "Leer reseñas",
    },
    trustFollowCta: {
      de: "Folgt uns",
      en: "Follow us",
      da: "Følg os",
      ar: "تابعونا",
      uk: "Стежте за нами",
      ru: "Подписывайтесь",
      vi: "Theo dõi chúng tôi",
      es: "Síganos",
    },
    // ── NumbersStrip, 4 stat-card labels ───────────────────────────
    statWeeksLabel: {
      de: "Wochen bis zur Heiratsurkunde",
      en: "weeks to the marriage certificate",
      da: "uger til vielsesattesten",
      ar: "أسابيع حتى شهادة الزواج",
      uk: "тижнів до свідоцтва про шлюб",
      ru: "недель до свидетельства о браке",
      vi: "tuần đến khi có giấy chứng nhận kết hôn",
      es: "semanas hasta el certificado de matrimonio",
    },
    statPriceLabel: {
      de: "Festpreis · alles inklusive",
      en: "Flat fee · all included",
      da: "Fast pris · alt inkluderet",
      ar: "رسوم ثابتة · كل شيء مشمول",
      uk: "Фіксована ціна · усе включено",
      ru: "Фиксированная цена · всё включено",
      vi: "Phí trọn gói · đã gồm tất cả",
      es: "Precio fijo · todo incluido",
    },
    statNoHiddenLabel: {
      de: "versteckte Kosten",
      en: "hidden costs",
      da: "skjulte omkostninger",
      ar: "تكاليف خفية",
      uk: "прихованих витрат",
      ru: "скрытых расходов",
      vi: "chi phí ẩn",
      es: "costos ocultos",
    },
  },

  // ─────────────────────────────────────────── PROCESS
  process: {
    eyebrow: {
      de: "So läuft es ab",
      en: "How it works",
      da: "Sådan forløber det",
      ar: "كيف تسير العملية",
      uk: "Як це працює",
      ru: "Как это работает",
      vi: "Cách thức hoạt động",
      es: "Cómo funciona",
    },
    headlineA: {
      de: "Drei",
      en: "Three",
      da: "Tre",
      ar: "ثلاث",
      uk: "Три",
      ru: "Три",
      vi: "Ba",
      es: "Tres",
    },
    headlineB: {
      de: "Schritte. Den Papierkram machen wir.",
      en: "simple steps. We handle the paperwork.",
      da: "trin. Papirarbejdet klarer vi.",
      ar: "خطوات بسيطة. ونتولّى نحن الأوراق.",
      uk: "прості кроки. Паперову роботу беремо на себе.",
      ru: "простых шага. Бумажную работу берём на себя.",
      vi: "bước đơn giản. Giấy tờ để chúng tôi lo.",
      es: "pasos sencillos. El papeleo lo hacemos nosotros.",
    },
    step1Number: { de: "01", en: "01", da: "01", ar: "01", uk: "01", ru: "01", vi: "01", es: "01" },
    step1Duration: {
      de: "Ein kurzer Call",
      en: "A short call",
      da: "Et kort opkald",
      ar: "مكالمة قصيرة",
      uk: "Коротка розмова",
      ru: "Короткий звонок",
      vi: "Một cuộc gọi ngắn",
      es: "Una llamada breve",
    },
    step1Title: {
      de: "Wir lernen euch kennen.",
      en: "We get to know you.",
      da: "Vi lærer jer at kende.",
      ar: "نتعرّف إليكم.",
      uk: "Ми знайомимося з вами.",
      ru: "Мы знакомимся с вами.",
      vi: "Chúng tôi làm quen với các bạn.",
      es: "Nos conocemos.",
    },
    step1Text: {
      de: "Per Chat oder Call klären wir eure Situation und welche Dokumente ihr braucht.",
      en: "Via chat or call we clarify your situation, including binational couples or partners living in different countries, and exactly which documents you need.",
      da: "Via chat eller opkald afklarer vi jeres situation og hvilke dokumenter I skal bruge.",
      ar: "عبر المحادثة أو مكالمة هاتفية نوضّح وضعكم، بما في ذلك الأزواج من جنسيتين مختلفتين أو الشريكان المقيمان في بلدين مختلفين، ونحدّد بدقة الوثائق التي تحتاجون إليها.",
      uk: "У чаті або телефоном ми з’ясовуємо вашу ситуацію — зокрема, якщо ви пара різних громадянств чи партнери, які живуть у різних країнах, — і які саме документи вам потрібні.",
      ru: "В чате или по телефону мы разбираемся в вашей ситуации — в том числе если у вас разное гражданство или вы живёте в разных странах — и выясняем, какие именно документы вам нужны.",
      vi: "Qua tin nhắn hoặc cuộc gọi, chúng tôi làm rõ hoàn cảnh của các bạn, kể cả với cặp đôi khác quốc tịch hoặc hai người đang sống ở hai quốc gia khác nhau, và chính xác các bạn cần những giấy tờ nào.",
      es: "Por chat o llamada aclaramos su situación y qué documentos necesitan.",
    },
    step1Outcome: {
      de: "Ihr wisst genau, was zu tun ist.",
      en: "You know exactly what to do.",
      da: "I ved præcis, hvad der skal gøres.",
      ar: "تعرفون بالضبط ما عليكم فعله.",
      uk: "Ви точно знаєте, що робити.",
      ru: "Вы точно знаете, что делать.",
      vi: "Các bạn biết chính xác cần làm gì.",
      es: "Saben exactamente qué hacer.",
    },
    step2Number: { de: "02", en: "02", da: "02", ar: "02", uk: "02", ru: "02", vi: "02", es: "02" },
    step2Duration: {
      de: "Zehn Minuten zuhause",
      en: "Ten minutes at home",
      da: "Ti minutter hjemme",
      ar: "عشر دقائق في المنزل",
      uk: "Десять хвилин удома",
      ru: "Десять минут дома",
      vi: "Mười phút tại nhà",
      es: "Diez minutos en casa",
    },
    step2Title: {
      de: "Schnelle Schnappschüsse, wir erledigen den Rest.",
      en: "Quick photos at home, we handle the rest.",
      da: "Hurtige billeder hjemme, vi klarer resten.",
      ar: "صور سريعة في المنزل، ونتولّى نحن الباقي.",
      uk: "Кілька швидких фото вдома — решту ми беремо на себе.",
      ru: "Пара быстрых фото дома — остальное сделаем мы.",
      vi: "Chụp ảnh nhanh tại nhà, phần còn lại để chúng tôi lo.",
      es: "Unas fotos rápidas, nosotros hacemos el resto.",
    },
    step2Text: {
      de: "Ihr ladet eure Dokumente per Handy hoch, AFL-Antrag und Behörden­kommunikation übernehmen wir. Apostille als Add-on buchbar.",
      en: "Upload your documents from your phone, we handle the AFL application and authority communication. Apostille bookable as an add-on.",
      da: "I uploader jeres dokumenter fra mobilen, vi klarer AFL-ansøgningen og myndighedskommunikationen. Apostille kan tilkøbes som tillæg.",
      ar: "ارفعوا وثائقكم من هاتفكم، ونتولّى نحن طلب AFL والتواصل مع الجهات الرسمية. ويمكن حجز الأبوستيل كخدمة إضافية.",
      uk: "Завантажте документи з телефона, а ми візьмемо на себе заяву до AFL і спілкування з органами влади. Апостиль можна замовити як додаткову послугу.",
      ru: "Загрузите документы с телефона, а заявление в AFL и общение с ведомствами мы возьмём на себя. Апостиль можно заказать дополнительно.",
      vi: "Tải giấy tờ lên từ điện thoại, chúng tôi lo hồ sơ AFL và việc liên lạc với cơ quan chức năng. Có thể đặt thêm dịch vụ Apostille.",
      es: "Suben sus documentos desde el teléfono; nosotros nos encargamos de la solicitud a la AFL y de la comunicación con las autoridades. La apostilla se puede reservar como servicio adicional.",
    },
    step2Outcome: {
      de: "Keine Behördengänge für euch.",
      en: "Zero trips to any authority.",
      da: "Ingen ture til myndigheder.",
      ar: "دون أي زيارة لأي جهة رسمية.",
      uk: "Жодних походів до установ.",
      ru: "Ни одного похода в ведомства.",
      vi: "Không phải đến bất kỳ cơ quan nào.",
      es: "Ustedes no tienen que ir a ninguna oficina.",
    },
    step3Number: { de: "03", en: "03", da: "03", ar: "03", uk: "03", ru: "03", vi: "03", es: "03" },
    step3Duration: {
      de: "Euer Hochzeitstag",
      en: "Your wedding day",
      da: "Jeres bryllupsdag",
      ar: "يوم زواجكم",
      uk: "День вашого весілля",
      ru: "День вашей свадьбы",
      vi: "Ngày cưới của các bạn",
      es: "El día de su boda",
    },
    step3Title: {
      de: "Glückwunsch! Ihr fliegt hin. Heiratet. Feiert.",
      en: "Congratulations! You fly in. Marry. Celebrate.",
      da: "Tillykke! I flyver derop. Bliver gift. Fester.",
      ar: "تهانينا! تسافرون. تتزوجون. تحتفلون.",
      uk: "Вітаємо! Ви прилітаєте. Одружуєтеся. Святкуєте.",
      ru: "Поздравляем! Вы прилетаете. Вступаете в брак. Празднуете.",
      vi: "Xin chúc mừng! Các bạn bay đến. Kết hôn. Ăn mừng.",
      es: "¡Felicidades! Viajan a Dinamarca. Se casan. Lo celebran.",
    },
    step3Text: {
      de: "Ihr kommt in Dänemark an, sagt Ja und nehmt eure Heiratsurkunde mit nach Hause.",
      en: "You arrive in Denmark, say yes, and take your certificate home with you.",
      da: "I ankommer til Danmark, siger ja og tager attesten med hjem.",
      ar: "تصلون إلى الدنمارك، وتقولون «نعم»، وتعودون إلى بلدكم ومعكم شهادتكم.",
      uk: "Ви приїжджаєте до Данії, кажете «так» і забираєте свідоцтво з собою додому.",
      ru: "Вы приезжаете в Данию, говорите «да» и увозите свидетельство о браке домой.",
      vi: "Các bạn đến Đan Mạch, nói lời đồng ý và mang giấy chứng nhận về nhà.",
      es: "Llegan a Dinamarca, dan el “sí” y se llevan a casa su certificado de matrimonio.",
    },
    step3Outcome: {
      de: "Verheiratet. Ganz einfach.",
      en: "Married. That simple.",
      da: "Gift. Så enkelt.",
      ar: "متزوجون. بهذه البساطة.",
      uk: "Одружені. Ось так просто.",
      ru: "Вы в браке. Вот так просто.",
      vi: "Đã kết hôn. Đơn giản vậy thôi.",
      es: "Casados. Así de fácil.",
    },
    ctaStart: {
      de: "Erstes Gespräch anfragen",
      en: "Request a first call",
      da: "Bed om en første samtale",
      ar: "اطلبوا مكالمة أولى",
      uk: "Домовитися про першу розмову",
      ru: "Договориться о первом звонке",
      vi: "Đặt lịch trao đổi đầu tiên",
      es: "Pedir una primera llamada",
    },
  },

  // ─────────────────────────────────────────── COMPARISON
  comparison: {
    cellInclusive: {
      de: "Inklusive",
      en: "Included",
      da: "Inkluderet",
      ar: "مشمول",
      uk: "Включено",
      ru: "Включено",
      vi: "Đã bao gồm",
      es: "Incluido",
    },
    cellHandled: {
      de: "Wir sagen euch, welche",
      en: "We tell you which ones",
      da: "Vi siger, hvilke",
      ar: "نخبركم بما تحتاجونه",
      uk: "Ми скажемо, які саме",
      ru: "Мы скажем, какие нужны",
      vi: "Chúng tôi báo cần những gì",
      es: "Les decimos cuáles",
    },
    cellSelf: {
      de: "Selbst",
      en: "DIY",
      da: "Selv",
      ar: "بأنفسكم",
      uk: "Самостійно",
      ru: "Самостоятельно",
      vi: "Tự làm",
      es: "Ustedes mismos",
    },
    cellPartial: {
      de: "Teilweise",
      en: "Partly",
      da: "Delvist",
      ar: "جزئيًا",
      uk: "Частково",
      ru: "Частично",
      vi: "Một phần",
      es: "En parte",
    },
    footnote: {
      de: "Apostillen und beglaubigte Übersetzungen eurer eigenen Urkunden sind nicht im Festpreis enthalten: Ihr bestellt sie im Ausstellungsland und zahlt sie direkt (~€130–450 zusammen, falls nötig); wir sagen euch genau, welche ihr braucht. Unser Apostille-Service (€149,99) ist etwas anderes: die Apostille auf eurer dänischen Heiratsurkunde nach der Trauung. Im Festpreis enthalten sind unsere Service-Leistungen und die AFL-Behördengebühr. Vergleichsbasis: In Eigenregie zahlt ihr mindestens die Behördengebühr des Familieretshuset (€281); Apostillen und Übersetzungen kommen je nach Land dazu, und fehlt etwas, dauert die Bearbeitung derzeit rund 2\u00A0Monate länger. Unser Wert ist der Service-Festpreis; Anreise und Übernachtung kommen bei jedem Weg hinzu.",
      en: "Apostilles and certified translations of your own documents are not included in the fixed price: you order them in the issuing country and pay directly (~€130–450 together, if needed); we tell you exactly which ones you need. Our Apostille Service (€149.99) is something else: the apostille on your Danish marriage certificate after the wedding. Included in the fixed price are our services and the AFL government fee. Comparison basis: on your own you pay at least the Familieretshuset fee (€281); apostilles and translations come on top depending on the country, and if anything is missing, processing currently takes about 2\u00A0months longer. Our figure is the service flat fee; travel and accommodation come on top whichever route you choose.",
      da: "Apostiller og autoriserede oversættelser af jeres egne dokumenter er ikke inkluderet i fastprisen: I bestiller dem i udstedelseslandet og betaler direkte (~€130–450 tilsammen, hvis nødvendigt); vi siger præcis, hvilke I skal bruge. Vores Apostille-Service (€149,99) er noget andet: apostillen på jeres danske vielsesattest efter vielsen. I fastprisen indgår vores ydelser og AFL-myndighedsgebyret. Sammenligningsgrundlag: På egen hånd betaler I mindst Familieretshusets gebyr (€281); apostiller og oversættelser kommer oveni afhængigt af landet, og mangler der noget, tager behandlingen i øjeblikket omkring 2\u00A0måneder længere. Vores tal er service-fastprisen; rejse og ophold kommer oveni, uanset hvilken vej I vælger.",
      ar: "الأبوستيل والترجمات المعتمدة لوثائقكم الخاصة غير مشمولة في السعر الثابت: تطلبونها في بلد الإصدار وتدفعون مباشرةً (~€130–450 معًا عند الحاجة)، ونخبركم بالضبط بما تحتاجونه. أما خدمة الأبوستيل لدينا (€149.99) فشيء آخر: الأبوستيل على شهادة زواجكم الدنماركية بعد الزواج. ويشمل السعر الثابت خدماتنا ورسوم AFL الحكومية. أساس المقارنة: عند إنجاز الإجراءات بأنفسكم تدفعون على الأقل رسوم Familieretshuset (€281)؛ وتُضاف الأبوستيل والترجمات بحسب البلد، وإذا نقص شيء تستغرق المعالجة حاليًا نحو شهرين إضافيين. أما رقمنا فهو الرسوم الثابتة للخدمة؛ وتُضاف تكاليف السفر والإقامة أيًّا كان المسار الذي تختارونه.",
      uk: "Апостилі та засвідчені переклади ваших власних документів не входять у фіксовану ціну: ви замовляєте їх у країні видачі й платите напряму (~€130–450 разом, якщо потрібно); ми точно скажемо, які саме потрібні. Наша послуга апостилю (€149,99) — це інше: апостиль на ваше данське свідоцтво про шлюб після весілля. У фіксовану ціну входять наші послуги та державний збір AFL. Основа порівняння: самостійно ви платите щонайменше збір Familieretshuset (€281); апостилі й переклади додаються залежно від країни, а якщо чогось бракує, розгляд нині триває приблизно на 2\u00A0місяці довше. Наша цифра — фіксована вартість послуг; дорога й проживання додаються за будь-якого варіанту.",
      ru: "Апостили и заверенные переводы ваших собственных документов не входят в фиксированную цену: вы заказываете их в стране выдачи и платите напрямую (~€130–450 вместе, если нужно); мы точно скажем, какие нужны. Наша услуга апостиля (€149,99) — это другое: апостиль на ваше датское свидетельство о браке после регистрации. В фиксированную цену входят наши услуги и государственный сбор AFL. Основа для сравнения: самостоятельно вы платите как минимум сбор Familieretshuset (€281); апостили и переводы добавляются в зависимости от страны, а если чего-то не хватает, рассмотрение сейчас занимает примерно на 2 месяца дольше. Наша цифра — фиксированная стоимость услуг; дорога и проживание добавляются при любом варианте.",
      vi: "Apostille và bản dịch công chứng cho giấy tờ của chính hai bạn không nằm trong giá cố định: hai bạn xin tại nước cấp và trả trực tiếp (~€130–450 cho cả hai, nếu cần); chúng tôi báo chính xác những gì cần. Dịch vụ Apostille của chúng tôi (€149,99) là việc khác: Apostille cho giấy chứng nhận kết hôn Đan Mạch sau đám cưới. Giá cố định bao gồm các dịch vụ của chúng tôi và lệ phí nhà nước AFL. Cơ sở so sánh: khi tự làm, hai bạn trả ít nhất lệ phí của Familieretshuset (€281); Apostille và bản dịch tính thêm tùy theo nước, và nếu thiếu giấy tờ, việc xử lý hiện kéo dài thêm khoảng 2 tháng. Con số của chúng tôi là phí dịch vụ trọn gói; chi phí đi lại và chỗ ở tính riêng, dù các bạn chọn con đường nào.",
      es: "Las apostillas y traducciones juradas de sus propios documentos no están incluidas en el precio fijo: las piden en el país emisor y las pagan directamente (~€130–450 en total, si hacen falta); les decimos exactamente cuáles necesitan. Nuestro servicio de apostilla (€149,99) es otra cosa: la apostilla sobre su certificado de matrimonio danés después de la boda. El precio fijo incluye nuestros servicios y la tasa oficial de la AFL. Base de comparación: por su cuenta pagan como mínimo la tasa oficial de Familieretshuset (€281); según el país se suman apostillas y traducciones, y si falta algo, la tramitación tarda actualmente unos 2 meses más. Nuestra cifra es el precio fijo del servicio; el viaje y el alojamiento se suman en cualquiera de los caminos.",
    },
    // Mobile toggle for the footnote; the content must stay fully reachable.
    footnoteToggle: {
      de: "Preis-Hinweise und Vergleichsbasis anzeigen",
      en: "Show pricing notes and comparison basis",
      da: "Vis prisnoter og sammenligningsgrundlag",
      ar: "عرض ملاحظات الأسعار وأساس المقارنة",
      uk: "Показати примітки до цін і основу порівняння",
      ru: "Показать примечания к ценам и основу сравнения",
      vi: "Xem ghi chú về giá và cơ sở so sánh",
      es: "Mostrar notas de precios y base de comparación",
    },
    eyebrow: {
      de: "Ehrlicher Vergleich",
      en: "An honest comparison",
      da: "Ærlig sammenligning",
      ar: "مقارنة صادقة",
      uk: "Чесне порівняння",
      ru: "Честное сравнение",
      vi: "So sánh trung thực",
      es: "Comparación honesta",
    },
    headlineA: {
      de: "Drei Wege,",
      en: "Three paths,",
      da: "Tre veje,",
      ar: "ثلاثة طرق،",
      uk: "Три шляхи —",
      ru: "Три пути,",
      vi: "Ba con đường,",
      es: "Tres caminos,",
    },
    headlineB: {
      de: "einen zu finden.",
      en: "one to pick.",
      da: "én at vælge.",
      ar: "واختيار واحد.",
      uk: "оберіть свій.",
      ru: "выбрать нужно один.",
      vi: "chọn một.",
      es: "uno para ustedes.",
    },
    lead: {
      de: "Wir erklären euch alle Optionen offen, auch wenn ihr am Ende nicht mit uns arbeitet.",
      en: "We'll explain every option openly, even if you end up not working with us.",
      da: "Vi forklarer alle muligheder åbent, også hvis I ender med ikke at arbejde med os.",
      ar: "نشرح لكم كل خيار بصراحة، حتى لو قررتم في النهاية عدم العمل معنا.",
      uk: "Ми відкрито пояснимо кожен варіант, навіть якщо зрештою ви не працюватимете з нами.",
      ru: "Мы открыто объясним каждый вариант, даже если в итоге вы не станете работать с нами.",
      vi: "Chúng tôi giải thích công khai mọi lựa chọn, kể cả khi cuối cùng các bạn không làm việc với chúng tôi.",
      es: "Les explicamos todas las opciones con franqueza, aunque al final no trabajen con nosotros.",
    },
    colDiy: {
      de: "Eigenregie",
      en: "On your own",
      da: "På egen hånd",
      ar: "بأنفسكم",
      uk: "Самотужки",
      ru: "Самостоятельно",
      vi: "Tự xoay xở",
      es: "Por su cuenta",
    },
    colDiyTag: {
      de: "DIY",
      en: "DIY",
      da: "DIY",
      ar: "ذاتيًا",
      uk: "Самостійно",
      ru: "Сами",
      vi: "Tự làm",
      es: "Sin ayuda",
    },
    colHome: {
      de: "Standesamt DE/AT/CH",
      en: "Registry office at home",
      da: "Vielse i hjemlandet",
      ar: "مكتب الأحوال المدنية في بلدكم",
      uk: "РАЦС удома",
      ru: "ЗАГС (Standesamt) дома",
      vi: "Cơ quan hộ tịch nơi đang sống",
      es: "Registro civil DE/AT/CH",
    },
    colHomeTag: {
      de: "Heimat",
      en: "Home",
      da: "Hjemland",
      ar: "في بلدكم",
      uk: "Удома",
      ru: "Дома",
      vi: "Nơi đang sống",
      es: "Su país",
    },
    colUs: {
      de: "Danish Wedding",
      en: "Danish Wedding",
      da: "Danish Wedding",
      ar: "Danish Wedding",
      uk: "Danish Wedding",
      ru: "Danish Wedding",
      vi: "Danish Wedding",
      es: "Danish Wedding",
    },
    colUsTag: {
      de: "Mit uns",
      en: "With us",
      da: "Med os",
      ar: "معنا",
      uk: "З нами",
      ru: "С нами",
      vi: "Với chúng tôi",
      es: "Con nosotros",
    },
    row1: {
      de: "Bearbeitungszeit",
      en: "Processing time",
      da: "Sagsbehandlingstid",
      ar: "مدة المعالجة",
      uk: "Тривалість",
      ru: "Срок оформления",
      vi: "Thời gian xử lý",
      es: "Tiempo de tramitación",
    },
    row1diy: {
      de: "gleich, bei Fehlern länger",
      en: "same, longer if anything is missing",
      da: "det samme, længere ved mangler",
      ar: "المدة نفسها، وأطول عند النقص",
      uk: "так само, довше, якщо чогось бракує",
      ru: "так же, дольше, если чего-то не хватает",
      vi: "như nhau, lâu hơn nếu thiếu giấy tờ",
      es: "igual, más largo si hay errores",
    },
    row1home: {
      de: "mit Befreiung oft 6–12\u00A0Monate",
      en: "often 6–12\u00A0months with a court exemption",
      da: "ofte 6–12\u00A0måneder med dispensation",
      ar: "غالبًا 6–12 شهرًا مع إعفاء من المحكمة",
      uk: "з дозволом суду часто 6–12\u00A0місяців",
      ru: "с разрешением суда часто 6–12\u00A0месяцев",
      vi: "thường 6–12 tháng nếu cần tòa án miễn trừ",
      es: "con dispensa, a menudo 6–12 meses",
    },
    row1us: {
      de: "3–6\u00A0Wochen",
      en: "3–6\u00A0weeks",
      da: "3–6\u00A0uger",
      ar: "3–6 أسابيع",
      uk: "3–6\u00A0тижнів",
      ru: "3–6 недель",
      vi: "3–6 tuần",
      es: "3–6 semanas",
    },
    row2: {
      de: "Dokumente selbst koordinieren",
      en: "Coordinate documents yourself",
      da: "Selv koordinere dokumenter",
      ar: "تنسيق الوثائق بأنفسكم",
      uk: "Самостійна координація документів",
      ru: "Самостоятельная координация документов",
      vi: "Tự điều phối giấy tờ",
      es: "Coordinar los documentos por su cuenta",
    },
    row3: {
      de: "Apostille besorgen",
      en: "Obtain apostille",
      da: "Skaffe apostille",
      ar: "استخراج الأبوستيل",
      uk: "Отримання апостилю",
      ru: "Получение апостиля",
      vi: "Xin chứng nhận Apostille",
      es: "Conseguir la apostilla",
    },
    row4: {
      de: "Beglaubigte Übersetzungen",
      en: "Certified translations",
      da: "Autoriserede oversættelser",
      ar: "الترجمات المعتمدة",
      uk: "Засвідчені переклади",
      ru: "Заверенные переводы",
      vi: "Bản dịch công chứng",
      es: "Traducciones juradas",
    },
    row5: {
      de: "Vorprüfung auf die Anforderungen der Behörde",
      en: "Pre-check against the authority's requirements",
      da: "Forhåndstjek mod myndighedens krav",
      ar: "فحص مسبق وفق متطلبات الجهة الرسمية",
      uk: "Попередня перевірка на відповідність вимогам органу",
      ru: "Предварительная проверка по требованиям ведомства",
      vi: "Kiểm tra trước theo yêu cầu của cơ quan chức năng",
      es: "Revisión previa según los requisitos de la autoridad",
    },
    row6: {
      de: "Begleitung per WhatsApp während des Antrags",
      en: "WhatsApp support throughout the application",
      da: "WhatsApp-vejledning under ansøgningen",
      ar: "دعم عبر WhatsApp طوال فترة الطلب",
      uk: "Підтримка у WhatsApp протягом усієї процедури",
      ru: "Поддержка в WhatsApp на протяжении всей подачи заявления",
      vi: "Hỗ trợ qua WhatsApp trong suốt quá trình nộp hồ sơ",
      es: "Acompañamiento por WhatsApp durante la solicitud",
    },
    row8: {
      de: "Persönliche Ansprechperson von A bis Z",
      en: "One personal contact from A to Z",
      da: "Én personlig kontakt fra A til Z",
      ar: "جهة اتصال شخصية واحدة من البداية إلى النهاية",
      uk: "Одна особиста контактна особа від початку до кінця",
      ru: "Одно контактное лицо от начала до конца",
      vi: "Một người phụ trách riêng từ A đến Z",
      es: "Una persona de contacto de principio a fin",
    },
    row9: {
      de: "Transparenter Gesamtpreis",
      en: "Transparent total price",
      da: "Gennemsigtig totalpris",
      ar: "سعر إجمالي شفاف",
      uk: "Прозора загальна ціна",
      ru: "Прозрачная итоговая цена",
      vi: "Tổng giá minh bạch",
      es: "Precio total transparente",
    },
    row9diy: {
      de: "ab €281 Behördengebühr + eure Zeit",
      en: "from €281 authority fee + your time",
      da: "fra €281 myndighedsgebyr + jeres tid",
      ar: "ابتداءً من €281 رسوم رسمية + وقتكم",
      uk: "від €281 збору + ваш час",
      ru: "от €281 сбора + ваше время",
      vi: "từ €281 lệ phí + thời gian của hai bạn",
      es: "desde €281 de tasa oficial + su tiempo",
    },
    row9home: {
      de: "je nach Standesamt",
      en: "depends on the registry office",
      da: "afhænger af kontoret",
      ar: "حسب مكتب الأحوال المدنية",
      uk: "залежно від РАЦСу",
      ru: "зависит от ЗАГСа",
      vi: "tùy cơ quan hộ tịch",
      es: "según el registro civil",
    },
    row9us: {
      de: "€649,99 Service-Festpreis",
      en: "€649.99 service flat fee",
      da: "€649,99 service-fastpris",
      ar: "رسوم ثابتة للخدمة €649.99",
      uk: "€649,99 — фіксована вартість послуг",
      ru: "€649,99 — фиксированная стоимость услуг",
      vi: "Phí dịch vụ trọn gói €649,99",
      es: "€649,99 de precio fijo del servicio",
    },
    // Wording must stay "no HIDDEN extra costs", not "no extra costs":
    // apostille and translation are not included, only disclosed up front.
    row10: {
      de: "Versteckte Zusatzkosten",
      en: "Hidden extra costs",
      da: "Skjulte ekstraomkostninger",
      ar: "تكاليف إضافية خفية",
      uk: "Приховані додаткові витрати",
      ru: "Скрытые дополнительные расходы",
      vi: "Chi phí phát sinh ẩn",
      es: "Costos adicionales ocultos",
    },
    row10diy: {
      de: "Variabel (Apostille, Übersetzung)",
      en: "Variable (apostille, translation)",
      da: "Variabel (apostille, oversættelse)",
      ar: "متغيرة (الأبوستيل، الترجمة)",
      uk: "Змінні (апостиль, переклад)",
      ru: "Варьируются (апостиль, перевод)",
      vi: "Không cố định (Apostille, bản dịch)",
      es: "Variables (apostilla, traducción)",
    },
    row10home: {
      de: "Möglich (Nachforderungen)",
      en: "Possible (follow-up requests)",
      da: "Muligt (efterfølgende krav)",
      ar: "محتملة (طلبات لاحقة)",
      uk: "Можливі (додаткові запити)",
      ru: "Возможны (дополнительные запросы)",
      vi: "Có thể có (yêu cầu bổ sung)",
      es: "Posibles (requerimientos adicionales)",
    },
    row10us: {
      de: "Keine, alles vorab im Vertrag",
      en: "None, all disclosed in the contract",
      da: "Ingen, alt oplyst i kontrakten",
      ar: "لا توجد، فكل شيء مذكور في العقد",
      uk: "Немає, усе зазначено в договорі",
      ru: "Нет, всё указано в договоре",
      vi: "Không có, tất cả đều ghi rõ trong hợp đồng",
      es: "Ninguno, todo por adelantado en el contrato",
    },
  },

  // ─────────────────────────────────────────── GUARANTEE (no promises)
  guarantee: {
    eyebrow: {
      de: "Unser Versprechen an euch",
      en: "Our promise to you",
      da: "Vores løfte til jer",
      ar: "وعدنا لكم",
      uk: "Наша обіцянка вам",
      ru: "Наше обещание вам",
      vi: "Cam kết của chúng tôi với các bạn",
      es: "Nuestra promesa para ustedes",
    },
    headlineA: {
      de: "Wir arbeiten",
      en: "We work",
      da: "Vi arbejder",
      ar: "نعمل",
      uk: "Ми працюємо",
      ru: "Мы работаем",
      vi: "Chúng tôi làm việc",
      es: "Trabajamos",
    },
    headlineHighlight: {
      de: "sorgfältig,",
      en: "carefully,",
      da: "omhyggeligt,",
      ar: "بعناية،",
      uk: "ретельно,",
      ru: "тщательно,",
      vi: "cẩn thận,",
      es: "con cuidado,",
    },
    headlineB: {
      de: "transparent und in Ruhe.",
      en: "transparently and calmly.",
      da: "gennemsigtigt og roligt.",
      ar: "وبشفافية وهدوء.",
      uk: "прозоро й спокійно.",
      ru: "прозрачно и без спешки.",
      vi: "minh bạch và từ tốn.",
      es: "con transparencia y con calma.",
    },
    lead: {
      de: "Wir sind ein kleines, spezialisiertes Team aus der Schweiz mit Fokus auf Eheschliessungen in Dänemark für Paare aus dem DACH-Raum und international. Jedes Mandat wird persönlich begleitet, vom ersten Kontakt bis zur Heiratsurkunde.",
      en: "We're a small, specialised Swiss team focused on marriages in Denmark for couples from the DACH region and beyond. Every case is handled personally, from first contact to your marriage certificate.",
      da: "Vi er et lille, specialiseret schweizisk team med fokus på vielser i Danmark for par fra DACH-regionen og internationalt. Hver sag følges personligt, fra første kontakt til jeres vielsesattest.",
      ar: "نحن فريق سويسري صغير ومتخصص، يركّز على الزواج في الدنمارك للأزواج من منطقة DACH (ألمانيا والنمسا وسويسرا) وخارجها. نتولّى كل حالة شخصيًا، من أول تواصل حتى شهادة زواجكم.",
      uk: "Ми — невелика спеціалізована команда зі Швейцарії, що займається шлюбами в Данії для пар із країн DACH (Німеччина, Австрія, Швейцарія) та інших країн. Кожну справу ми ведемо особисто — від першого контакту до свідоцтва про шлюб.",
      ru: "Мы — небольшая специализированная команда из Швейцарии, которая занимается регистрацией браков в Дании для пар из стран DACH (Германия, Австрия, Швейцария) и не только. Каждое дело мы ведём лично — от первого контакта до свидетельства о браке.",
      vi: "Chúng tôi là một đội ngũ Thụy Sĩ nhỏ, chuyên sâu về kết hôn tại Đan Mạch cho các cặp đôi từ các nước DACH (Đức, Áo, Thụy Sĩ) và nhiều nơi khác. Mỗi trường hợp đều được xử lý trực tiếp, từ lần liên hệ đầu tiên đến giấy chứng nhận kết hôn.",
      es: "Somos un equipo pequeño y especializado de Suiza, centrado en bodas en Dinamarca para parejas de la región DACH y de otros países. Acompañamos personalmente cada caso, desde el primer contacto hasta el certificado de matrimonio.",
    },
    pillar1Title: {
      de: "Festpreise im Vertrag",
      en: "Fixed prices in the contract",
      da: "Fastpriser i kontrakten",
      ar: "أسعار ثابتة في العقد",
      uk: "Фіксовані ціни в договорі",
      ru: "Фиксированные цены в договоре",
      vi: "Giá cố định trong hợp đồng",
      es: "Precios fijos en el contrato",
    },
    pillar1Text: {
      de: "Was ihr bezahlt, steht schriftlich im Vertrag. Keine nachträglichen Zuschläge, keine versteckten Gebühren. Komplexe Fälle kalkulieren wir vorab korrekt.",
      en: "What you pay is written in the contract. No later surcharges, no hidden fees. We price complex cases correctly upfront.",
      da: "Det I betaler, står skriftligt i kontrakten. Ingen efterfølgende tillæg, ingen skjulte gebyrer. Komplekse sager prissætter vi korrekt på forhånd.",
      ar: "ما تدفعونه مكتوب في العقد. لا رسوم إضافية لاحقة، ولا رسوم خفية. ونسعّر الحالات المعقّدة بشكل صحيح منذ البداية.",
      uk: "Те, що ви платите, записано в договорі. Жодних подальших доплат, жодних прихованих зборів. Складні випадки ми одразу правильно оцінюємо.",
      ru: "Сумма к оплате прописана в договоре. Никаких последующих доплат и скрытых сборов. Сложные случаи мы правильно рассчитываем заранее.",
      vi: "Số tiền các bạn trả được ghi trong hợp đồng. Không phụ thu về sau, không phí ẩn. Với những trường hợp phức tạp, chúng tôi báo giá chính xác ngay từ đầu.",
      es: "Lo que pagan figura por escrito en el contrato. Sin recargos posteriores, sin cargos ocultos. Los casos complejos los calculamos correctamente por adelantado.",
    },
    pillar2Title: {
      de: "Sorgfältige Vorprüfung",
      en: "Careful pre-check",
      da: "Grundigt forhåndstjek",
      ar: "فحص مسبق دقيق",
      uk: "Ретельна попередня перевірка",
      ru: "Тщательная предварительная проверка",
      vi: "Kiểm tra trước cẩn thận",
      es: "Revisión previa cuidadosa",
    },
    pillar2Text: {
      de: "Jeder Scan wird vor Einreichung gegen die aktuellen Anforderungen von Familieretshuset geprüft. Sollten wir einen Fehler übersehen, bearbeiten wir ihn kostenfrei nach.",
      en: "Every scan is reviewed against Familieretshuset's current requirements before submission. If we miss something, we'll rework it at no cost.",
      da: "Hver scanning gennemgås mod Familieretshusets aktuelle krav før indsendelse. Overser vi noget, retter vi det omkostningsfrit.",
      ar: "نراجع كل نسخة ممسوحة وفق المتطلبات الحالية لـ Familieretshuset قبل التقديم. وإذا فاتنا شيء، نعيد العمل عليه دون أي تكلفة.",
      uk: "Кожен скан перед поданням ми перевіряємо на відповідність чинним вимогам Familieretshuset. Якщо ми щось пропустимо, то безкоштовно це доопрацюємо.",
      ru: "Перед подачей каждый скан проверяется на соответствие актуальным требованиям Familieretshuset. Если мы что-то упустим, исправим это бесплатно.",
      vi: "Mỗi bản scan đều được đối chiếu với yêu cầu hiện hành của Familieretshuset trước khi nộp. Nếu chúng tôi bỏ sót điều gì, chúng tôi sẽ làm lại miễn phí.",
      es: "Antes de presentar la solicitud, revisamos cada escaneo según los requisitos actuales de Familieretshuset. Si se nos pasa un error, lo corregimos sin costo.",
    },
    pillar3Title: {
      de: "14\u00A0Tage Widerruf",
      en: "14-day withdrawal",
      da: "14 dages fortrydelse",
      ar: "الانسحاب خلال 14 يومًا",
      uk: "14 днів на відмову",
      ru: "14 дней на отказ",
      vi: "Hủy hợp đồng trong 14 ngày",
      es: "14 días de desistimiento",
    },
    pillar3Text: {
      de: "EU-Verbraucher­schutz. Wenn ihr nach der Buchung widerruft, gelten die Bedingungen der Widerrufsbelehrung. Vor einem sofortigen Leistungsbeginn bestätigen wir das ausdrücklich.",
      en: "EU consumer protection. If you withdraw after booking, the withdrawal policy applies. Before an immediate start of work we ask for explicit confirmation.",
      da: "EU's forbrugerbeskyttelse. Hvis I fortryder efter bookingen, gælder fortrydelsesvilkårene. Før øjeblikkelig opstart beder vi om udtrykkelig bekræftelse.",
      ar: "حماية المستهلك في الاتحاد الأوروبي. إذا انسحبتم من العقد بعد الحجز، تسري تعليمات حق الانسحاب. وقبل البدء الفوري بالعمل نطلب منكم تأكيدًا صريحًا.",
      uk: "Захист прав споживачів у ЄС. Якщо ви відмовляєтеся від договору після бронювання, діють умови, викладені в інформації про право на відмову. Перед негайним початком роботи ми просимо вашого явного підтвердження.",
      ru: "Защита прав потребителей ЕС. Если вы откажетесь от договора после бронирования, действуют условия уведомления о праве на отказ. Перед немедленным началом работы мы просим вашего явного подтверждения.",
      vi: "Bảo vệ người tiêu dùng theo quy định của EU. Nếu các bạn hủy hợp đồng sau khi đặt dịch vụ, áp dụng hướng dẫn về quyền hủy hợp đồng. Trước khi bắt đầu công việc ngay, chúng tôi đề nghị các bạn xác nhận rõ ràng.",
      es: "Protección del consumidor de la UE. Si desisten después de reservar, se aplican las condiciones de la información sobre el derecho de desistimiento. Antes de comenzar el servicio de inmediato, lo confirmamos de forma expresa con ustedes.",
    },
    footerPills: {
      de: ["Schweizer Recht · aus der Schweiz", "DSGVO- und revDSG-konform", "Vereidigte Übersetzer DE · EN · DA"],
      en: ["Swiss law · Switzerland-based", "GDPR and revDSG compliant", "Sworn translators DE · EN · DA"],
      da: ["Schweizisk lov · fra Schweiz", "GDPR og revDSG-konform", "Autoriserede oversættere DE · EN · DA"],
      ar: ["القانون السويسري · مقرّنا في سويسرا", "متوافق مع GDPR وrevDSG", "مترجمون محلّفون DE · EN · DA"],
      uk: ["Швейцарське право · компанія зі Швейцарії", "Відповідно до GDPR і швейцарського revDSG", "Присяжні перекладачі DE · EN · DA"],
      ru: ["Швейцарское право · компания из Швейцарии", "Соответствие GDPR и revDSG", "Присяжные переводчики DE · EN · DA"],
      vi: ["Luật Thụy Sĩ · trụ sở tại Thụy Sĩ", "Tuân thủ GDPR và revDSG", "Phiên dịch viên tuyên thệ DE · EN · DA"],
      es: ["Derecho suizo · desde Suiza", "Conforme al RGPD y a la revDSG", "Traductores jurados DE · EN · DA"],
    },
  },

  // ─────────────────────────────────────────── DIFFERENTIATOR
  differentiator: {
    eyebrow: {
      de: "Was uns unterscheidet",
      en: "What sets us apart",
      da: "Det, der gør os anderledes",
      ar: "ما يميّزنا",
      uk: "Чим ми відрізняємося",
      ru: "Чем мы отличаемся",
      vi: "Điều khác biệt ở chúng tôi",
      es: "Lo que nos diferencia",
    },
    headlineA: {
      de: "Nicht die grösste Agentur.",
      en: "Not the biggest agency.",
      da: "Ikke det største bureau.",
      ar: "لسنا أكبر وكالة.",
      uk: "Не найбільша агенція.",
      ru: "Не самое большое агентство.",
      vi: "Không phải công ty lớn nhất.",
      es: "No la agencia más grande.",
    },
    headlineB: {
      de: "Die aufmerksamste.",
      en: "The most attentive one.",
      da: "Det mest nærværende.",
      ar: "بل الأكثر اهتمامًا.",
      uk: "Найуважніша.",
      ru: "Самое внимательное.",
      vi: "Mà là nơi chu đáo nhất.",
      es: "La más atenta.",
    },
    lead: {
      de: "Wir nehmen bewusst nicht jedes Mandat an. Dadurch bleibt Zeit für die, die wir begleiten.",
      en: "We take on only a limited number of cases each month, so we keep time for every couple we guide.",
      da: "Vi tager bevidst kun et begrænset antal sager om måneden, så vi har tid til hvert par, vi vejleder.",
      ar: "نقبل عددًا محدودًا فقط من الحالات كل شهر، لنحتفظ بالوقت الكافي لكل زوجين نرافقهما.",
      uk: "Щомісяця ми беремо лише обмежену кількість справ, тож маємо час для кожної пари, яку супроводжуємо.",
      ru: "Мы берём лишь ограниченное число дел в месяц, чтобы у нас оставалось время для каждой пары, которую мы сопровождаем.",
      vi: "Mỗi tháng chúng tôi chỉ nhận một số lượng hồ sơ có hạn, để luôn có thời gian cho từng cặp đôi chúng tôi đồng hành.",
      es: "No aceptamos todos los casos, y es a propósito. Así nos queda tiempo para las parejas que acompañamos.",
    },
    col1Title: {
      de: "Bei anderen",
      en: "Elsewhere",
      da: "Andre steder",
      ar: "في أماكن أخرى",
      uk: "Деінде",
      ru: "У других",
      vi: "Ở nơi khác",
      es: "Con otros",
    },
    col1Label: {
      de: "Klassische Agenturen",
      en: "Traditional agencies",
      da: "Traditionelle bureauer",
      ar: "الوكالات التقليدية",
      uk: "Традиційні агенції",
      ru: "Обычные агентства",
      vi: "Các công ty dịch vụ truyền thống",
      es: "Agencias tradicionales",
    },
    col2Title: {
      de: "Bei uns",
      en: "With us",
      da: "Hos os",
      ar: "معنا",
      uk: "У нас",
      ru: "У нас",
      vi: "Với chúng tôi",
      es: "Con nosotros",
    },
    theirs: {
      de: [
        "Automatisierte E-Mail-Antworten",
        "Ticket-Nummer statt Ansprechperson",
        "Ihr erhaltet Formular-PDFs und sollt selbst nachforschen",
        "Preisliste voller Sternchen und Zusatzgebühren",
        "Nur Werktage, 9–17 Uhr",
      ],
      en: [
        "Automated email responses",
        "A ticket number instead of a person",
        "You receive form PDFs and are told to figure it out",
        "Price list full of footnotes and add-ons",
        "Weekdays only, 9 am – 5 pm",
      ],
      da: [
        "Automatiske e-mailsvar",
        "Et sagsnummer i stedet for en person",
        "I får blanket-PDF-er og skal selv finde ud af det",
        "Prisliste fuld af stjerner og tillæg",
        "Kun hverdage, 9–17",
      ],
      ar: [
        "ردود آلية بالبريد الإلكتروني",
        "رقم تذكرة بدلًا من شخص",
        "تتلقون نماذج PDF ويُطلب منكم تدبّر أمركم بأنفسكم",
        "قائمة أسعار مليئة بالحواشي والإضافات",
        "أيام العمل فقط، من 9 صباحًا حتى 5 مساءً",
      ],
      uk: [
        "Автоматичні відповіді електронною поштою",
        "Номер тікета замість людини",
        "Вам надсилають PDF-бланки, а далі розбирайтеся самі",
        "Прайс-лист, повний приміток і доплат",
        "Лише в робочі дні, 9:00–17:00",
      ],
      ru: [
        "Автоматические ответы по электронной почте",
        "Номер заявки вместо живого человека",
        "Вам присылают PDF-бланки, и разбираться приходится самим",
        "Прайс-лист, полный сносок и доплат",
        "Только по будням, 9:00–17:00",
      ],
      vi: [
        "Email trả lời tự động",
        "Một mã phiếu yêu cầu thay vì một con người",
        "Các bạn nhận các mẫu PDF và phải tự tìm hiểu",
        "Bảng giá đầy chú thích và phụ phí",
        "Chỉ ngày thường, 9:00–17:00",
      ],
      es: [
        "Respuestas automáticas por correo electrónico",
        "Un número de ticket en lugar de una persona de contacto",
        "Reciben formularios en PDF y tienen que investigar por su cuenta",
        "Lista de precios llena de asteriscos y cargos adicionales",
        "Solo días laborables, 9:00–17:00",
      ],
    },
    ours: {
      de: [
        "Eine feste Ansprechperson von A bis Z",
        "Antwort in der Regel innerhalb weniger Stunden",
        "Digitale Begleitung für Routine. Wir prüfen vor der Behörde persönlich",
        "Festpreis im Vertrag, keine Zusatzgebühren",
        "WhatsApp-Support auch abends und am Wochenende",
      ],
      en: [
        "One dedicated contact from A to Z",
        "Replies typically within a few hours",
        "Digital workflow for routine. We personally review before the authority",
        "Fixed price in the contract, no add-ons",
        "WhatsApp support also evenings and weekends",
      ],
      da: [
        "Én fast kontaktperson fra A til Z",
        "Svar typisk inden for få timer",
        "Digitalt rutineflow. Vi gennemgår personligt før myndigheden",
        "Fastpris i kontrakten, ingen tillæg",
        "WhatsApp-support også aftener og weekender",
      ],
      ar: [
        "شخص واحد مخصّص للتواصل معكم من البداية إلى النهاية",
        "ردود عادةً خلال بضع ساعات",
        "سير عمل رقمي للمهام الروتينية، ومراجعة شخصية منا قبل الوصول إلى الجهة الرسمية",
        "سعر ثابت في العقد، دون إضافات",
        "دعم عبر WhatsApp في المساء وعطلات نهاية الأسبوع أيضًا",
      ],
      uk: [
        "Одна постійна контактна особа від початку до кінця",
        "Відповідь зазвичай протягом кількох годин",
        "Цифровий процес для рутини. Перед поданням до органу влади ми все перевіряємо особисто",
        "Фіксована ціна в договорі, без доплат",
        "Підтримка у WhatsApp також увечері та у вихідні",
      ],
      ru: [
        "Одно личное контактное лицо от начала до конца",
        "Ответ обычно в течение нескольких часов",
        "Цифровой процесс для рутины. Перед подачей в ведомство мы всё проверяем лично",
        "Фиксированная цена в договоре, без доплат",
        "Поддержка в WhatsApp также по вечерам и в выходные",
      ],
      vi: [
        "Một người phụ trách riêng từ A đến Z",
        "Thường phản hồi trong vài giờ",
        "Quy trình số hóa cho các việc thông thường. Chúng tôi trực tiếp kiểm tra trước khi gửi đến cơ quan chức năng",
        "Giá cố định trong hợp đồng, không phụ phí",
        "Hỗ trợ qua WhatsApp cả buổi tối và cuối tuần",
      ],
      es: [
        "Una persona de contacto fija de principio a fin",
        "Respuesta normalmente en pocas horas",
        "Acompañamiento digital para lo rutinario. Antes de enviar nada a la autoridad, lo revisamos personalmente",
        "Precio fijo en el contrato, sin cargos adicionales",
        "Asistencia por WhatsApp también por las noches y los fines de semana",
      ],
    },
  },

  // ─────────────────────────────────────────── PRICING
  pricing: {
    eyebrow: {
      de: "Eure Pakete",
      en: "Your packages",
      da: "Jeres pakker",
      ar: "باقاتكم",
      uk: "Ваші пакети",
      ru: "Ваши пакеты",
      vi: "Các gói dịch vụ",
      es: "Sus paquetes",
    },
    headlineA: {
      de: "Festpreis im Vertrag.",
      en: "A fixed price in the contract.",
      da: "Fast pris i kontrakten.",
      ar: "سعر ثابت في العقد.",
      uk: "Фіксована ціна в договорі.",
      ru: "Фиксированная цена в договоре.",
      vi: "Giá cố định trong hợp đồng.",
      es: "Precio fijo en el contrato.",
    },
    // The italic accent after headlineA. Only Complete includes the authority fee, so it names Complete.
    headlineHighlight: {
      de: "Bei Complete inklusive Behördengebühr.",
      en: "Complete includes the authority fee.",
      da: "Complete inkluderer myndighedsgebyret.",
      ar: "باقة Complete تشمل رسوم الجهة الرسمية.",
      uk: "У Complete збір органу влади вже включено.",
      ru: "В Complete сбор ведомства уже включён.",
      vi: "Gói Complete đã gồm lệ phí cơ quan chức năng.",
      es: "Complete incluye la tasa de la autoridad.",
    },
    // Empty on purpose: headlineA and its accent say it all.
    headlineB: {
      de: "",
      en: "",
      da: "",
      ar: "",
      uk: "",
      ru: "",
      vi: "",
      es: "",
    },
    lead: {
      de: "",
      en: "",
      da: "",
      ar: "",
      uk: "",
      ru: "",
      vi: "",
      es: "",
    },
    popular: {
      de: "Empfohlen",
      en: "Recommended",
      da: "Anbefalet",
      ar: "موصى بها",
      uk: "Рекомендуємо",
      ru: "Рекомендуем",
      vi: "Đề xuất",
      es: "Recomendado",
    },
    swipeCount: {
      de: "2 Pakete + 2 Zusätze",
      en: "2 packages + 2 add-ons",
      da: "2 pakker + 2 tilvalg",
      ar: "باقتان + خدمتان إضافيتان",
      uk: "2 пакети + 2 додаткові послуги",
      ru: "2 пакета + 2 дополнительные услуги",
      vi: "2 gói + 2 dịch vụ bổ sung",
      es: "2 paquetes + 2 adicionales",
    },
    swipeHint: {
      de: "← zum Vergleichen wischen →",
      en: "← swipe to compare →",
      da: "← stryg for at sammenligne →",
      ar: "← اسحبوا للمقارنة →",
      uk: "← гортайте, щоб порівняти →",
      ru: "← листайте, чтобы сравнить →",
      vi: "← vuốt để so sánh →",
      es: "← deslicen para comparar →",
    },
    spring: {
      de: "−50\u00A0% Aktion",
      en: "−50\u00A0% offer",
      da: "−50\u00A0% tilbud",
      ar: "عرض بخصم 50 %",
      uk: "Акція −50\u00A0%",
      ru: "Акция −50 %",
      vi: "Ưu đãi −50%",
      es: "Oferta −50 %",
    },
    // Link to /preise with descriptive anchor text: the price keywords belong
    // to that page, so the home page must not compete for them.
    fullPricingHint: {
      de: "Ihr wollt die vollständige Aufschlüsselung, inklusive dem, was nicht enthalten ist?",
      en: "Want the full breakdown, including what's not included and why?",
      da: "Vil I have hele opdelingen, inklusive det der ikke er med?",
      ar: "تريدون التفاصيل الكاملة، بما في ذلك ما هو غير مشمول ولماذا؟",
      uk: "Хочете повну розбивку, зокрема що не входить і чому?",
      ru: "Хотите полную расшифровку, включая то, что не входит в цену, и почему?",
      vi: "Muốn xem chi tiết đầy đủ, kể cả những gì không bao gồm và lý do?",
      es: "¿Quieren el desglose completo, también lo que no está incluido?",
    },
    fullPricingLink: {
      de: "Alle Preise und Leistungen im Detail",
      en: "See all prices and services in detail",
      da: "Se alle priser og ydelser i detaljer",
      ar: "اطّلعوا على جميع الأسعار والخدمات بالتفصيل",
      uk: "Усі ціни та послуги детально",
      ru: "Все цены и услуги подробно",
      vi: "Xem chi tiết tất cả giá và dịch vụ",
      es: "Todos los precios y servicios en detalle",
    },
    comparisonLink: {
      de: "Ehrlicher Vergleich: selbst, zuhause oder mit uns",
      en: "Honest comparison: on your own, at home or with us",
      da: "Ærlig sammenligning: selv, hjemme eller med os",
      ar: "مقارنة صادقة: بأنفسكم، أو في بلدكم، أو معنا",
      uk: "Чесне порівняння: самотужки, удома чи з нами",
      ru: "Честное сравнение: самостоятельно, дома или с нами",
      vi: "So sánh trung thực: tự làm, tại nơi đang sống hay với chúng tôi",
      es: "Comparación honesta: por su cuenta, en su país o con nosotros",
    },
    feesTitle: {
      de: "Offizielle Behördengebühren einblenden",
      en: "Show official authority fees",
      da: "Vis officielle myndighedsgebyrer",
      ar: "عرض الرسوم الرسمية للجهات الحكومية",
      uk: "Показати офіційні державні збори",
      ru: "Показать официальные государственные сборы",
      vi: "Xem lệ phí chính thức của cơ quan chức năng",
      es: "Mostrar las tasas oficiales",
    },
    pkgFreeName: { de: "Schnupper-Check", en: "Starter check", da: "Prøve-tjek", ar: "الفحص الأولي", uk: "Стартова перевірка", ru: "Первичная проверка", vi: "Kiểm tra khởi đầu", es: "Comprobación inicial" },
    pkgFreeTag: {
      de: "Passt das überhaupt für uns?",
      en: "Is this a fit for us at all?",
      da: "Passer det til os?",
      ar: "هل هذا مناسب لنا أصلًا؟",
      uk: "Чи взагалі нам це підходить?",
      ru: "Подходит ли это нам вообще?",
      vi: "Liệu có phù hợp với chúng tôi không?",
      es: "¿Esto encaja con nosotros?",
    },
    pkgEssentialName: { de: "Essential · Selbermacher", en: "Essential · Self-guided", da: "Essential · Gør det selv", ar: "Essential · تنفيذ ذاتي", uk: "Essential · Самостійно", ru: "Essential · Самостоятельно", vi: "Essential · Tự thực hiện", es: "Essential · Por su cuenta" },
    pkgEssentialTag: {
      de: "Ihr macht es selbst, mit unserer Anleitung.",
      en: "You handle it, with our guidance.",
      da: "I står selv for det, med vores vejledning.",
      ar: "تتولّون الأمر بأنفسكم، بإرشاد منا.",
      uk: "Ви робите все самі — з нашими підказками.",
      ru: "Вы всё делаете сами — по нашей инструкции.",
      vi: "Các bạn tự làm, có chúng tôi hướng dẫn.",
      es: "Lo hacen ustedes mismos, con nuestras instrucciones.",
    },
    pkgCompleteName: { de: "Complete · Rundum begleitet", en: "Complete · Fully guided", da: "Complete · Fuldt vejledt", ar: "Complete · مرافقة كاملة", uk: "Complete · Повний супровід", ru: "Complete · Полное сопровождение", vi: "Complete · Đồng hành trọn gói", es: "Complete · Acompañamiento completo" },
    pkgCompleteTag: {
      de: "Wir übernehmen Antrag, Abstimmung und Behördenkommunikation. Ihr reist zur Trauung an.",
      en: "We handle the application, coordination and authority communication. You travel in for the ceremony.",
      da: "Vi håndterer ansøgning, koordinering og myndighedskontakt. I rejser til vielsen.",
      ar: "نتولّى الطلب والتنسيق والتواصل مع الجهات الرسمية. وتسافرون أنتم لحضور المراسم.",
      uk: "Ми беремо на себе заяву, координацію та спілкування з органами влади. Ви приїжджаєте на церемонію.",
      ru: "Мы берём на себя заявление, согласование и общение с ведомствами. Вы приезжаете на церемонию.",
      vi: "Chúng tôi lo hồ sơ, điều phối và liên lạc với cơ quan chức năng. Các bạn đến Đan Mạch để dự buổi lễ.",
      es: "Nos encargamos de la solicitud, la coordinación y la comunicación con las autoridades. Ustedes viajan para la ceremonia.",
    },
    pkgApostilleName: {
      de: "Apostille-Service · Add-on",
      en: "Apostille Service · add-on",
      da: "Apostille-Service · tilvalg",
      ar: "خدمة الأبوستيل · خدمة إضافية",
      uk: "Послуга апостилю · додатково",
      ru: "Услуга апостиля · дополнительно",
      vi: "Dịch vụ Apostille · bổ sung",
      es: "Servicio de apostilla · adicional",
    },
    pkgApostilleTag: {
      de: "Heiratsurkunde zuhause anerkannt",
      en: "Marriage certificate recognised at home",
      da: "Vielsesattest anerkendt hjemme",
      ar: "شهادة زواج معترف بها في بلدكم",
      uk: "Щоб свідоцтво про шлюб визнали вдома",
      ru: "Свидетельство о браке для признания в вашей стране",
      vi: "Giấy chứng nhận kết hôn được công nhận ở nước nhà",
      es: "Certificado de matrimonio reconocido en su país",
    },
    guideLinkEssential: {
      de: "Welche Dokumente? Zum Dokumente-Guide",
      en: "Which documents? See the documents guide",
      da: "Hvilke dokumenter? Se dokumentguiden",
      ar: "ما الوثائق المطلوبة؟ راجعوا دليل الوثائق",
      uk: "Які документи? Дивіться посібник про документи",
      ru: "Какие документы? Читайте справочник по документам",
      vi: "Cần giấy tờ gì? Xem cẩm nang về giấy tờ",
      es: "¿Qué documentos? Ver la guía de documentos",
    },
    guideLinkApostille: {
      de: "Wann braucht ihr sie? Zum Apostille-Guide",
      en: "When do you need it? See the apostille guide",
      da: "Hvornår skal I bruge den? Se apostilleguiden",
      ar: "متى تحتاجون إليه؟ راجعوا دليل الأبوستيل",
      uk: "Коли він потрібен? Дивіться посібник про апостиль",
      ru: "Когда он нужен? Читайте справочник по апостилю",
      vi: "Khi nào cần? Xem cẩm nang về Apostille",
      es: "¿Cuándo la necesitan? Ver la guía de la apostilla",
    },
    guideLinkComplete: {
      de: "Mit oder ohne Agentur? Zum Vergleich",
      en: "With or without an agency? See the comparison",
      da: "Med eller uden bureau? Se sammenligningen",
      ar: "مع وكالة أم بدونها؟ راجعوا المقارنة",
      uk: "З агенцією чи без? Дивіться порівняння",
      ru: "С агентством или без? Смотрите сравнение",
      vi: "Có nên dùng dịch vụ hay tự làm? Xem bảng so sánh",
      es: "¿Con o sin agencia? Ver la comparación",
    },
    ctaCheck: { de: "Gratis prüfen", en: "Free check", da: "Gratis tjek", ar: "الفحص المجاني", uk: "Безкоштовна перевірка", ru: "Бесплатная проверка", vi: "Kiểm tra miễn phí", es: "Comprobar gratis" },
    ctaBook: { de: "Essential wählen", en: "Choose Essential", da: "Vælg Essential", ar: "اختاروا Essential", uk: "Обрати Essential", ru: "Выбрать Essential", vi: "Chọn Essential", es: "Elegir Essential" },
    ctaAddon: { de: "Apostille hinzufügen", en: "Add apostille", da: "Tilføj apostille", ar: "أضيفوا الأبوستيل", uk: "Додати апостиль", ru: "Добавить апостиль", vi: "Thêm Apostille", es: "Añadir apostilla" },
    ctaComplete: { de: "Complete wählen", en: "Choose Complete", da: "Vælg Complete", ar: "اختاروا Complete", uk: "Обрати Complete", ru: "Выбрать Complete", vi: "Chọn Complete", es: "Elegir Complete" },
    priceNoteEssential: {
      de: "zzgl. ~€281 AFL-Gebühr, die ihr direkt an die Behörde zahlt",
      en: "plus ~€281 AFL fee, which you pay directly to the authority",
      da: "plus ~€281 AFL-gebyr, som I betaler direkte til myndigheden",
      ar: "إضافةً إلى رسوم AFL (~€281)، التي تدفعونها مباشرةً إلى الجهة الرسمية",
      uk: "плюс збір AFL ~€281, який ви сплачуєте безпосередньо органу влади",
      ru: "плюс сбор AFL ~€281, который вы платите ведомству напрямую",
      vi: "chưa gồm phí AFL ~€281, các bạn trả trực tiếp cho cơ quan chức năng",
      es: "más ~€281 de tasa de la AFL, que pagan directamente a la autoridad",
    },
    priceNoteComplete: {
      de: "inkl. AFL-Gebühr (~€281), keine separate Zahlung an die Behörde",
      en: "incl. AFL fee (~€281), no separate payment to the authority",
      da: "inkl. AFL-gebyr (~€281), ingen separat betaling til myndigheden",
      ar: "شاملًا رسوم AFL (~€281)، دون دفع منفصل إلى الجهة الرسمية",
      uk: "включно зі збором AFL (~€281), без окремої оплати органу влади",
      ru: "включая сбор AFL (~€281), без отдельной оплаты ведомству",
      vi: "đã gồm phí AFL (~€281), không phải thanh toán riêng cho cơ quan chức năng",
      es: "incluye la tasa de la AFL (~€281), sin pago aparte a la autoridad",
    },
    // Complete's key fact as a green chip on the card; the rest of the note
    // stays as the grey line under it.
    feeIncludedChip: {
      de: "Behördengebühr (~€281) inklusive",
      en: "Authority fee (~€281) included",
      da: "Myndighedsgebyr (~€281) inkluderet",
      ar: "الرسوم الرسمية (~€281) مشمولة",
      uk: "Державний збір (~€281) включено",
      ru: "Государственный сбор (~€281) включён",
      vi: "Đã gồm lệ phí nhà nước (~€281)",
      es: "Tasa oficial (~€281) incluida",
    },
    feeIncludedRest: {
      de: "keine separate Zahlung an die Behörde",
      en: "no separate payment to the authority",
      da: "ingen separat betaling til myndigheden",
      ar: "دون دفع منفصل إلى الجهة الرسمية",
      uk: "без окремої оплати органу влади",
      ru: "без отдельной оплаты ведомству",
      vi: "không phải thanh toán riêng cho cơ quan chức năng",
      es: "sin pago aparte a la autoridad",
    },
    priceNoteApostille: {
      de: "Zusatz zu beiden Paketen",
      en: "Add-on to both packages",
      da: "Tilvalg til begge pakker",
      ar: "خدمة إضافية للباقتين",
      uk: "Доповнення до обох пакетів",
      ru: "Дополнительно к обоим пакетам",
      vi: "Dịch vụ bổ sung cho cả hai gói",
      es: "Servicio adicional para ambos paquetes",
    },
    savingsLabel: {
      de: "Du sparst",
      en: "You save",
      da: "Du sparer",
      ar: "توفّرون",
      uk: "Ви заощаджуєте",
      ru: "Вы экономите",
      vi: "Các bạn tiết kiệm",
      es: "Ahorran",
    },
    slotsLeftSingular: {
      de: "Noch {n} Platz frei",
      en: "Only {n} slot left",
      da: "Kun {n} plads tilbage",
      ar: "لم يتبقَّ سوى {n} مكان",
      uk: "Залишилося лише {n} місце",
      ru: "Осталось всего {n} место",
      vi: "Chỉ còn {n} suất",
      es: "Solo queda {n} plaza libre",
    },
    slotsLeftPlural: {
      de: "Noch {n} Plätze diesen Monat",
      en: "{n} slots left this month",
      da: "{n} pladser tilbage i denne måned",
      ar: "الأماكن المتبقية هذا الشهر: {n}",
      uk: "Вільних місць цього місяця: {n}",
      ru: "Свободных мест в этом месяце: {n}",
      vi: "Còn {n} suất trong tháng này",
      es: "Quedan {n} plazas este mes",
    },
    trustStripTitle: {
      de: "Sichere Zahlung",
      en: "Secure payment",
      da: "Sikker betaling",
      ar: "دفع آمن",
      uk: "Безпечна оплата",
      ru: "Безопасная оплата",
      vi: "Thanh toán an toàn",
      es: "Pago seguro",
    },
    trustStripSub: {
      de: "SSL-verschlüsselt · DSGVO-konform · Teilzahlung möglich",
      en: "SSL-encrypted · GDPR-compliant · instalments available",
      da: "SSL-krypteret · GDPR-sikker · delbetaling muligt",
      ar: "مشفّر بتقنية SSL · متوافق مع GDPR · إمكانية الدفع على أقساط",
      uk: "SSL-шифрування · відповідно до GDPR · можлива оплата частинами",
      ru: "Шифрование SSL · соответствие GDPR · возможна оплата частями",
      vi: "Mã hóa SSL · tuân thủ GDPR · có thể trả theo đợt",
      es: "Cifrado SSL · conforme al RGPD · pago fraccionado posible",
    },
    // ── Package features (Essential / Complete / Apostille) ──
    essFeat1: {
      de: "Individuelle Doku-Liste",
      en: "Personal document list",
      da: "Personlig dokumentliste",
      ar: "قائمة وثائق شخصية",
      uk: "Персональний список документів",
      ru: "Индивидуальный список документов",
      vi: "Danh sách giấy tờ cá nhân hóa",
      es: "Lista de documentos personalizada",
    },
    essFeat2: {
      de: "Schritt-für-Schritt-Video und PDF (18 Seiten)",
      en: "Step-by-step video and PDF (18 pages)",
      da: "Trin-for-trin-video og PDF (18 sider)",
      ar: "فيديو وملف PDF خطوة بخطوة (18 صفحة)",
      uk: "Покрокове відео та PDF (18 сторінок)",
      ru: "Пошаговое видео и PDF (18 страниц)",
      vi: "Video và PDF hướng dẫn từng bước (18 trang)",
      es: "Video paso a paso y PDF (18 páginas)",
    },
    essFeat3: {
      de: "WhatsApp-Support während drei Wochen",
      en: "WhatsApp support for three weeks",
      da: "WhatsApp-support i tre uger",
      ar: "دعم عبر WhatsApp لمدة ثلاثة أسابيع",
      uk: "Підтримка у WhatsApp протягом трьох тижнів",
      ru: "Поддержка в WhatsApp в течение трёх недель",
      vi: "Hỗ trợ qua WhatsApp trong ba tuần",
      es: "Asistencia por WhatsApp durante tres semanas",
    },
    essFeat4: {
      de: "Anleitung zur Termin-Buchung in DK",
      en: "Guide to booking appointments in DK",
      da: "Vejledning til tidsbooking i DK",
      ar: "إرشادات لحجز المواعيد في الدنمارك",
      uk: "Інструкція з бронювання дати в Данії",
      ru: "Инструкция по бронированию даты в Дании",
      vi: "Hướng dẫn đặt lịch hẹn tại Đan Mạch",
      es: "Instrucciones para reservar la fecha en DK",
    },
    essFeatFee: {
      de: "AFL-Behördengebühr (Familieretshuset, ~€{fee}): zahlt ihr selbst direkt",
      en: "AFL authority fee (Familieretshuset, ~€{fee}): you pay directly",
      da: "AFL-myndighedsgebyr (Familieretshuset, ~€{fee}): I betaler selv",
      ar: "رسوم AFL الرسمية (Familieretshuset، ~€{fee}): تدفعونها مباشرةً",
      uk: "Державний збір AFL (Familieretshuset, ~€{fee}): ви сплачуєте напряму",
      ru: "Государственный сбор AFL (Familieretshuset, ~€{fee}): вы оплачиваете напрямую",
      vi: "Phí AFL của cơ quan chức năng (Familieretshuset, ~€{fee}): các bạn trả trực tiếp",
      es: "Tasa oficial de la AFL (Familieretshuset, ~€{fee}): la pagan ustedes directamente",
    },
    compFeat2: {
      de: "Individuelle Doku-Liste + manuelle Vorprüfung jedes Scans",
      en: "Personal document list + manual review of every scan",
      da: "Personlig dokumentliste + manuel gennemgang af hver scanning",
      ar: "قائمة وثائق شخصية + مراجعة يدوية لكل نسخة ممسوحة ضوئيًا",
      uk: "Персональний список документів + ручна перевірка кожного скану",
      ru: "Индивидуальный список документов + ручная проверка каждого скана",
      vi: "Danh sách giấy tờ cá nhân hóa + kiểm tra thủ công từng bản scan",
      es: "Lista de documentos personalizada + revisión manual de cada escaneo",
    },
    compFeat3: {
      de: "Beratung zu binationalen Sonderfällen und Drittstaaten-Dokumenten",
      en: "Advice on binational cases and third-country documents",
      da: "Rådgivning om binationale sager og tredjelands-dokumenter",
      ar: "استشارة في حالات الأزواج من جنسيتين مختلفتين ووثائق الدول من خارج الاتحاد الأوروبي",
      uk: "Консультації щодо пар різних громадянств і документів із третіх країн",
      ru: "Консультации по случаям пар с разным гражданством и документам из третьих стран",
      vi: "Tư vấn cho cặp đôi khác quốc tịch và giấy tờ từ nước thứ ba",
      es: "Asesoría sobre casos binacionales especiales y documentos de terceros países",
    },
    compFeat4: {
      de: "Wir reichen den Antrag beim Familieretshuset für euch ein",
      en: "We submit the application to Familieretshuset for you",
      da: "Vi indsender ansøgningen til Familieretshuset for jer",
      ar: "نقدّم الطلب إلى Familieretshuset نيابةً عنكم",
      uk: "Ми подаємо заяву до Familieretshuset замість вас",
      ru: "Мы подаём заявление в Familieretshuset за вас",
      vi: "Chúng tôi nộp hồ sơ lên Familieretshuset thay các bạn",
      es: "Presentamos por ustedes la solicitud ante Familieretshuset",
    },
    compFeat5: {
      de: "Wir kommunizieren mit der Behörde für euch",
      en: "We handle the authority communication for you",
      da: "Vi står for myndighedskontakten for jer",
      ar: "نتولّى التواصل مع الجهات الرسمية نيابةً عنكم",
      uk: "Ми ведемо спілкування з органами влади замість вас",
      ru: "Мы общаемся с ведомством за вас",
      vi: "Chúng tôi thay các bạn liên lạc với cơ quan chức năng",
      es: "Nos comunicamos con la autoridad por ustedes",
    },
    compFeat6: {
      de: "Trauungs-Termin in eurer Wunschkommune",
      en: "Wedding date in your preferred municipality",
      da: "Vielsestid i jeres ønskede kommune",
      ar: "موعد الزواج في البلدية التي تفضّلونها",
      uk: "Дата весілля в бажаній комуні",
      ru: "Дата регистрации в выбранной вами коммуне",
      vi: "Ngày cưới tại kommune các bạn mong muốn",
      es: "Fecha de la boda en el municipio que prefieran",
    },
    compFeat7: {
      de: "Persönliche Ansprechperson per WhatsApp, Mo–So 8–22 Uhr",
      en: "Personal contact via WhatsApp, Mon–Sun 8 am–10 pm",
      da: "Personlig kontaktperson via WhatsApp, man–søn 8–22",
      ar: "تواصل شخصي عبر WhatsApp، من الاثنين إلى الأحد من 8 صباحًا حتى 10 مساءً",
      uk: "Особистий контакт у WhatsApp, Пн–Нд 8:00–22:00",
      ru: "Личный контакт в WhatsApp, пн–вс 8:00–22:00",
      vi: "Người phụ trách riêng qua WhatsApp, thứ Hai–Chủ nhật 8:00–22:00",
      es: "Contacto personal por WhatsApp, de lunes a domingo, 8:00–22:00",
    },
    compFeatFee: {
      de: "Behördengebühr des Familieretshuset (~€{fee}) inklusive, ohne Aufschlag",
      en: "Familieretshuset authority fee (~€{fee}) included, no mark-up",
      da: "Familieretshusets gebyr (~€{fee}) inkluderet, uden tillæg",
      ar: "رسوم Familieretshuset الرسمية (~€{fee}) مشمولة دون أي زيادة",
      uk: "Державний збір Familieretshuset (~€{fee}) включено, без націнки",
      ru: "Государственный сбор Familieretshuset (~€{fee}) включён, без наценки",
      vi: "Đã gồm lệ phí của Familieretshuset (~€{fee}), không cộng thêm phí",
      es: "Tasa oficial de Familieretshuset (~€{fee}) incluida, sin recargo",
    },
    // One change of municipality after the approval is part of Complete (AGB §4).
    compFeatSwitch: {
      de: "Ein Gemeindewechsel inklusive, falls eure Wunschkommune keinen Termin hat",
      en: "One change of municipality included, if your preferred one has no date",
      da: "Ét kommuneskift inkluderet, hvis jeres ønskede kommune ikke har en dato",
      ar: "تغيير واحد للبلدية مشمول، إذا لم يتوفر موعد في البلدية التي تفضّلونها",
      uk: "Одна зміна комуни включена, якщо в бажаній комуні немає вільної дати",
      ru: "Одна смена коммуны включена, если в выбранной нет свободной даты",
      vi: "Đã gồm một lần đổi kommune, nếu kommune các bạn mong muốn không còn ngày trống",
      es: "Un cambio de municipio incluido, si el municipio que prefieren no tiene fecha",
    },
    // ── Add-ons (Pricing.tsx): apostille booked directly, the others on request ──
    // Interpreter: the municipalities ask for one when a partner does not
    // understand the ceremony language; their pages ask for no sworn
    // interpreter (checked 03.10.2026), so the add-on promises none.
    addonOnRequest: {
      de: "auf Anfrage",
      en: "on request",
      da: "på forespørgsel",
      ar: "عند الطلب",
      uk: "на запит",
      ru: "по запросу",
      vi: "theo yêu cầu",
      es: "a consultar",
    },
    addonApostilleCta: {
      de: "Apostille buchen",
      en: "Book apostille",
      da: "Book apostille",
      ar: "احجزوا الأبوستيل",
      uk: "Замовити апостиль",
      ru: "Заказать апостиль",
      vi: "Đặt Apostille",
      es: "Reservar apostilla",
    },
    addonRequestAria: {
      de: "{name} per {channel} anfragen",
      en: "Enquire about {name} via {channel}",
      da: "Forespørg om {name} via {channel}",
      ar: "استفسروا عن {name} عبر {channel}",
      uk: "Запитати про послугу «{name}» через {channel}",
      ru: "{name}: запрос через {channel}",
      vi: "Hỏi về {name} qua {channel}",
      es: "Consultar sobre {name} por {channel}",
    },
    addonPhotoName: {
      de: "Fotografie",
      en: "Photography",
      da: "Fotografi",
      ar: "التصوير",
      uk: "Фотозйомка",
      ru: "Фотосъёмка",
      vi: "Chụp ảnh cưới",
      es: "Fotografía",
    },
    addonPhotoF1: {
      de: "Zwei Partner zur Wahl: Ieva Vi und Renee Song",
      en: "Two partners to choose from: Ieva Vi and Renee Song",
      da: "To partnere at vælge imellem: Ieva Vi og Renee Song",
      ar: "شريكان للاختيار: Ieva Vi وRenee Song",
      uk: "Двоє партнерів на вибір: Ieva Vi та Renee Song",
      ru: "Два партнёра на выбор: Ieva Vi и Renee Song",
      vi: "Hai đối tác để lựa chọn: Ieva Vi và Renee Song",
      es: "Dos colaboradores a elegir: Ieva Vi y Renee Song",
    },
    addonPhotoF2: {
      de: "Für eure Trauung in Dänemark",
      en: "For your wedding in Denmark",
      da: "Til jeres vielse i Danmark",
      ar: "لزواجكم في الدنمارك",
      uk: "Для вашого весілля в Данії",
      ru: "Для вашей свадьбы в Дании",
      vi: "Cho đám cưới của các bạn tại Đan Mạch",
      es: "Para su boda en Dinamarca",
    },
    addonPhotoF3: {
      de: "Preis je nach Dauer und Ort",
      en: "Price depends on duration and location",
      da: "Pris efter varighed og sted",
      ar: "يتوقف السعر على المدة والمكان",
      uk: "Ціна залежить від тривалості та місця",
      ru: "Цена зависит от продолжительности и места",
      vi: "Giá tùy theo thời lượng và địa điểm",
      es: "Precio según duración y lugar",
    },
    addonPhotoWa: {
      de: "Hallo! Wir interessieren uns für Hochzeitsfotos von euren Partnern (Ieva Vi oder Renee Song) bei unserer Trauung in Dänemark.",
      en: "Hi! We're interested in wedding photos by your partners (Ieva Vi or Renee Song) at our wedding in Denmark.",
      da: "Hej! Vi er interesserede i bryllupsbilleder fra jeres partnere (Ieva Vi eller Renee Song) til vores vielse i Danmark.",
      ar: "مرحبًا! نحن مهتمون بتصوير زواجنا في الدنمارك مع أحد شركائكم (Ieva Vi أو Renee Song).",
      uk: "Добрий день! Нас цікавить весільна фотозйомка від ваших партнерів (Ieva Vi або Renee Song) на нашому весіллі в Данії.",
      ru: "Здравствуйте! Нас интересует свадебная фотосъёмка от ваших партнёров (Ieva Vi или Renee Song) на нашей свадьбе в Дании.",
      vi: "Xin chào! Chúng tôi muốn tìm hiểu về việc chụp ảnh cưới với đối tác của các bạn (Ieva Vi hoặc Renee Song) tại Đan Mạch.",
      es: "¡Hola! Nos interesan las fotos de boda de sus colaboradores (Ieva Vi o Renee Song) para nuestra boda en Dinamarca.",
    },
    addonInterpreterName: {
      de: "Dolmetscher",
      en: "Interpreter",
      da: "Tolk",
      ar: "مترجم فوري",
      uk: "Усний перекладач",
      ru: "Устный переводчик",
      vi: "Phiên dịch viên",
      es: "Intérprete",
    },
    addonInterpreterF1: {
      de: "Arabisch, Russisch, Spanisch oder Portugiesisch",
      en: "Arabic, Russian, Spanish or Portuguese",
      da: "Arabisk, russisk, spansk eller portugisisk",
      ar: "العربية أو الروسية أو الإسبانية أو البرتغالية",
      uk: "Арабська, російська, іспанська або португальська",
      ru: "Арабский, русский, испанский или португальский",
      vi: "Tiếng Ả Rập, tiếng Nga, tiếng Tây Ban Nha hoặc tiếng Bồ Đào Nha",
      es: "Árabe, ruso, español o portugués",
    },
    addonInterpreterF2: {
      de: "Nötig, wenn einer von euch die Trauungssprache nicht versteht",
      en: "Needed if one of you does not understand the ceremony language",
      da: "Nødvendig, hvis en af jer ikke forstår vielsessproget",
      ar: "مطلوب إذا كان أحدكم لا يفهم لغة المراسم",
      uk: "Потрібен, якщо хтось із вас не розуміє мови церемонії",
      ru: "Нужен, если кто-то из вас не понимает языка церемонии",
      vi: "Cần thiết nếu một trong hai bạn không hiểu ngôn ngữ của buổi lễ",
      es: "Necesario si uno de ustedes no entiende el idioma de la ceremonia",
    },
    addonInterpreterF3: {
      de: "Von uns organisiert, bitte früh anfragen",
      en: "Arranged by us, please ask early",
      da: "Arrangeret af os, spørg i god tid",
      ar: "نتولّى ترتيبه، يرجى الطلب مبكرًا",
      uk: "Організовуємо ми — будь ласка, звертайтеся заздалегідь",
      ru: "Организуем мы — пожалуйста, обращайтесь заранее",
      vi: "Do chúng tôi sắp xếp, vui lòng hỏi sớm",
      es: "Lo organizamos nosotros, consulten con tiempo",
    },
    addonInterpreterWa: {
      de: "Hallo! Wir brauchen einen Dolmetscher (Arabisch, Russisch, Spanisch oder Portugiesisch) für unsere Trauung in Dänemark.",
      en: "Hi! We need an interpreter (Arabic, Russian, Spanish or Portuguese) for our wedding in Denmark.",
      da: "Hej! Vi har brug for en tolk (arabisk, russisk, spansk eller portugisisk) til vores vielse i Danmark.",
      ar: "مرحبًا! نحتاج إلى مترجم فوري (العربية أو الروسية أو الإسبانية أو البرتغالية) لزواجنا في الدنمارك.",
      uk: "Добрий день! Нам потрібен усний перекладач (арабська, російська, іспанська або португальська мова) для нашого весілля в Данії.",
      ru: "Здравствуйте! Нам нужен устный переводчик (арабский, русский, испанский или португальский) на нашу свадьбу в Дании.",
      vi: "Xin chào! Chúng tôi cần một phiên dịch viên (tiếng Ả Rập, tiếng Nga, tiếng Tây Ban Nha hoặc tiếng Bồ Đào Nha) cho đám cưới tại Đan Mạch.",
      es: "¡Hola! Necesitamos un intérprete (árabe, ruso, español o portugués) para nuestra boda en Dinamarca.",
    },
    // Express add-on, on request (operator, 18.10.2026). No promise of a date:
    // Familieretshuset and the municipal calendars set the pace.
    addonExpressEyebrow: {
      de: "Express-Service",
      en: "Express service",
      da: "Express-service",
      ar: "خدمة الإكسبريس",
      uk: "Послуга «Експрес»",
      ru: "Услуга «Экспресс»",
      vi: "Dịch vụ Express",
      es: "Servicio Express",
    },
    addonExpressName: {
      de: "Express",
      en: "Express",
      da: "Express",
      ar: "إكسبريس",
      uk: "Експрес",
      ru: "Экспресс",
      vi: "Express",
      es: "Express",
    },
    addonExpressTag: {
      de: "So früh wie möglich heiraten: Wir prüfen eure Unterlagen mit Vorrang und reichen den Antrag ohne Wartezeit ein.",
      en: "Get married as early as possible: we check your documents with priority and file the application without delay.",
      da: "Bliv gift så tidligt som muligt: Vi tjekker jeres dokumenter med forrang og indsender ansøgningen uden ventetid.",
      ar: "تزوّجوا في أقرب وقت ممكن: نفحص وثائقكم بأولوية ونقدّم الطلب دون انتظار.",
      uk: "Одружитися якомога раніше: ми першочергово перевіряємо ваші документи й подаємо заяву без зволікань.",
      ru: "Пожениться как можно раньше: мы проверяем ваши документы в приоритетном порядке и подаём заявление без промедления.",
      vi: "Kết hôn sớm nhất có thể: chúng tôi ưu tiên kiểm tra giấy tờ và nộp hồ sơ ngay, không để chờ.",
      es: "Casarse lo antes posible: revisamos sus documentos con prioridad y presentamos la solicitud sin tiempo de espera.",
    },
    addonExpressF2: {
      de: "Wir suchen in allen passenden Kommunen nach dem frühesten freien Trautermin",
      en: "We search all suitable municipalities for the earliest free ceremony date",
      da: "Vi søger i alle relevante kommuner efter den tidligste ledige vielsestid",
      ar: "نبحث في جميع البلديات المناسبة عن أقرب موعد زواج متاح",
      uk: "Шукаємо найранішу вільну дату церемонії в усіх відповідних комунах",
      ru: "Ищем самую раннюю свободную дату церемонии во всех подходящих коммунах",
      vi: "Tìm lịch cưới trống sớm nhất ở tất cả các kommune phù hợp",
      es: "Buscamos en todos los municipios adecuados la primera fecha libre para la boda",
    },
    // 21.10.2026 (Alexander): never hint that we watch the municipalities' calendars; we ask them.
    addonExpressF3: {
      de: "Wir fragen laufend bei den Kommunen nach und melden uns, sobald ein früherer Termin frei wird; gebucht wird nur mit eurem OK",
      en: "We keep asking the municipalities and get in touch as soon as an earlier date opens up; we only book with your OK",
      da: "Vi spørger løbende kommunerne og giver besked, så snart en tidligere tid bliver ledig; vi booker kun med jeres OK",
      ar: "نسأل البلديات باستمرار ونتواصل معكم فور توفّر موعد أبكر، ولا نحجز إلا بموافقتكم",
      uk: "Ми постійно питаємо в комунах і повідомляємо, щойно звільняється раніша дата; бронюємо лише з вашої згоди",
      ru: "Мы постоянно спрашиваем в коммунах и сообщаем, как только освобождается более ранняя дата; бронируем только с вашего согласия",
      vi: "Chúng tôi thường xuyên hỏi các kommune và báo ngay khi có lịch sớm hơn; chỉ đặt khi hai bạn đồng ý",
      es: "Consultamos continuamente a los municipios y les avisamos en cuanto quede libre una fecha anterior; solo reservamos con su visto bueno",
    },
    addonExpressF4: {
      de: "Wechsel zu einer Kommune mit früherem Termin, wenn sich einer ergibt",
      en: "A switch to a municipality with an earlier date if one comes up",
      da: "Skift til en kommune med en tidligere tid, hvis der åbner sig en",
      ar: "الانتقال إلى بلدية لديها موعد أقرب إن توفّر",
      uk: "Перехід до комуни з ранішою датою, якщо така з’явиться",
      ru: "Переход в коммуну с более ранней датой, если она появится",
      vi: "Chuyển sang kommune có lịch sớm hơn nếu có",
      es: "Cambio a un municipio con una fecha anterior, si surge una",
    },
    addonExpressF5: {
      de: "Auf Wunsch Trauung mit Standesbeamtem an einem Ort eurer Wahl, wo die Kommune das anbietet, etwa in Kopenhagen auf einem Boot oder in einem Garten",
      en: "On request, a ceremony with a registrar at a place of your choice where the municipality offers it, for example on a boat or in a garden in Copenhagen",
      da: "Efter ønske vielse med vielsesforretter et sted efter eget valg, hvor kommunen tilbyder det, fx på en båd eller i en have i København",
      ar: "عند الطلب، مراسم زواج مع موظف التسجيل في مكان من اختياركم حيث تتيح البلدية ذلك، مثلًا على متن قارب أو في حديقة في كوبنهاغن",
      uk: "За бажанням церемонія з реєстратором у вибраному вами місці, якщо комуна це пропонує, наприклад на човні чи в саду в Копенгагені",
      ru: "По желанию церемония с регистратором в выбранном вами месте, если коммуна это предлагает, например на лодке или в саду в Копенгагене",
      vi: "Theo yêu cầu, lễ cưới với nhân viên hộ tịch tại nơi hai bạn chọn nếu kommune có cung cấp, ví dụ trên thuyền hoặc trong vườn ở Copenhagen",
      es: "Si lo desean, ceremonia con un oficiante en un lugar de su elección, donde el municipio lo ofrezca, por ejemplo en Copenhague en un barco o en un jardín",
    },
    addonExpressImgAlt: {
      de: "Brautpaar läuft Hand in Hand eine leere Landstrasse entlang",
      en: "A bride and groom running hand in hand down an empty country road",
      da: "Et brudepar løber hånd i hånd ned ad en tom landevej",
      ar: "عروسان يركضان يدًا بيد على طريق ريفي خالٍ",
      uk: "Наречені біжать, тримаючись за руки, порожньою заміською дорогою",
      ru: "Жених и невеста бегут, держась за руки, по пустой загородной дороге",
      vi: "Cô dâu chú rể nắm tay nhau chạy trên một con đường vắng ở vùng quê",
      es: "Novios corriendo de la mano por una carretera vacía en el campo",
    },
    addonExpressNote: {
      de: "Einen bestimmten Termin kann niemand garantieren: Den Antrag bearbeitet das Familieretshuset, die Termine vergeben die Kommunen. Eine Trauung ausserhalb des Rathauses kostet bei der Kommune extra; die Einzelheiten besprechen wir im Chat.",
      en: "Nobody can guarantee a specific date: Familieretshuset handles the application and the municipalities give the dates. A ceremony outside the town hall costs extra at the municipality; we go through the details by chat.",
      da: "Ingen kan garantere en bestemt dato: Familieretshuset behandler ansøgningen, og kommunerne fordeler tiderne. En vielse uden for rådhuset koster ekstra hos kommunen; detaljerne gennemgår vi på chat.",
      ar: "لا يمكن لأحد ضمان موعد محدد: فـFamilieretshuset تعالج الطلب، والبلديات تحدد المواعيد. وتُكلّف المراسم خارج مبنى البلدية رسومًا إضافية لدى البلدية، ونوضح التفاصيل عبر الدردشة.",
      uk: "Ніхто не може гарантувати конкретну дату: заяву розглядає Familieretshuset, а дати призначають комуни. Церемонія поза ратушею коштує в комуні додатково; подробиці обговоримо в чаті.",
      ru: "Никто не может гарантировать конкретную дату: заявление рассматривает Familieretshuset, а даты назначают коммуны. Церемония вне ратуши стоит в коммуне дополнительно; подробности обсудим в чате.",
      vi: "Không ai có thể bảo đảm một ngày cụ thể: Familieretshuset xử lý hồ sơ, còn các kommune sắp xếp lịch. Lễ cưới ngoài tòa thị chính có phụ phí của kommune; chi tiết chúng tôi trao đổi qua chat.",
      es: "Nadie puede garantizar una fecha concreta: Familieretshuset tramita la solicitud y los municipios asignan las fechas. Una ceremonia fuera del ayuntamiento tiene un costo adicional en el municipio; los detalles los vemos por chat.",
    },
    addonExpressWa: {
      de: "Hallo! Wir möchten so früh wie möglich in Dänemark heiraten und interessieren uns für den Express-Service. Unser Wunschzeitraum: ",
      en: "Hi! We would like to get married in Denmark as early as possible and are interested in the Express service. Our preferred time frame: ",
      da: "Hej! Vi vil gerne giftes i Danmark så tidligt som muligt og er interesserede i Express-servicen. Vores ønskede periode: ",
      ar: "مرحبًا! نرغب في الزواج في الدنمارك في أقرب وقت ممكن ونهتم بخدمة الإكسبريس. الفترة التي نفضّلها: ",
      uk: "Добрий день! Ми хочемо одружитися в Данії якомога раніше й цікавимося послугою «Експрес». Бажаний період: ",
      ru: "Здравствуйте! Мы хотим пожениться в Дании как можно раньше и интересуемся услугой «Экспресс». Желаемый период: ",
      vi: "Xin chào! Chúng tôi muốn kết hôn tại Đan Mạch sớm nhất có thể và quan tâm đến dịch vụ Express. Thời gian mong muốn của chúng tôi: ",
      es: "¡Hola! Queremos casarnos en Dinamarca lo antes posible y nos interesa el servicio Express. El periodo que preferimos: ",
    },
    addonExpressInquirySub: {
      de: "Vorrang bei Prüfung und Antrag, Terminsuche in allen Kommunen",
      en: "Priority check and application, date search in all municipalities",
      da: "Forrang ved tjek og ansøgning, søgning i alle kommuner",
      ar: "أولوية في الفحص والطلب، وبحث عن موعد في كل البلديات",
      uk: "Першочергова перевірка й заява, пошук дати в усіх комунах",
      ru: "Приоритетная проверка и заявление, поиск даты во всех коммунах",
      vi: "Ưu tiên kiểm tra và nộp hồ sơ, tìm lịch ở mọi kommune",
      es: "Prioridad en la revisión y la solicitud, búsqueda de fecha en todos los municipios",
    },
    apoFeat1: {
      de: "Apostille auf eurer dänischen Heiratsurkunde",
      en: "Apostille on your Danish marriage certificate",
      da: "Apostille på jeres danske vielsesattest",
      ar: "الأبوستيل على شهادة زواجكم الدنماركية",
      uk: "Апостиль на вашому данському свідоцтві про шлюб",
      ru: "Апостиль на ваше датское свидетельство о браке",
      vi: "Chứng nhận Apostille cho giấy chứng nhận kết hôn Đan Mạch",
      es: "Apostilla sobre su certificado de matrimonio danés",
    },
    apoFeat2: {
      de: "Apostille-Gebühr des dänischen Aussenministeriums inklusive",
      en: "Danish foreign ministry apostille fee included",
      da: "Udenrigsministeriets apostillegebyr inkluderet",
      ar: "رسوم الأبوستيل لدى وزارة الخارجية الدنماركية مشمولة",
      uk: "Збір Міністерства закордонних справ Данії за апостиль включено",
      ru: "Сбор МИД Дании за апостиль включён",
      vi: "Đã gồm lệ phí Apostille của Bộ Ngoại giao Đan Mạch",
      es: "Tasa de apostilla del Ministerio de Asuntos Exteriores danés incluida",
    },
    apoFeat3: {
      de: "Direkt-Kurier zum dänischen Aussenministerium",
      en: "Direct courier to the Danish foreign ministry",
      da: "Direkte kurer til det danske udenrigsministerium",
      ar: "بريد سريع مباشر إلى وزارة الخارجية الدنماركية",
      uk: "Пряма кур’єрська доставка до Міністерства закордонних справ Данії",
      ru: "Прямая курьерская доставка в МИД Дании",
      vi: "Chuyển phát trực tiếp đến Bộ Ngoại giao Đan Mạch",
      es: "Mensajería directa al Ministerio de Asuntos Exteriores danés",
    },
    apoFeat4: {
      de: "Getrackter UPS-Versand zu euch",
      en: "Tracked UPS shipping to you",
      da: "Sporet UPS-forsendelse til jer",
      ar: "شحن إليكم عبر UPS مع إمكانية التتبع",
      uk: "Відстежувана доставка UPS до вас",
      ru: "Отправка вам через UPS с отслеживанием",
      vi: "Gửi đến các bạn qua UPS có mã theo dõi",
      es: "Envío UPS con seguimiento hasta su domicilio",
    },
    apoFeat5: {
      de: "Beglaubigte Übersetzung DE/AT/CH bei Bedarf",
      en: "Certified translation DE/AT/CH if needed",
      da: "Autoriseret oversættelse DE/AT/CH efter behov",
      ar: "ترجمة معتمدة لألمانيا أو النمسا أو سويسرا عند الحاجة",
      uk: "За потреби — засвідчений переклад для Німеччини, Австрії чи Швейцарії",
      ru: "Заверенный перевод для DE/AT/CH при необходимости",
      vi: "Bản dịch công chứng cho Đức/Áo/Thụy Sĩ nếu cần",
      es: "Traducción jurada DE/AT/CH si hace falta",
    },
    // ── Gemeindewechsel (Pricing.tsx): booked directly, after the approval ──
    pkgSwitchName: {
      de: "Gemeindewechsel",
      en: "Change of municipality",
      da: "Kommuneskift",
      ar: "تغيير البلدية",
      uk: "Зміна комуни",
      ru: "Смена коммуны",
      vi: "Đổi kommune",
      es: "Cambio de municipio",
    },
    pkgSwitchTag: {
      de: "Genehmigt, aber kein Termin frei? Wir wechseln für euch die Gemeinde.",
      en: "Approved, but no date available? We move your wedding to another municipality.",
      da: "Godkendt, men ingen ledig dato? Vi flytter jeres vielse til en anden kommune.",
      ar: "حصلتم على الموافقة، لكن لا يوجد موعد متاح؟ ننقل زواجكم إلى بلدية أخرى.",
      uk: "Дозвіл уже є, але вільної дати немає? Ми перенесемо ваше весілля до іншої комуни.",
      ru: "Разрешение есть, а свободной даты нет? Мы перенесём вашу регистрацию в другую коммуну.",
      vi: "Đã được chấp thuận nhưng không còn ngày trống? Chúng tôi chuyển đám cưới của các bạn sang một kommune khác.",
      es: "¿Tienen la aprobación, pero no hay fecha libre? Cambiamos de municipio por ustedes.",
    },
    // Band above the packages for couples who already have the approval.
    switchBandLabel: {
      de: "Genehmigung schon da?",
      en: "Already approved?",
      da: "Allerede godkendt?",
      ar: "هل حصلتم على الموافقة بالفعل؟",
      uk: "Дозвіл уже є?",
      ru: "Разрешение уже есть?",
      vi: "Đã được chấp thuận rồi?",
      es: "¿Ya tienen la aprobación?",
    },
    switchBandMore: {
      de: "So läuft der Wechsel",
      en: "How the change works",
      da: "Sådan foregår skiftet",
      ar: "كيف يتم التغيير",
      uk: "Як відбувається зміна",
      ru: "Как проходит смена",
      vi: "Quy trình đổi kommune",
      es: "Así funciona el cambio",
    },
    switchLine: {
      de: "Wir wechseln für euch die Gemeinde, für {price} mit Geld-zurück-Garantie.",
      en: "We change the municipality for you, for {price} with a money-back guarantee.",
      da: "Vi skifter kommune for jer, for {price} med pengene-tilbage-garanti.",
      ar: "نغيّر البلدية من أجلكم مقابل {price} مع ضمان استرداد المال.",
      uk: "Ми змінимо для вас комуну за {price} з гарантією повернення коштів.",
      ru: "Мы сменим для вас коммуну за {price} с гарантией возврата денег.",
      vi: "Chúng tôi đổi kommune cho các bạn với giá {price}, có bảo đảm hoàn tiền.",
      es: "Cambiamos el municipio por ustedes por {price}, con garantía de devolución.",
    },
    priceNoteSwitch: {
      de: "Einzeln buchbar, im Complete einmal inklusive",
      en: "Bookable on its own, once included in Complete",
      da: "Kan bookes alene, én gang inkluderet i Complete",
      ar: "يمكن حجزه بشكل مستقل، وهو مشمول مرة واحدة في Complete",
      uk: "Можна замовити окремо; у Complete один раз включено",
      ru: "Можно заказать отдельно, один раз входит в Complete",
      vi: "Có thể đặt riêng, gói Complete đã gồm một lần",
      es: "Reservable por separado, incluido una vez en Complete",
    },
    switchFeat1: {
      de: "Wir finden eine Kommune mit freiem Trautermin",
      en: "We find a municipality with a free wedding date",
      da: "Vi finder en kommune med en ledig vielsesdato",
      ar: "نجد لكم بلدية لديها موعد زواج متاح",
      uk: "Ми знаходимо комуну з вільною датою весілля",
      ru: "Мы находим коммуну со свободной датой регистрации",
      vi: "Chúng tôi tìm kommune còn ngày cưới trống",
      es: "Encontramos un municipio con fecha de boda libre",
    },
    switchFeat2: {
      de: "Weiterleitung eurer Genehmigung beim Familieretshuset",
      en: "Your approval forwarded by Familieretshuset",
      da: "Jeres godkendelse videresendt af Familieretshuset",
      ar: "تحويل موافقتكم عن طريق Familieretshuset",
      uk: "Пересилання вашого дозволу через Familieretshuset",
      ru: "Familieretshuset пересылает ваше разрешение",
      vi: "Familieretshuset chuyển giấy phép kết hôn của các bạn",
      es: "Reenvío de su aprobación a través de Familieretshuset",
    },
    switchFeat3: {
      de: "Buchung des neuen Termins, Bestätigung an euch",
      en: "The new date booked, confirmation sent to you",
      da: "Den nye dato booket, bekræftelse til jer",
      ar: "حجز الموعد الجديد وإرسال التأكيد إليكم",
      uk: "Бронювання нової дати й підтвердження для вас",
      ru: "Бронирование новой даты и подтверждение для вас",
      vi: "Đặt ngày mới, gửi xác nhận cho các bạn",
      es: "Reserva de la nueva fecha y confirmación para ustedes",
    },
    switchFeat4: {
      de: "Auch wenn ihr den Antrag selbst gestellt habt",
      en: "Also if you applied yourselves",
      da: "Også hvis I selv har ansøgt",
      ar: "حتى لو قدّمتم الطلب بأنفسكم",
      uk: "Також якщо заяву ви подавали самі",
      ru: "Даже если заявление вы подавали сами",
      vi: "Kể cả khi các bạn tự nộp hồ sơ",
      es: "También si presentaron la solicitud ustedes mismos",
    },
    // Money-back guarantee of the change of municipality (AGB §4, since 27.10.2026).
    switchGuarantee: {
      de: "Geld zurück, wenn wir in eurem Zeitraum keinen Termin sichern",
      en: "Money back if we can't secure a date in your time frame",
      da: "Pengene tilbage, hvis vi ikke sikrer en dato i jeres tidsrum",
      ar: "نعيد لكم المبلغ إذا لم نتمكن من تأمين موعد ضمن الفترة التي تحددونها",
      uk: "Повернемо гроші, якщо не забезпечимо дату у ваш період",
      ru: "Вернём деньги, если не обеспечим дату в ваш период",
      vi: "Hoàn tiền nếu chúng tôi không giữ được ngày cưới trong khoảng thời gian của các bạn",
      es: "Les devolvemos el dinero si no conseguimos una fecha en su periodo",
    },
    addonSwitchCta: {
      de: "Gemeindewechsel buchen",
      en: "Book the change",
      da: "Book kommuneskift",
      ar: "احجزوا تغيير البلدية",
      uk: "Замовити зміну комуни",
      ru: "Заказать смену коммуны",
      vi: "Đặt dịch vụ đổi kommune",
      es: "Reservar el cambio",
    },
    guideLinkSwitch: {
      de: "Welche Gemeinde passt?",
      en: "Which municipality fits?",
      da: "Hvilken kommune passer?",
      ar: "أي بلدية تناسبكم؟",
      uk: "Яка комуна вам підходить?",
      ru: "Какая коммуна подходит?",
      vi: "Kommune nào phù hợp?",
      es: "¿Qué municipio les conviene?",
    },
    // ── Risk-reversal strip below the cards ──
    rr1: {
      de: "Festpreis im Vertrag",
      en: "Fixed price in the contract",
      da: "Fast pris i kontrakten",
      ar: "سعر ثابت في العقد",
      uk: "Фіксована ціна в договорі",
      ru: "Фиксированная цена в договоре",
      vi: "Giá cố định trong hợp đồng",
      es: "Precio fijo en el contrato",
    },
    // Checkout offers full payment or a deposit (ExpressCheckout, preiseFaq),
    // so "100\u00A0% at booking" would be untrue.
    rr2: {
      de: "Komplett oder mit Anzahlung zahlbar",
      en: "Pay in full or with a deposit",
      da: "Betal fuldt ud eller med depositum",
      ar: "الدفع كاملًا أو بدفعة مقدّمة",
      uk: "Оплата повністю або з передоплатою",
      ru: "Оплата полностью или с предоплатой",
      vi: "Thanh toán toàn bộ hoặc đặt cọc",
      es: "Pago completo o con anticipo",
    },
    // The statutory right for EU consumers; granted to Swiss customers by
    // contract (operator decision 28.09.2026), which is the real extra here.
    rr3: {
      de: "14\u00A0Tage Widerrufsrecht, auch für die Schweiz",
      en: "14-day right of withdrawal, Switzerland included",
      da: "14 dages fortrydelsesret, også i Schweiz",
      ar: "حق الانسحاب من العقد خلال 14 يومًا، بما في ذلك سويسرا",
      uk: "14 днів на відмову від договору, зокрема й у Швейцарії",
      ru: "Право на отказ в течение 14 дней, в том числе для Швейцарии",
      vi: "Quyền hủy hợp đồng trong 14 ngày, áp dụng cả tại Thụy Sĩ",
      es: "14 días de derecho de desistimiento, también para Suiza",
    },
    rr4: {
      de: "Kostenloser Eignungs-Check vor Buchung",
      en: "Free eligibility check before booking",
      da: "Gratis egnethedstjek før booking",
      ar: "فحص أهلية مجاني قبل الحجز",
      uk: "Безкоштовна перевірка можливості шлюбу перед бронюванням",
      ru: "Бесплатная проверка перед бронированием",
      vi: "Kiểm tra điều kiện miễn phí trước khi đặt",
      es: "Comprobación de requisitos gratuita antes de reservar",
    },
    // ── Free check card: the button ──
    leadCta: {
      de: "Kostenlos prüfen",
      en: "Check for free",
      da: "Tjek gratis",
      ar: "افحصوا مجانًا",
      // The full phrases wrap in the 4-column card at 1024px (Safari); the
      // price above already says €0.
      uk: "Перевірити",
      ru: "Проверить",
      vi: "Kiểm tra miễn phí",
      es: "Comprobar gratis",
    },
    // ── Free check card (first column, operator 11.10.2026) ──
    // Never promise the document list here: it only follows personal contact
    // (decision 04.10.2026, "Check: Kontakt zuerst").
    freeName: {
      de: "Gratis-Check · Kennenlernen",
      en: "Free check · Get to know us",
      da: "Gratis tjek · Lær os at kende",
      ar: "فحص مجاني · تعارف",
      uk: "Безкоштовна перевірка · Знайомство",
      ru: "Бесплатная проверка · Знакомство",
      vi: "Kiểm tra miễn phí · Làm quen",
      es: "Comprobación gratuita · Para conocernos",
    },
    freeTag: {
      de: "Passt Dänemark zu euch? Wir schauen uns euren Fall an, bevor ihr etwas bucht.",
      en: "Does Denmark work for you? We look at your case before you book anything.",
      da: "Passer Danmark til jer? Vi ser på jeres sag, før I booker noget.",
      ar: "هل الدنمارك مناسبة لكم؟ ننظر في حالتكم قبل أن تحجزوا أي شيء.",
      uk: "Чи підходить вам Данія? Ми розглянемо вашу ситуацію ще до бронювання.",
      ru: "Подходит ли вам Дания? Мы посмотрим на вашу ситуацию ещё до бронирования.",
      vi: "Đan Mạch có phù hợp với các bạn không? Chúng tôi xem xét trường hợp của các bạn trước khi các bạn đặt bất cứ gì.",
      es: "¿Dinamarca encaja con ustedes? Revisamos su caso antes de que reserven nada.",
    },
    freeNote: {
      de: "kostenlos und unverbindlich, ohne Zahlungsdaten",
      en: "free and non-binding, no payment details",
      da: "gratis og uforpligtende, uden betalingsoplysninger",
      ar: "مجاني ودون التزام، ودون بيانات دفع",
      uk: "безкоштовно й без зобов’язань, без платіжних даних",
      ru: "бесплатно и без обязательств, без платёжных данных",
      vi: "miễn phí, không ràng buộc, không cần thông tin thanh toán",
      es: "gratis y sin compromiso, sin datos de pago",
    },
    freeFeat1: {
      de: "60-Sekunden-Check online, Ergebnis sofort",
      en: "60-second online check, instant result",
      da: "60-sekunders tjek online, resultat med det samme",
      ar: "فحص عبر الإنترنت خلال 60 ثانية، والنتيجة فورًا",
      uk: "Онлайн-перевірка за 60 секунд, результат одразу",
      ru: "Онлайн-проверка за 60 секунд, результат сразу",
      vi: "Kiểm tra trực tuyến 60 giây, có kết quả ngay",
      es: "Comprobación de 60 segundos en línea, resultado al instante",
    },
    freeFeat2: {
      de: "Persönliche Vorab-Einschätzung eures Falls",
      en: "Personal first assessment of your case",
      da: "Personlig første vurdering af jeres sag",
      ar: "تقييم أولي شخصي لحالتكم",
      uk: "Особиста попередня оцінка вашої ситуації",
      ru: "Личная предварительная оценка вашей ситуации",
      vi: "Đánh giá sơ bộ riêng cho trường hợp của các bạn",
      es: "Primera valoración personal de su caso",
    },
    freeFeat3: {
      de: "Kurzes Kennenlernen per WhatsApp oder Telefon",
      en: "A short chat on WhatsApp or by phone",
      da: "En kort snak på WhatsApp eller telefon",
      ar: "تعارف قصير عبر WhatsApp أو الهاتف",
      uk: "Коротке знайомство у WhatsApp або телефоном",
      ru: "Короткое знакомство в WhatsApp или по телефону",
      vi: "Trò chuyện ngắn qua WhatsApp hoặc điện thoại",
      es: "Breve charla para conocernos, por WhatsApp o teléfono",
    },
    freeFeat4: {
      de: "Welches Paket passt, ohne Kaufzwang",
      en: "Which package fits, no obligation to buy",
      da: "Hvilken pakke der passer, uden købepligt",
      ar: "أي باقة تناسبكم، دون أي التزام بالشراء",
      uk: "Який пакет підходить, без зобов’язань купувати",
      ru: "Какой пакет подходит, без обязательств покупать",
      vi: "Gói nào phù hợp, không bắt buộc mua",
      es: "Qué paquete les conviene, sin obligación de compra",
    },
    freeFounders: {
      de: "Ihr sprecht direkt mit uns Gründern",
      en: "You talk directly to us, the founders",
      da: "I taler direkte med os, stifterne",
      ar: "تتحدثون مباشرةً معنا، نحن المؤسسين",
      uk: "Ви спілкуєтеся напряму з нами, засновниками",
      ru: "Вы общаетесь напрямую с нами, основателями",
      vi: "Các bạn trao đổi trực tiếp với chúng tôi, những người sáng lập",
      es: "Hablan directamente con nosotros, los fundadores",
    },
    freeChatLead: {
      de: "Lieber direkt schreiben?",
      en: "Rather write to us?",
      da: "Hellere skrive direkte?",
      ar: "تفضّلون الكتابة مباشرةً؟",
      uk: "Хочете написати напряму?",
      ru: "Хотите написать напрямую?",
      vi: "Muốn nhắn trực tiếp?",
      es: "¿Prefieren escribirnos directamente?",
    },
    freeWaMsg: {
      de: "Hallo! Wir würden euch gern kurz kennenlernen und wissen, ob eine Trauung in Dänemark für uns passt.",
      en: "Hi! We'd like to get to know you briefly and find out whether a wedding in Denmark works for us.",
      da: "Hej! Vi vil gerne lære jer kort at kende og høre, om en vielse i Danmark passer til os.",
      ar: "مرحبًا! نودّ التعرّف عليكم قليلًا ومعرفة ما إذا كان الزواج في الدنمارك مناسبًا لنا.",
      uk: "Добрий день! Ми хотіли б коротко познайомитися й дізнатися, чи підходить нам весілля в Данії.",
      ru: "Здравствуйте! Мы хотели бы коротко познакомиться и узнать, подходит ли нам регистрация брака в Дании.",
      vi: "Xin chào! Chúng tôi muốn làm quen một chút và tìm hiểu xem kết hôn tại Đan Mạch có phù hợp với chúng tôi không.",
      es: "¡Hola! Nos gustaría conocerlos brevemente y saber si una boda en Dinamarca es adecuada para nosotros.",
    },
    // ── Add-on block below the packages ──
    addonsEyebrow: {
      de: "Zusatzleistungen",
      en: "Add-ons",
      da: "Tilvalg",
      ar: "خدمات إضافية",
      uk: "Додаткові послуги",
      ru: "Дополнительные услуги",
      vi: "Dịch vụ bổ sung",
      es: "Servicios adicionales",
    },
    addonsTitle: {
      de: "Was ihr bei Bedarf dazunehmen könnt",
      en: "What you can add if you need it",
      da: "Det I kan tilføje efter behov",
      ar: "ما يمكنكم إضافته عند الحاجة",
      uk: "Що можна додати за потреби",
      ru: "Что можно добавить при необходимости",
      vi: "Những gì các bạn có thể thêm khi cần",
      es: "Lo que pueden añadir si lo necesitan",
    },
    addonSwitchImgAlt: {
      de: "Rathaus von Kopenhagen mit dem Drachenbrunnen in der Abenddämmerung",
      en: "Copenhagen City Hall with the Dragon Fountain at dusk",
      da: "Københavns Rådhus med Dragespringvandet i skumringen",
      ar: "مبنى بلدية كوبنهاغن مع نافورة التنين عند الغسق",
      uk: "Ратуша Копенгагена з фонтаном «Дракон» у сутінках",
      ru: "Ратуша Копенгагена с фонтаном «Дракон» в сумерках",
      vi: "Tòa thị chính Copenhagen với Đài phun nước Rồng lúc hoàng hôn",
      es: "Ayuntamiento de Copenhague con la Fuente del Dragón al anochecer",
    },
    addonSwitchImgCaption: {
      de: "Rathaus Kopenhagen",
      en: "Copenhagen City Hall",
      da: "Københavns Rådhus",
      ar: "بلدية كوبنهاغن",
      uk: "Ратуша Копенгагена",
      ru: "Ратуша Копенгагена",
      vi: "Tòa thị chính Copenhagen",
      es: "Ayuntamiento de Copenhague",
    },
    addonPhotoImgAlt: {
      de: "Unsere Partnerfotografin Ieva Vi",
      en: "Our partner photographer Ieva Vi",
      da: "Vores partnerfotograf Ieva Vi",
      ar: "المصوّرة الشريكة لدينا Ieva Vi",
      uk: "Наша партнерка, фотографиня Ieva Vi",
      ru: "Наш фотограф-партнёр Ieva Vi",
      vi: "Nhiếp ảnh gia đối tác của chúng tôi, Ieva Vi",
      es: "Nuestra fotógrafa colaboradora Ieva Vi",
    },
    addonPhotoImgCaption: {
      de: "Ieva Vi · Partnerfotografin, Kopenhagen",
      en: "Ieva Vi · partner photographer, Copenhagen",
      da: "Ieva Vi · partnerfotograf, København",
      ar: "Ieva Vi · مصوّرة شريكة، كوبنهاغن",
      uk: "Ieva Vi · партнерка-фотографиня, Копенгаген",
      ru: "Ieva Vi · фотограф-партнёр, Копенгаген",
      vi: "Ieva Vi · nhiếp ảnh gia đối tác, Copenhagen",
      es: "Ieva Vi · fotógrafa colaboradora, Copenhague",
    },
    addonInterpreterLangsLabel: {
      de: "Sprachen",
      en: "Languages",
      da: "Sprog",
      ar: "اللغات",
      uk: "Мови",
      ru: "Языки",
      vi: "Ngôn ngữ",
      es: "Idiomas",
    },
    langAr: { de: "Arabisch", en: "Arabic", da: "Arabisk", ar: "العربية", uk: "Арабська", ru: "Арабский", vi: "Tiếng Ả Rập", es: "Árabe" },
    langRu: { de: "Russisch", en: "Russian", da: "Russisk", ar: "الروسية", uk: "Російська", ru: "Русский", vi: "Tiếng Nga", es: "Ruso" },
    langEs: { de: "Spanisch", en: "Spanish", da: "Spansk", ar: "الإسبانية", uk: "Іспанська", ru: "Испанский", vi: "Tiếng Tây Ban Nha", es: "Español" },
    langPt: { de: "Portugiesisch", en: "Portuguese", da: "Portugisisk", ar: "البرتغالية", uk: "Португальська", ru: "Португальский", vi: "Tiếng Bồ Đào Nha", es: "Portugués" },
    addonPhotoTag: {
      de: "Eure Trauung in Bildern, von Ieva Vi oder Renee Song.",
      en: "Your wedding in pictures, by Ieva Vi or Renee Song.",
      da: "Jeres vielse i billeder, af Ieva Vi eller Renee Song.",
      ar: "زواجكم بالصور، بعدسة Ieva Vi أو Renee Song.",
      uk: "Ваше весілля у світлинах від Ieva Vi або Renee Song.",
      ru: "Ваша свадьба в фотографиях от Ieva Vi или Renee Song.",
      vi: "Lễ cưới của các bạn qua ống kính của Ieva Vi hoặc Renee Song.",
      es: "Su boda en imágenes, de la mano de Ieva Vi o Renee Song.",
    },
    addonInterpreterTag: {
      de: "Damit ihr beide bei der Trauung jedes Wort versteht.",
      en: "So that both of you understand every word of the ceremony.",
      da: "Så I begge forstår hvert ord ved vielsen.",
      ar: "حتى يفهم كلاكما كل كلمة في المراسم.",
      uk: "Щоб ви обоє розуміли кожне слово церемонії.",
      ru: "Чтобы вы оба понимали каждое слово церемонии.",
      vi: "Để cả hai bạn hiểu từng lời trong buổi lễ.",
      es: "Para que ambos entiendan cada palabra de la ceremonia.",
    },
    priceFrom: {
      de: "ab",
      en: "from",
      da: "fra",
      ar: "من",
      uk: "від",
      ru: "от",
      vi: "từ",
      es: "desde",
    },
    priceNoteInterpreter: {
      de: "auf Anfrage, je nach Sprache und Dauer",
      en: "on request, depending on language and length",
      da: "på forespørgsel, afhængigt af sprog og varighed",
      ar: "عند الطلب، حسب اللغة والمدة",
      uk: "на запит, залежно від мови й тривалості",
      ru: "по запросу, в зависимости от языка и продолжительности",
      vi: "theo yêu cầu, tùy ngôn ngữ và thời lượng",
      es: "a consultar, según idioma y duración",
    },
    addonChatHint: {
      de: "Unverbindlich per Chat anfragen",
      en: "Ask by chat, no obligation",
      da: "Spørg uforpligtende via chat",
      ar: "استفسروا عبر الدردشة دون التزام",
      uk: "Запитайте в чаті без зобов’язань",
      ru: "Спросите в чате без обязательств",
      vi: "Hỏi qua chat, không ràng buộc",
      es: "Consultar por chat sin compromiso",
    },
  },

  // ─────────────────────────────────────────── FINAL CTA
  finalCta: {
    eyebrow: {
      de: "Nächster Schritt",
      en: "Next step",
      da: "Næste skridt",
      ar: "الخطوة التالية",
      uk: "Наступний крок",
      ru: "Следующий шаг",
      vi: "Bước tiếp theo",
      es: "Siguiente paso",
    },
    headlineA: {
      de: "Vom ersten Gespräch",
      en: "From first call",
      da: "Fra første samtale",
      ar: "من أول مكالمة",
      uk: "Від першої розмови",
      ru: "От первого звонка",
      vi: "Từ cuộc gọi đầu tiên",
      es: "De la primera conversación",
    },
    headlineB: {
      de: "bis zum Ja-Wort.",
      en: "to your yes.",
      da: "til jeres ja.",
      ar: "حتى كلمة «نعم».",
      uk: "до вашого «так».",
      ru: "до вашего «да».",
      vi: "đến lời đồng ý.",
      es: "al “sí, quiero”.",
    },
    lead: {
      de: "Kostenlos prüfen, ob euer Fall passt. Wenn ja: Festpreis, persönliche Ansprechperson und klare Schritte, von der Dokumenten-Liste bis zur Heiratsurkunde in Dänemark.",
      en: "Start with a free eligibility check, no cost and no obligation. If you're a good fit, you get a fixed price, one dedicated contact and a clear path from your document checklist to your marriage certificate in Denmark.",
      da: "Gratis egnethedstjek først. Passer det: fast pris, fast kontakt og klare trin, fra dokumentlisten til vielsesattesten i Danmark.",
      ar: "ابدأوا بفحص أهلية مجاني، دون تكلفة ودون التزام. وإذا كانت حالتكم مناسبة، تحصلون على سعر ثابت، وشخص واحد مخصّص للتواصل، وطريق واضح من قائمة وثائقكم حتى شهادة زواجكم في الدنمارك.",
      uk: "Почніть із безкоштовної перевірки можливості шлюбу — без оплати й без зобов’язань. Якщо ваш випадок нам підходить, ви отримаєте фіксовану ціну, одну постійну контактну особу та чіткий шлях від чек-листа документів до свідоцтва про шлюб у Данії.",
      ru: "Начните с бесплатной проверки — без затрат и обязательств. Если ваш случай подходит, вы получаете фиксированную цену, одно личное контактное лицо и понятный путь от чек-листа документов до свидетельства о браке в Дании.",
      vi: "Hãy bắt đầu với bài kiểm tra điều kiện, miễn phí và không ràng buộc. Nếu phù hợp, các bạn có giá cố định, một người phụ trách riêng và lộ trình rõ ràng từ danh sách giấy tờ đến giấy chứng nhận kết hôn tại Đan Mạch.",
      es: "Comprueben gratis si su caso encaja. Si es así: precio fijo, una persona de contacto dedicada a ustedes y pasos claros, desde la lista de documentos hasta el certificado de matrimonio en Dinamarca.",
    },
    option1Title: {
      de: "Kostenloser 60-Sekunden-Check",
      en: "Free 60-second check",
      da: "Gratis 60-sekunders tjek",
      ar: "الفحص المجاني خلال 60 ثانية",
      uk: "Безкоштовна перевірка за 60 секунд",
      ru: "Бесплатная проверка за 60 секунд",
      vi: "Kiểm tra miễn phí trong 60 giây",
      es: "Comprobación gratuita de 60 segundos",
    },
    secondaryLink: {
      de: "Oder direkt Pakete ansehen",
      en: "Or view packages directly",
      da: "Eller se pakker direkte",
      ar: "أو اطّلعوا على الباقات مباشرةً",
      uk: "Або одразу перегляньте пакети",
      ru: "Или сразу посмотреть пакеты",
      vi: "Hoặc xem ngay các gói",
      es: "O ver directamente los paquetes",
    },
  },

  // ─────────────────────────────────────────── FAQ
  faq: {
    eyebrow: {
      de: "Häufige Fragen",
      en: "Frequently asked questions",
      da: "Ofte stillede spørgsmål",
      ar: "الأسئلة الشائعة",
      uk: "Поширені запитання",
      ru: "Частые вопросы",
      vi: "Câu hỏi thường gặp",
      es: "Preguntas frecuentes",
    },
    headlineA: {
      de: "Was Paare uns",
      en: "What couples",
      da: "Hvad par",
      ar: "ما يسألنا عنه",
      uk: "Що пари",
      ru: "Что пары",
      vi: "Những điều các cặp đôi",
      es: "Lo que las parejas",
    },
    showAll: {
      de: "Alle {count} Fragen anzeigen",
      en: "Show all {count} questions",
      da: "Vis alle {count} spørgsmål",
      ar: "عرض جميع الأسئلة ({count})",
      uk: "Показати всі запитання ({count})",
      ru: "Показать все вопросы ({count})",
      vi: "Xem tất cả {count} câu hỏi",
      es: "Ver las {count} preguntas",
    },
    showLess: {
      de: "Weniger anzeigen",
      en: "Show fewer",
      da: "Vis færre",
      ar: "عرض أقل",
      uk: "Показати менше",
      ru: "Свернуть",
      vi: "Thu gọn",
      es: "Ver menos",
    },
    headlineHighlight: {
      de: "oft fragen.",
      en: "often ask us.",
      da: "ofte spørger om.",
      ar: "الأزواج كثيرًا.",
      uk: "часто запитують у нас.",
      ru: "часто спрашивают у нас.",
      vi: "thường hỏi chúng tôi.",
      es: "nos preguntan a menudo.",
    },
    items: {
      de: [
        {
          q: "Können wir in Dänemark heiraten, auch wenn keiner von uns dort wohnt?",
          a: "Ja. Paare ohne dänischen Wohnsitz beantragen zuerst online beim Familieretshuset, der dänischen Agentur für Familienrecht, die Ehefähigkeitsprüfung. Mit der Genehmigung bucht ihr den Termin bei einer Kommune eurer Wahl, zum Beispiel in Tønder, Aabenraa oder Kopenhagen.",
          link: { href: "/eignungs-check", label: "In 60 Sekunden prüfen, ob es bei euch passt" },
        },
        {
          q: "Ist das dänische Procedere juristisch seriös oder ein „Schlupfloch“?",
          a: "Nein, ein Schlupfloch ist es nicht. Familieretshuset entscheidet nach dem dänischen Ehegesetz und prüft jedes Paar nach denselben Kriterien. Dänemark hat das Verfahren nur schlank gehalten und für Paare aus dem Ausland geöffnet. Bei individuellen Zweifeln empfehlen wir eine lokale Mandatsanwältin oder einen Mandatsanwalt.",
        },
        {
          q: "Ist eine dänische Heirat in Deutschland, Österreich und der Schweiz wirklich anerkannt?",
          a: "Ja. In Deutschland und Österreich braucht die dänische Heiratsurkunde dank EU-Verordnung 2016/1191 nicht einmal eine Apostille. In der Schweiz meldet ihr die Ehe über die Schweizer Vertretung (Regionales Konsularcenter in Stockholm); deren Merkblatt nennt keine Apostille, sondern verlangt die Heiratsurkunde im Original, gestempelt, unterschrieben und höchstens sechs Monate alt. Reicht ihr direkt bei der kantonalen Aufsichtsbehörde ein, fragt dort vorher, ob sie eine Apostille will. Verlangt eine Behörde eine Apostille, besorgt sie unser Apostille-Service (Add-on, €149,99) beim dänischen Aussenministerium.",
          link: { href: "/ratgeber/anerkennung-daenische-ehe-deutschland", label: "Anerkennung Schritt für Schritt" },
        },
        {
          q: "Wie lange dauert der gesamte Prozess bei euch?",
          a: "Mit vollständigen Unterlagen meist drei bis sechs Wochen bis zur Trauung, je nach Kommune. Das Familieretshuset entscheidet vollständige Anträge in der Regel innerhalb von 5\u00A0Arbeitstagen, danach zählt der freie Termin: Tønder traut dienstags und donnerstags, Ærø vergibt Termine innerhalb von 10\u00A0Wochen, in Kopenhagen wartet ihr bis zu 4\u00A0Monate (Stand Oktober 2026).",
          link: { href: "/ratgeber/daenemark-standesamt-wartezeiten-2026", label: "Aktuelle Wartezeiten 2026" },
        },
        {
          q: "Was kostet die Trauung selbst?",
          a: "Eine Trauungsgebühr im Rathaus fällt nicht an: Tønder und Aabenraa schreiben das ausdrücklich, Kopenhagen verlangt seit August 2025 keine mehr. Pflicht ist nur die Gebühr des Familieretshuset, 2.100 DKK (2026, ca. €281), die auch bei einer Absage nicht erstattet wird. Unser Essential-Paket kostet €249,99 zuzüglich dieser Gebühr, Complete €649,99 inklusive.",
          link: { href: "/preise", label: "Alle Preise und Leistungen" },
        },
        {
          q: "Welche Dokumente brauchen wir?",
          a: "Nach dem kostenlosen Check melden wir uns persönlich und schicken euch eine Dokumentenliste, genau auf eure Situation zugeschnitten. Meist reichen Pass oder ID und, wenn ihr gemeinsam im Ausland wohnt, ein Wohnsitznachweis; je nach Fall kommen Scheidungsurteil, Sterbeurkunde, Aufenthaltsnachweis, Geburtsurkunden gemeinsamer Kinder oder auf Anfrage Beziehungsnachweise dazu. Eine Ledigkeitsbescheinigung verlangt Familieretshuset nur im Einzelfall, ein Ehefähigkeitszeugnis gar nicht.",
          link: { href: "/ratgeber/dokumente-hochzeit-daenemark-2026", label: "Vollständige Dokumenten-Übersicht" },
        },
        {
          q: "Müssen wir beide nach Dänemark reisen?",
          a: "Ja, für die Trauung müsst ihr beide persönlich anwesend sein. Alles davor läuft digital und persönlich begleitet: Antrag, Unterlagenprüfung, Behördenkommunikation und Terminabstimmung. In Tønder und Aabenraa gibt es keine Anmeldung am Vortag, dort schafft ihr alles an einem Tag.",
          link: { href: "/ratgeber/grenznahe-standesaemter-daenemark", label: "Grenznahe Standesämter im Vergleich" },
        },
        {
          q: "Brauchen wir ein Visum für die Trauung?",
          a: "Das hängt vom Pass ab. Mit einem Pass aus der EU, dem EWR oder der Schweiz nicht. Alle anderen müssen am Trauungstag rechtmässig in Dänemark sein (§ 11 a dänisches Ehegesetz): je nach Nationalität visumfrei, mit Schengen-Visum (ein Touristenvisum genügt) oder mit Aufenthaltstitel. Den Nachweis verlangt das Familieretshuset; er steht auf eurer persönlichen Dokumentenliste, und wir prüfen ihn vorab.",
          link: { href: "/ratgeber/heiraten-daenemark-ohne-wohnsitz", label: "Heiraten in Dänemark als Ausländer" },
        },
        {
          q: "Brauchen wir Trauzeugen?",
          a: "Ja, zwei. Viele Kommunen stellen sie kostenlos, aber nicht zu jeder Uhrzeit: Tønder nur vormittags (dienstags und donnerstags, 9:30–12 Uhr), Aabenraa montags bis donnerstags, Kopenhagen immer. Bringt ihr in Tønder eigene mit, meldet ihr ihre Namen und Adressen mindestens eine Woche vorher.",
          link: { href: "/ratgeber/trauzeugen-daenemark", label: "Trauzeugen: die Regeln je Kommune" },
        },
        {
          q: "Wird die Trauung auf Deutsch gehalten?",
          a: "Das ist möglich. Tønder, Aabenraa, Haderslev, Kolding und Kopenhagen trauen laut ihren offiziellen Seiten auch auf Deutsch, sonst auf Englisch oder Dänisch. Versteht einer von euch keine dieser Sprachen, braucht ihr einen Dolmetscher; für Arabisch, Russisch, Spanisch oder Portugiesisch organisieren wir ihn auf Anfrage.",
          link: { href: "/ratgeber/dolmetscher-hochzeit-daenemark", label: "Dolmetscher und Trausprache" },
        },
        {
          q: "Was passiert, wenn Unterlagen fehlen?",
          a: "Dann wird es langsam: Fordert das Familieretshuset etwas nach, landet der Antrag in einer eigenen Warteschlange. Mitte Oktober 2026 wurden dort Anträge vom August bearbeitet, also rund 2\u00A0Monate Verzug. Deshalb prüfen wir jede Unterlage, bevor sie eingereicht wird.",
        },
        {
          q: "Wie lange ist die Genehmigung gültig?",
          a: "Vier Monate ab dem Ausstellungsdatum. Der Trauungstermin muss in dieses Fenster fallen, sonst braucht ihr einen neuen Antrag mit neuer Gebühr.",
        },
        {
          q: "Bekommen wir die Heiratsurkunde sofort?",
          a: "Ja. Direkt nach der Trauung bekommt ihr internationale Heiratsurkunden in mehreren Sprachen, in Tønder zum Beispiel zwei Exemplare auf Dänisch, Deutsch, Englisch, Französisch und Spanisch.",
        },
        {
          q: "Können wir samstags oder am Strand heiraten?",
          a: "Ja, aber nicht überall. Paaren ohne dänischen Wohnsitz bieten Tønder und Aabenraa keine Samstage an; samstags trauen zum Beispiel Kopenhagen (10:00–12:40 Uhr), Kolding (9–12 Uhr, mit eigenen Trauzeugen) und Fanø (jeden dritten Samstag im Monat). Am Strand heiraten könnt ihr etwa in Aabenraa, mit eigenen Trauzeugen.",
          link: { href: "/ratgeber/samstag-strand-hochzeit-daenemark", label: "Samstags- und Strandtrauungen" },
        },
        {
          q: "Können wir bei der Trauung den Nachnamen ändern?",
          a: "Nein, ohne Wohnsitz in Dänemark nicht. Die Urkunde zeigt die Namen aus euren Pässen am Trauungstag; die Namensänderung erklärt ihr danach zu Hause beim Standesamt.",
          link: { href: "/ratgeber/namensaenderung-nach-hochzeit-daenemark", label: "Namensänderung nach der Trauung" },
        },
        {
          q: "Können gleichgeschlechtliche Paare in Dänemark heiraten?",
          a: "Ja. Dänemark traut gleichgeschlechtliche Paare seit 2012 nach denselben Regeln wie alle anderen Paare. Deutschland, Österreich und die Schweiz erkennen die Ehe an; dort gilt die Ehe für alle seit 2017, 2019 und 2022.",
        },
      ],
      en: [
        {
          q: "Can we marry in Denmark if neither of us lives there?",
          a: "Yes. Couples without Danish residence first apply online to Familieretshuset, the Danish Agency of Family Law, for the marriage eligibility review. With the approval you book your date with a municipality of your choice, for example Tønder, Aabenraa or Copenhagen.",
          link: { href: "/eignungs-check", label: "Check in 60 seconds whether it works for you" },
        },
        {
          q: "Is the Danish process legally sound or some kind of loophole?",
          a: "No, it is not a loophole. Familieretshuset decides under Danish marriage law and checks every couple against the same criteria. Denmark has simply kept the procedure lean and open to couples from abroad. For individual legal doubts you should consult a licensed lawyer in your country.",
        },
        {
          q: "Is a Danish marriage legally recognised in Germany, Austria and Switzerland?",
          a: "Yes. In Germany and Austria the Danish marriage certificate does not even need an apostille, thanks to EU Regulation 2016/1191. In Switzerland you report the marriage via the Swiss representation (the Regional Consular Centre in Stockholm); its leaflet lists no apostille but asks for the original marriage certificate, stamped, signed and no older than six months. If you file directly with the cantonal supervisory authority, ask them beforehand whether they want an apostille. If an authority does require an apostille, our Apostille Service (add-on, €149.99) obtains it from the Danish Ministry of Foreign Affairs.",
          link: { href: "/ratgeber/anerkennung-daenische-ehe-deutschland", label: "How recognition works, step by step" },
        },
        {
          q: "How long does the whole process take with you?",
          a: "With complete documents usually three to six weeks to the wedding, depending on the municipality. Familieretshuset usually decides complete applications within 5\u00A0working days; after that, it depends on the next free date: Tønder holds ceremonies on Tuesdays and Thursdays, Ærø offers dates within 10\u00A0weeks, in Copenhagen you wait up to 4\u00A0months (as of October 2026).",
          link: { href: "/ratgeber/daenemark-standesamt-wartezeiten-2026", label: "Current waiting times 2026" },
        },
        {
          q: "What does the ceremony itself cost?",
          a: "There is no ceremony fee at the town hall: Tønder and Aabenraa say so explicitly, and Copenhagen has not charged one since August 2025. The only mandatory fee is Familieretshuset's, DKK 2,100 (2026, about €281), which is not refunded if you cancel. Our Essential package costs €249.99 plus this fee, Complete €649.99 including it.",
          link: { href: "/preise", label: "All prices and services" },
        },
        {
          q: "Which documents do we need?",
          a: "After the free check we get in touch personally and send you a document list tailored to your situation. Usually that is your passport or ID card and, if you live together abroad, proof of residence; depending on your case also a divorce decree, death certificate, proof of legal stay, birth certificates of joint children or, on request, relationship evidence. Familieretshuset asks for a certificate of marital status only in individual cases, and a certificate of no impediment is not required in Denmark.",
          link: { href: "/ratgeber/dokumente-hochzeit-daenemark-2026", label: "Full document overview" },
        },
        {
          q: "Do we both have to travel to Denmark?",
          a: "Yes, both of you need to be present in person for the ceremony. Before that, application, documents and appointment coordination can be handled remotely. Tønder and Aabenraa require no registration the day before, so there you can do it all in one day.",
          link: { href: "/ratgeber/grenznahe-standesaemter-daenemark", label: "Registry offices near the border compared" },
        },
        {
          q: "Do we need a visa for the wedding?",
          a: "It depends on the passport. With an EU, EEA or Swiss passport, no. Everyone else must be lawfully in Denmark on the wedding day (Section 11 a of the Danish Marriage Act): visa-free, with a Schengen visa (a tourist visa is enough) or with a residence permit, depending on nationality. Familieretshuset asks for proof; it is on your personal document list, and we check it beforehand.",
          link: { href: "/ratgeber/heiraten-daenemark-ohne-wohnsitz", label: "Marrying in Denmark as a foreigner" },
        },
        {
          q: "Do we need witnesses?",
          a: "Yes, two. Many municipalities provide them free of charge, but not at all times: Tønder only in the morning (Tuesdays and Thursdays, 9:30–12), Aabenraa Monday to Thursday, Copenhagen always. If you bring your own in Tønder, send their names and addresses at least one week ahead.",
          link: { href: "/ratgeber/trauzeugen-daenemark", label: "Witnesses: the rules per municipality" },
        },
        {
          q: "Can the ceremony be held in German?",
          a: "Yes, that is possible. According to their official pages, Tønder, Aabenraa, Haderslev, Kolding and Copenhagen also hold ceremonies in German, otherwise in English or Danish. If one of you understands none of these languages, you need an interpreter; for Arabic, Russian, Spanish or Portuguese we arrange one on request.",
          link: { href: "/ratgeber/dolmetscher-hochzeit-daenemark", label: "Interpreters and ceremony language" },
        },
        {
          q: "What happens if documents are missing?",
          a: "Then it gets slow: if Familieretshuset asks for more, the application moves to a separate queue. In mid-October 2026 that queue was working on applications from August, a delay of about 2\u00A0months. That is why we check every document before it is submitted.",
        },
        {
          q: "How long is the approval valid?",
          a: "Four months from the date of issue. Your wedding date must fall within this window, otherwise you need a new application with a new fee.",
        },
        {
          q: "Do we get the marriage certificate straight away?",
          a: "Yes. Right after the ceremony you receive international marriage certificates in several languages; in Tønder, for example, two copies in Danish, German, English, French and Spanish.",
        },
        {
          q: "Can we marry on a Saturday or on the beach?",
          a: "Yes, but not everywhere. Tønder and Aabenraa do not offer Saturday ceremonies to couples without Danish residence; on Saturdays you can marry, for example, in Copenhagen (10:00–12:40), Kolding (9–12, with your own witnesses) and on Fanø (every third Saturday of the month). A beach wedding is possible in Aabenraa, for example, with your own witnesses.",
          link: { href: "/ratgeber/samstag-strand-hochzeit-daenemark", label: "Saturday and beach weddings" },
        },
        {
          q: "Can we change our surname at the ceremony?",
          a: "No, not without residence in Denmark. The certificate shows the names in your passports on the wedding day; you declare the name change afterwards at your registry office at home.",
          link: { href: "/ratgeber/namensaenderung-nach-hochzeit-daenemark", label: "Changing your name after the wedding" },
        },
        {
          q: "Can same-sex couples marry in Denmark?",
          a: "Yes. Denmark has married same-sex couples since 2012, under the same rules as every other couple. Germany, Austria and Switzerland recognise the marriage; marriage equality has applied there since 2017, 2019 and 2022 respectively.",
        },
      ],
      da: [
        {
          q: "Kan vi blive gift i Danmark, selv om ingen af os bor der?",
          a: "Ja. Par uden dansk bopæl søger først online hos Familieretshuset om prøvelse af ægteskabsbetingelserne. Med godkendelsen booker I tiden hos en kommune efter eget valg, for eksempel i Tønder, Aabenraa eller København.",
          link: { href: "/eignungs-check", label: "Tjek på 60 sekunder, om det passer for jer" },
        },
        {
          q: "Er den danske model juridisk seriøs eller et smuthul?",
          a: "Nej, det er ikke et smuthul. Familieretshuset afgør efter dansk ægteskabslov og vurderer alle par efter de samme kriterier. Danmark har blot holdt proceduren enkel og åben for par fra udlandet. Er I i tvivl i jeres konkrete sag, anbefaler vi en advokat i jeres land.",
        },
        {
          q: "Er et dansk ægteskab anerkendt i Tyskland, Østrig og Schweiz?",
          a: "Ja. I Tyskland og Østrig behøver den danske vielsesattest takket være EU-forordning 2016/1191 ikke engang en apostille. I Schweiz anmelder I ægteskabet via den schweiziske repræsentation (det regionale konsularcenter i Stockholm); dens vejledning nævner ingen apostille, men kræver vielsesattesten i original, stemplet, underskrevet og højst seks måneder gammel. Indsender I direkte til den kantonale tilsynsmyndighed, så spørg dér på forhånd, om de vil have en apostille. Kræver en myndighed en apostille, skaffer vores Apostille-Service (tilvalg, €149,99) den fra Udenrigsministeriet.",
          link: { href: "/ratgeber/anerkennung-daenische-ehe-deutschland", label: "Sådan foregår anerkendelsen" },
        },
        {
          q: "Hvor lang tid tager hele forløbet hos jer?",
          a: "Med komplette dokumenter typisk tre til seks uger til vielsen, afhængigt af kommunen. Familieretshuset afgør komplette ansøgninger som regel inden for 5\u00A0arbejdsdage; derefter afhænger det af den næste ledige tid: Tønder vier tirsdag og torsdag, Ærø giver tider inden for 10\u00A0uger, i København venter I op til 4\u00A0måneder (pr. oktober 2026).",
          link: { href: "/ratgeber/daenemark-standesamt-wartezeiten-2026", label: "Aktuelle ventetider 2026" },
        },
        {
          q: "Hvad koster selve vielsen?",
          a: "Der er intet vielsesgebyr på rådhuset: Tønder og Aabenraa skriver det udtrykkeligt, og København har ikke opkrævet et siden august 2025. Det eneste obligatoriske gebyr er Familieretshusets, 2.100 kr. (2026, ca. €281), som ikke refunderes ved afbud. Vores Essential-pakke koster €249,99 plus dette gebyr, Complete €649,99 inklusive.",
          link: { href: "/preise", label: "Alle priser og ydelser" },
        },
        {
          q: "Hvilke dokumenter skal vi bruge?",
          a: "Efter det gratis tjek kontakter vi jer personligt og sender en dokumentliste, tilpasset jeres situation. Typisk: pas eller ID-kort og, hvis I bor sammen i udlandet, bopælsattest; afhængigt af sagen desuden skilsmissedom, dødsattest, dokumentation for lovligt ophold, fødselsattester på fælles børn eller, hvis der bliver bedt om det, dokumentation for forholdet. En civilstandsattest beder Familieretshuset kun om i den enkelte sag, og ægteskabsattest fra hjemlandet kræves ikke i Danmark.",
          link: { href: "/ratgeber/dokumente-hochzeit-daenemark-2026", label: "Fuld dokumentoversigt" },
        },
        {
          q: "Skal vi begge rejse til Danmark?",
          a: "Ja, I skal begge være til stede personligt ved vielsen. Forarbejdet med ansøgning, dokumenter og tid kan vi klare på afstand. Tønder og Aabenraa kræver intet fremmøde dagen før, så dér klarer I det hele på én dag.",
          link: { href: "/ratgeber/grenznahe-standesaemter-daenemark", label: "Grænsenære vielseskontorer sammenlignet" },
        },
        {
          q: "Skal vi have visum til vielsen?",
          a: "Det afhænger af passet. Med et pas fra EU, EØS eller Schweiz: nej. Alle andre skal have lovligt ophold i Danmark på vielsesdagen (ægteskabslovens § 11 a): visumfrit, med Schengen-visum (et turistvisum er nok) eller med opholdstilladelse, afhængigt af nationalitet. Familieretshuset beder om dokumentation; den står på jeres personlige dokumentliste, og vi tjekker den på forhånd.",
          link: { href: "/ratgeber/heiraten-daenemark-ohne-wohnsitz", label: "Gift i Danmark som udlænding" },
        },
        {
          q: "Skal vi have vidner med?",
          a: "Ja, to. Mange kommuner stiller gratis vidner, men ikke på alle tidspunkter: Tønder kun om formiddagen (tirsdag og torsdag, 9.30–12), Aabenraa mandag til torsdag, København altid. Tager I egne vidner med i Tønder, sender I deres navne og adresser mindst en uge før.",
          link: { href: "/ratgeber/trauzeugen-daenemark", label: "Vidner: reglerne pr. kommune" },
        },
        {
          q: "Kan vielsen foregå på tysk?",
          a: "Ja, det er muligt. Ifølge deres officielle sider vier Tønder, Aabenraa, Haderslev, Kolding og København også på tysk, ellers på engelsk eller dansk. Forstår en af jer ingen af disse sprog, skal I have en tolk; til arabisk, russisk, spansk eller portugisisk skaffer vi en på forespørgsel.",
          link: { href: "/ratgeber/dolmetscher-hochzeit-daenemark", label: "Tolk og vielsessprog" },
        },
        {
          q: "Hvad sker der, hvis der mangler dokumenter?",
          a: "Så går det langsomt: Beder Familieretshuset om mere, havner ansøgningen i en særskilt kø. Midt i oktober 2026 behandlede de dér sager fra august, altså cirka 2 måneders forsinkelse. Derfor tjekker vi hvert dokument, før det indsendes.",
        },
        {
          q: "Hvor længe er godkendelsen gyldig?",
          a: "Fire måneder fra udstedelsesdatoen. Vielsen skal ligge inden for dette vindue, ellers skal I søge igen og betale gebyret på ny.",
        },
        {
          q: "Får vi vielsesattesten med det samme?",
          a: "Ja. Lige efter vielsen får I internationale vielsesattester på flere sprog, i Tønder for eksempel to eksemplarer på dansk, tysk, engelsk, fransk og spansk.",
        },
        {
          q: "Kan vi blive gift en lørdag eller på stranden?",
          a: "Ja, men ikke overalt. Tønder og Aabenraa tilbyder ikke lørdagsvielser til par uden dansk bopæl; om lørdagen vier for eksempel København (10.00–12.40), Kolding (9–12, med egne vidner) og Fanø (hver tredje lørdag i måneden). En strandvielse er mulig for eksempel i Aabenraa, med egne vidner.",
          link: { href: "/ratgeber/samstag-strand-hochzeit-daenemark", label: "Lørdags- og strandvielser" },
        },
        {
          q: "Kan vi skifte efternavn ved vielsen?",
          a: "Nej, ikke uden bopæl i Danmark. Attesten viser navnene fra jeres pas på vielsesdagen; navneændringen anmelder I bagefter hos myndighederne derhjemme.",
          link: { href: "/ratgeber/namensaenderung-nach-hochzeit-daenemark", label: "Navneændring efter vielsen" },
        },
        {
          q: "Kan par af samme køn blive gift i Danmark?",
          a: "Ja. Danmark har viet par af samme køn siden 2012, efter de samme regler som alle andre par. Tyskland, Østrig og Schweiz anerkender ægteskabet; ægteskab for alle har gældt dér siden henholdsvis 2017, 2019 og 2022.",
        },
      ],
      ar: [
        {
          q: "هل يمكننا الزواج في الدنمارك إذا لم يكن أيٌّ منا مقيمًا فيها؟",
          a: "نعم. يتقدّم الأزواج غير المقيمين في الدنمارك أولًا بطلب عبر الإنترنت إلى Familieretshuset، وكالة قانون الأسرة الدنماركية، لفحص أهليتهم للزواج. ومع الموافقة تحجزون موعدكم لدى بلدية من اختياركم، مثل Tønder أو Aabenraa أو كوبنهاغن.",
          link: { href: "/eignungs-check", label: "تحقّقوا خلال 60 ثانية مما إذا كان ذلك ممكنًا لكم" },
        },
        {
          q: "هل الإجراء الدنماركي سليم قانونيًا أم أنه نوع من الثغرات؟",
          a: "لا، ليس ثغرة. تبتّ Familieretshuset وفق قانون الزواج الدنماركي، وتفحص كل زوجين وفق المعايير نفسها. كل ما في الأمر أن الدنمارك أبقت الإجراء بسيطًا ومفتوحًا للأزواج القادمين من الخارج. وفي حال وجود شكوك قانونية تخص حالتكم، ينبغي لكم استشارة محامٍ مرخّص في بلدكم.",
        },
        {
          q: "هل يُعترف قانونيًا بالزواج الدنماركي في ألمانيا والنمسا وسويسرا؟",
          a: "نعم. في ألمانيا والنمسا لا تحتاج شهادة الزواج الدنماركية حتى إلى أبوستيل، بفضل لائحة الاتحاد الأوروبي 2016/1191. أما في سويسرا فتسجّلون الزواج عبر الممثلية السويسرية (المركز القنصلي الإقليمي في ستوكهولم)، ونشرتها لا تذكر الأبوستيل، بل تطلب أصل شهادة الزواج مختومًا وموقّعًا، على ألا يزيد عمره على ستة أشهر. وإذا قدّمتم الوثائق مباشرة إلى سلطة الإشراف في الكانتون، فاسألوها مسبقًا عمّا إذا كانت تريد أبوستيل. وإذا طلبت جهة ما الأبوستيل، تستخرجه لكم خدمة الأبوستيل لدينا (خدمة إضافية، €149.99) من وزارة الخارجية الدنماركية.",
          link: { href: "/ratgeber/anerkennung-daenische-ehe-deutschland", label: "كيف يتم الاعتراف بالزواج، خطوة بخطوة" },
        },
        {
          q: "كم تستغرق العملية كلها معكم؟",
          a: "مع اكتمال الوثائق، عادةً من ثلاثة إلى ستة أسابيع حتى الزواج، بحسب البلدية. تبتّ Familieretshuset عادةً في الطلبات المكتملة خلال 5 أيام عمل؛ وبعد ذلك يتوقف الأمر على أقرب موعد متاح: تُقام المراسم في Tønder يومي الثلاثاء والخميس، وتوفّر Ærø مواعيد خلال 10 أسابيع، أما في كوبنهاغن فتنتظرون حتى 4 أشهر (بحسب الوضع في أكتوبر 2026).",
          link: { href: "/ratgeber/daenemark-standesamt-wartezeiten-2026", label: "مدد الانتظار الحالية 2026" },
        },
        {
          q: "كم تكلّف المراسم نفسها؟",
          a: "لا توجد رسوم للمراسم في دار البلدية: تنصّ على ذلك صراحةً كلٌّ من Tønder وAabenraa، ولا تفرض كوبنهاغن أي رسوم منذ أغسطس 2025. والرسوم الإلزامية الوحيدة هي رسوم Familieretshuset، وقدرها DKK 2,100 (2026، نحو €281)، ولا تُستردّ في حال الإلغاء. تكلّف باقة Essential لدينا €249.99 إضافةً إلى هذه الرسوم، وباقة Complete €649.99 شاملةً إياها.",
          link: { href: "/preise", label: "جميع الأسعار والخدمات" },
        },
        {
          q: "ما الوثائق التي نحتاجها؟",
          a: "بعد الفحص المجاني نتواصل معكم شخصيًا ونرسل إليكم قائمة وثائق مُعدّة لوضعكم. وفي معظم الحالات: جواز السفر أو بطاقة الهوية، وإثبات الإقامة إذا كنتم تقيمون معًا خارج الدنمارك؛ وبحسب حالتكم أيضًا حكم الطلاق أو شهادة الوفاة أو إثبات الإقامة القانونية أو شهادات ميلاد الأطفال المشتركين، أو إثباتات العلاقة عند الطلب. ولا تطلب Familieretshuset شهادة الحالة الاجتماعية إلا في حالات فردية، ولا تُشترط شهادة عدم وجود مانع من الزواج في الدنمارك.",
          link: { href: "/ratgeber/dokumente-hochzeit-daenemark-2026", label: "نظرة شاملة على الوثائق" },
        },
        {
          q: "هل يجب أن نسافر كلانا إلى الدنمارك؟",
          a: "نعم، يجب أن يحضر الطرفان شخصيًا مراسم الزواج. أما قبل ذلك، فيمكن إنجاز الطلب والوثائق وتنسيق الموعد عن بُعد. ولا تشترط Tønder وAabenraa التسجيل في اليوم السابق، لذا يمكنكم هناك إنجاز كل شيء في يوم واحد.",
          link: { href: "/ratgeber/grenznahe-standesaemter-daenemark", label: "مقارنة مكاتب الزواج المدني القريبة من الحدود" },
        },
        {
          q: "هل نحتاج إلى تأشيرة للزواج؟",
          a: "يتوقف ذلك على جواز السفر. مع جواز سفر من الاتحاد الأوروبي أو المنطقة الاقتصادية الأوروبية أو سويسرا، لا. أما حاملو الجوازات الأخرى فيجب أن يكونوا موجودين في الدنمارك بصورة قانونية يوم الزواج (المادة 11 a من قانون الزواج الدنماركي): دون تأشيرة، أو بتأشيرة شنغن (تكفي التأشيرة السياحية)، أو بتصريح إقامة، بحسب الجنسية. وتطلب Familieretshuset إثباتًا على ذلك؛ وهو مدرج في قائمة وثائقكم الشخصية، ونتحقق منه مسبقًا.",
          link: { href: "/ratgeber/heiraten-daenemark-ohne-wohnsitz", label: "الزواج في الدنمارك للأجانب" },
        },
        {
          q: "هل نحتاج إلى شهود؟",
          a: "نعم، شاهدان. توفّرهما بلديات كثيرة مجانًا، ولكن ليس في جميع الأوقات: Tønder في الفترة الصباحية فقط (الثلاثاء والخميس، 9:30–12)، وAabenraa من الاثنين إلى الخميس، وكوبنهاغن دائمًا. وإذا أحضرتم شهودكم في Tønder، فأرسلوا أسماءهم وعناوينهم قبل أسبوع على الأقل.",
          link: { href: "/ratgeber/trauzeugen-daenemark", label: "الشهود: القواعد في كل بلدية" },
        },
        {
          q: "هل يمكن إجراء المراسم بالألمانية؟",
          a: "نعم، هذا ممكن. فبحسب صفحاتها الرسمية، تُقيم Tønder وAabenraa وHaderslev وKolding وكوبنهاغن المراسم بالألمانية أيضًا، وإلا فبالإنجليزية أو الدنماركية. وإذا كان أحدكم لا يفهم أيًّا من هذه اللغات، فأنتم بحاجة إلى مترجم فوري؛ وللعربية أو الروسية أو الإسبانية أو البرتغالية نرتّب لكم مترجمًا عند الطلب.",
          link: { href: "/ratgeber/dolmetscher-hochzeit-daenemark", label: "المترجمون الفوريون ولغة المراسم" },
        },
        {
          q: "ماذا يحدث إذا كانت هناك وثائق ناقصة؟",
          a: "عندها تتباطأ الأمور: فإذا طلبت Familieretshuset وثائق إضافية، ينتقل الطلب إلى قائمة انتظار منفصلة. وفي منتصف أكتوبر 2026 كانت هذه القائمة تعالج طلبات من شهر أغسطس، أي بتأخير يقارب شهرين. ولهذا نراجع كل وثيقة قبل تقديمها.",
        },
        {
          q: "ما مدة صلاحية الموافقة؟",
          a: "أربعة أشهر من تاريخ الإصدار. ويجب أن يقع موعد زواجكم ضمن هذه المدة، وإلا فستحتاجون إلى طلب جديد برسوم جديدة.",
        },
        {
          q: "هل نحصل على شهادة الزواج فورًا؟",
          a: "نعم. مباشرةً بعد المراسم تتسلّمون شهادات زواج دولية بعدة لغات؛ ففي Tønder مثلًا نسختان بالدنماركية والألمانية والإنجليزية والفرنسية والإسبانية.",
        },
        {
          q: "هل يمكننا الزواج يوم السبت أو على الشاطئ؟",
          a: "نعم، ولكن ليس في كل مكان. لا تتيح Tønder وAabenraa مراسم يوم السبت للأزواج غير المقيمين في الدنمارك؛ أما أيام السبت فيمكنكم الزواج مثلًا في كوبنهاغن (10:00–12:40)، وKolding (9–12، مع شهودكم الخاصين)، وعلى جزيرة Fanø (في السبت الثالث من كل شهر). ويمكن الزواج على الشاطئ مثلًا في Aabenraa، مع شهودكم الخاصين.",
          link: { href: "/ratgeber/samstag-strand-hochzeit-daenemark", label: "الزواج يوم السبت وعلى الشاطئ" },
        },
        {
          q: "هل يمكننا تغيير اسم العائلة أثناء المراسم؟",
          a: "لا، ليس دون إقامة في الدنمارك. تُظهر الشهادة الأسماء الواردة في جوازات سفركم يوم الزواج؛ وتصرّحون بتغيير الاسم لاحقًا لدى مكتب الأحوال المدنية في بلدكم.",
          link: { href: "/ratgeber/namensaenderung-nach-hochzeit-daenemark", label: "تغيير الاسم بعد الزواج" },
        },
        {
          q: "هل يمكن للأزواج من الجنس نفسه الزواج في الدنمارك؟",
          a: "نعم. تعقد الدنمارك زواج الأزواج من الجنس نفسه منذ عام 2012، وفق القواعد نفسها المطبّقة على سائر الأزواج. وتعترف ألمانيا والنمسا وسويسرا بهذا الزواج؛ إذ يسري فيها مبدأ المساواة في الزواج منذ 2017 و2019 و2022 على التوالي.",
        },
      ],
      uk: [
        {
          q: "Чи можемо ми одружитися в Данії, якщо ніхто з нас там не живе?",
          a: "Так. Пари без місця проживання в Данії спершу подають онлайн-заяву до Familieretshuset — Агентства сімейного права Данії — на перевірку права на шлюб. Отримавши дозвіл, ви бронюєте дату в обраній вами комуні (муніципалітеті), наприклад у Теннері (Tønder), Обенро (Aabenraa) чи Копенгагені.",
          link: { href: "/eignungs-check", label: "Перевірте за 60 секунд, чи це можливо для вас" },
        },
        {
          q: "Данська процедура юридично надійна чи це якась лазівка?",
          a: "Ні, це не лазівка. Familieretshuset ухвалює рішення відповідно до данського закону про шлюб і перевіряє кожну пару за однаковими критеріями. Просто Данія зберегла процедуру простою й відкритою для пар з-за кордону. Якщо у вас є індивідуальні юридичні сумніви, зверніться до адвоката з ліцензією у вашій країні.",
        },
        {
          q: "Чи визнають данський шлюб у Німеччині, Австрії та Швейцарії?",
          a: "Так. У Німеччині та Австрії данському свідоцтву про шлюб завдяки Регламенту ЄС 2016/1191 навіть не потрібен апостиль. У Швейцарії про шлюб повідомляють через швейцарське представництво (Регіональний консульський центр у Стокгольмі); його пам’ятка апостиля не згадує, а вимагає оригінал свідоцтва про шлюб із печаткою та підписом, виданий не більше ніж шість місяців тому. Якщо ви подаєте документи безпосередньо до кантонального наглядового органу, заздалегідь запитайте там, чи потрібен їм апостиль. Якщо якась установа вимагає апостиль, ми отримаємо його в Міністерстві закордонних справ Данії в межах нашої послуги апостилю (додаткова послуга, €149,99).",
          link: { href: "/ratgeber/anerkennung-daenische-ehe-deutschland", label: "Як відбувається визнання — крок за кроком" },
        },
        {
          q: "Скільки триває весь процес із вами?",
          a: "З повним пакетом документів — зазвичай від трьох до шести тижнів до весілля, залежно від комуни. Familieretshuset зазвичай розглядає повні заяви протягом 5\u00A0робочих днів; далі все залежить від найближчої вільної дати: Теннер проводить церемонії щовівторка й щочетверга, Ере (Ærø) пропонує дати в межах 10\u00A0тижнів, у Копенгагені доведеться чекати до 4\u00A0місяців (станом на жовтень 2026 р.).",
          link: { href: "/ratgeber/daenemark-standesamt-wartezeiten-2026", label: "Актуальні строки очікування 2026" },
        },
        {
          q: "Скільки коштує сама церемонія?",
          a: "Збору за церемонію в ратуші немає: Теннер і Обенро прямо про це зазначають, а Копенгаген не стягує його з серпня 2025 року. Єдиний обов’язковий збір — збір Familieretshuset: DKK 2\u00A0100 (2026 р., близько €281), який не повертається в разі скасування. Наш пакет Essential коштує €249,99 плюс цей збір, Complete — €649,99 включно з ним.",
          link: { href: "/preise", label: "Усі ціни та послуги" },
        },
        {
          q: "Які документи нам потрібні?",
          a: "Після безкоштовної перевірки ми особисто зв’яжемося з вами й надішлемо список документів, складений саме для вашої ситуації. Зазвичай це паспорт або посвідчення особи та, якщо ви живете разом за кордоном, підтвердження місця проживання; залежно від випадку — також рішення про розірвання шлюбу, свідоцтво про смерть, підтвердження законного перебування, свідоцтва про народження спільних дітей або, на запит, підтвердження стосунків. Довідку про сімейний стан Familieretshuset вимагає лише в окремих випадках, а довідка про відсутність перешкод до шлюбу в Данії не потрібна.",
          link: { href: "/ratgeber/dokumente-hochzeit-daenemark-2026", label: "Повний огляд документів" },
        },
        {
          q: "Чи мусимо ми обоє їхати до Данії?",
          a: "Так, на церемонії ви обоє маєте бути присутні особисто. А до цього заяву, документи й узгодження дати можна вирішити дистанційно. У Теннері та Обенро не потрібно реєструватися напередодні церемонії, тож там усе можна зробити за один день.",
          link: { href: "/ratgeber/grenznahe-standesaemter-daenemark", label: "РАЦСи поблизу кордону: порівняння" },
        },
        {
          q: "Чи потрібна нам віза для весілля?",
          a: "Це залежить від паспорта. З паспортом ЄС, ЄЕЗ або Швейцарії — ні. Усі інші мають законно перебувати в Данії в день весілля (§ 11 a данського закону про шлюб): без візи, зі шенгенською візою (туристичної достатньо) або з посвідкою на проживання — залежно від громадянства. Familieretshuset вимагає підтвердження; воно є у вашому персональному списку документів, і ми перевіряємо його заздалегідь.",
          link: { href: "/ratgeber/heiraten-daenemark-ohne-wohnsitz", label: "Одруження в Данії для іноземців" },
        },
        {
          q: "Чи потрібні нам свідки?",
          a: "Так, двоє. Багато комун надають їх безкоштовно, але не в будь-який час: Теннер — лише зранку (щовівторка й щочетверга, 9:30–12:00), Обенро — з понеділка по четвер, Копенгаген — завжди. Якщо в Теннері ви приводите власних свідків, надішліть їхні імена та адреси щонайменше за тиждень.",
          link: { href: "/ratgeber/trauzeugen-daenemark", label: "Свідки: правила в кожній комуні" },
        },
        {
          q: "Чи можна провести церемонію німецькою мовою?",
          a: "Так, це можливо. Згідно з їхніми офіційними сторінками, Теннер, Обенро, Гадерслев (Haderslev), Колдінг (Kolding) і Копенгаген проводять церемонії також німецькою, в інших випадках — англійською або данською. Якщо хтось із вас не розуміє жодної з цих мов, потрібен усний перекладач; для арабської, російської, іспанської чи португальської ми організуємо його на запит.",
          link: { href: "/ratgeber/dolmetscher-hochzeit-daenemark", label: "Усні перекладачі та мова церемонії" },
        },
        {
          q: "Що буде, якщо бракує документів?",
          a: "Тоді все сповільнюється: якщо Familieretshuset запитує додаткові документи, заява потрапляє в окрему чергу. У середині жовтня 2026 року в цій черзі розглядали справи із серпня — затримка близько 2\u00A0місяців. Саме тому ми перевіряємо кожен документ перед поданням.",
        },
        {
          q: "Як довго дійсний дозвіл?",
          a: "Чотири місяці з дати видачі. Дата вашого весілля має припадати на цей період, інакше знадобиться нова заява з новим збором.",
        },
        {
          q: "Чи отримаємо ми свідоцтво про шлюб одразу?",
          a: "Так. Одразу після церемонії ви отримуєте міжнародні свідоцтва про шлюб кількома мовами; у Теннері, наприклад, два примірники данською, німецькою, англійською, французькою та іспанською.",
        },
        {
          q: "Чи можна одружитися в суботу або на пляжі?",
          a: "Так, але не всюди. Теннер і Обенро не пропонують суботніх церемоній парам без місця проживання в Данії; у суботу можна одружитися, наприклад, у Копенгагені (10:00–12:40), Колдінгу (9:00–12:00, з власними свідками) і на острові Фане (Fanø) щомісяця в третю суботу. Весілля на пляжі можливе, наприклад, в Обенро — з власними свідками.",
          link: { href: "/ratgeber/samstag-strand-hochzeit-daenemark", label: "Весілля в суботу та на пляжі" },
        },
        {
          q: "Чи можна змінити прізвище під час церемонії?",
          a: "Ні, без місця проживання в Данії — ні. У свідоцтві будуть зазначені імена з ваших паспортів на день весілля; про зміну прізвища ви заявляєте вже після цього в РАЦСі у своїй країні.",
          link: { href: "/ratgeber/namensaenderung-nach-hochzeit-daenemark", label: "Зміна прізвища після весілля" },
        },
        {
          q: "Чи можуть одностатеві пари одружитися в Данії?",
          a: "Так. Данія реєструє шлюби одностатевих пар з 2012 року за тими самими правилами, що й для всіх інших пар. Німеччина, Австрія та Швейцарія визнають такий шлюб; шлюбна рівність діє там відповідно з 2017, 2019 і 2022 років.",
        },
      ],
      ru: [
        {
          q: "Можно ли пожениться в Дании, если никто из нас там не живёт?",
          a: "Да. Пары без места жительства в Дании сначала подают онлайн-заявление в Familieretshuset (Датское агентство по семейному праву) на проверку права на вступление в брак. С полученным разрешением вы бронируете дату в коммуне (муниципалитете) по своему выбору, например в Тённере (Tønder), Обенро (Aabenraa) или Копенгагене.",
          link: { href: "/eignungs-check", label: "Проверьте за 60 секунд, подходит ли это вам" },
        },
        {
          q: "Датская процедура юридически надёжна или это какая-то лазейка?",
          a: "Нет, это не лазейка. Familieretshuset принимает решения по датскому закону о браке и проверяет каждую пару по одним и тем же критериям. Просто Дания сохранила процедуру несложной и открытой для пар из-за рубежа. При индивидуальных правовых сомнениях обратитесь к лицензированному юристу в вашей стране.",
        },
        {
          q: "Признаётся ли датский брак в Германии, Австрии и Швейцарии?",
          a: "Да. В Германии и Австрии датскому свидетельству о браке даже не нужен апостиль благодаря Регламенту ЕС 2016/1191. В Швейцарии о браке заявляют через швейцарское представительство (Региональный консульский центр в Стокгольме); его памятка апостиль не упоминает, а требует оригинал свидетельства о браке с печатью и подписью, выданный не более шести месяцев назад. Если вы подаёте документы напрямую в кантональный надзорный орган, заранее спросите там, нужен ли им апостиль. Если какое-либо ведомство требует апостиль, наша услуга апостиля (дополнительно, €149,99) получит его в Министерстве иностранных дел Дании.",
          link: { href: "/ratgeber/anerkennung-daenische-ehe-deutschland", label: "Как проходит признание брака: шаг за шагом" },
        },
        {
          q: "Сколько длится весь процесс с вами?",
          a: "При полном комплекте документов — обычно от трёх до шести недель до регистрации, в зависимости от коммуны. Familieretshuset по полным заявлениям обычно принимает решение в течение 5 рабочих дней; дальше всё зависит от ближайшей свободной даты: в Тённере (Tønder) регистрируют по вторникам и четвергам, на Эрё (Ærø) даты дают в пределах 10 недель, в Копенгагене ждать приходится до 4 месяцев (по состоянию на октябрь 2026 г.).",
          link: { href: "/ratgeber/daenemark-standesamt-wartezeiten-2026", label: "Актуальные сроки ожидания в 2026 году" },
        },
        {
          q: "Сколько стоит сама церемония?",
          a: "Сбора за церемонию в ратуше нет: Тённер (Tønder) и Обенро (Aabenraa) прямо об этом пишут, а Копенгаген не взимает его с августа 2025 года. Единственный обязательный сбор — сбор Familieretshuset: DKK 2 100 (2026 г., около €281), который не возвращается при отмене. Наш пакет Essential стоит €249,99 плюс этот сбор, Complete — €649,99 уже с ним.",
          link: { href: "/preise", label: "Все цены и услуги" },
        },
        {
          q: "Какие документы нам нужны?",
          a: "После бесплатной проверки мы лично свяжемся с вами и пришлём список документов, составленный под вашу ситуацию. Обычно это паспорт или удостоверение личности и, если вы живёте вместе за границей, подтверждение места жительства; в зависимости от случая — также решение о расторжении брака, свидетельство о смерти, подтверждение законного пребывания, свидетельства о рождении общих детей или, по запросу, подтверждения отношений. Справку о семейном положении Familieretshuset запрашивает лишь в отдельных случаях, а справка об отсутствии препятствий к браку в Дании не требуется.",
          link: { href: "/ratgeber/dokumente-hochzeit-daenemark-2026", label: "Полный обзор документов" },
        },
        {
          q: "Нужно ли нам обоим ехать в Данию?",
          a: "Да, на церемонии вы оба должны присутствовать лично. Всё, что до неё, — заявление, документы и согласование даты — можно сделать дистанционно. В Тённере (Tønder) и Обенро (Aabenraa) не нужно являться накануне для предварительной регистрации, поэтому там всё можно уложить в один день.",
          link: { href: "/ratgeber/grenznahe-standesaemter-daenemark", label: "Приграничные ратуши: сравнение" },
        },
        {
          q: "Нужна ли нам виза для регистрации брака?",
          a: "Это зависит от паспорта. С паспортом ЕС, ЕЭЗ или Швейцарии — нет. Все остальные в день регистрации должны находиться в Дании законно (§ 11 a датского Закона о браке): в зависимости от гражданства — без визы, по шенгенской визе (туристической достаточно) или с видом на жительство. Familieretshuset запрашивает подтверждение; оно есть в вашем персональном списке документов, и мы проверяем его заранее.",
          link: { href: "/ratgeber/heiraten-daenemark-ohne-wohnsitz", label: "Брак в Дании для иностранцев" },
        },
        {
          q: "Нужны ли нам свидетели?",
          a: "Да, двое. Многие коммуны предоставляют их бесплатно, но не в любое время: Тённер (Tønder) — только в первой половине дня (по вторникам и четвергам, 9:30–12:00), Обенро (Aabenraa) — с понедельника по четверг, Копенгаген — всегда. Если в Тённер вы приводите своих свидетелей, сообщите их имена и адреса не менее чем за неделю.",
          link: { href: "/ratgeber/trauzeugen-daenemark", label: "Свидетели: правила в каждой коммуне" },
        },
        {
          q: "Может ли церемония пройти на немецком?",
          a: "Да, это возможно. Согласно их официальным сайтам, Тённер (Tønder), Обенро (Aabenraa), Хадерслев (Haderslev), Колдинг (Kolding) и Копенгаген проводят церемонии и на немецком, в остальных случаях — на английском или датском. Если кто-то из вас не понимает ни одного из этих языков, нужен устный переводчик; для арабского, русского, испанского или португальского языка мы организуем его по запросу.",
          link: { href: "/ratgeber/dolmetscher-hochzeit-daenemark", label: "Переводчики и язык церемонии" },
        },
        {
          q: "Что будет, если каких-то документов не хватает?",
          a: "Тогда всё замедляется: если Familieretshuset запрашивает дополнительные документы, заявление переходит в отдельную очередь. В середине октября 2026 года там рассматривали дела, поступившие в августе, — задержка около 2 месяцев. Поэтому мы проверяем каждый документ до подачи.",
        },
        {
          q: "Сколько действует разрешение?",
          a: "Четыре месяца с даты выдачи. Дата регистрации должна попасть в этот срок, иначе понадобятся новое заявление и новый сбор.",
        },
        {
          q: "Получим ли мы свидетельство о браке сразу?",
          a: "Да. Сразу после церемонии вы получаете международные свидетельства о браке на нескольких языках; в Тённере (Tønder), например, два экземпляра на датском, немецком, английском, французском и испанском.",
        },
        {
          q: "Можно ли пожениться в субботу или на пляже?",
          a: "Да, но не везде. Тённер (Tønder) и Обенро (Aabenraa) не предлагают субботние церемонии парам без места жительства в Дании; по субботам можно пожениться, например, в Копенгагене (10:00–12:40), в Колдинге (Kolding; 9:00–12:00, со своими свидетелями) и на Фанё (Fanø; каждую третью субботу месяца). Свадьба на пляже возможна, например, в Обенро — со своими свидетелями.",
          link: { href: "/ratgeber/samstag-strand-hochzeit-daenemark", label: "Свадьба в субботу и на пляже" },
        },
        {
          q: "Можно ли сменить фамилию при регистрации брака?",
          a: "Нет, без места жительства в Дании — нельзя. В свидетельстве указываются имена из ваших паспортов на день регистрации; о смене фамилии вы заявляете потом в ЗАГСе (Standesamt) по месту жительства.",
          link: { href: "/ratgeber/namensaenderung-nach-hochzeit-daenemark", label: "Смена фамилии после свадьбы" },
        },
        {
          q: "Могут ли однополые пары пожениться в Дании?",
          a: "Да. Дания регистрирует браки однополых пар с 2012 года по тем же правилам, что и для всех остальных пар. Германия, Австрия и Швейцария признают такой брак; равноправие в браке действует там соответственно с 2017, 2019 и 2022 годов.",
        },
      ],
      vi: [
        {
          q: "Chúng tôi có thể kết hôn tại Đan Mạch nếu cả hai đều không sống ở đó không?",
          a: "Có. Các cặp đôi không cư trú tại Đan Mạch trước hết nộp hồ sơ trực tuyến lên Familieretshuset (Cơ quan Luật Gia đình Đan Mạch) để được thẩm định điều kiện kết hôn. Khi có giấy phép kết hôn, các bạn đặt ngày cưới tại chính quyền địa phương (kommune) tùy chọn, ví dụ Tønder, Aabenraa hoặc Copenhagen.",
          link: { href: "/eignungs-check", label: "Kiểm tra trong 60 giây xem trường hợp của các bạn có phù hợp không" },
        },
        {
          q: "Thủ tục tại Đan Mạch có vững chắc về mặt pháp lý không, hay chỉ là một kẽ hở?",
          a: "Không, đây không phải kẽ hở. Familieretshuset ra quyết định theo luật hôn nhân Đan Mạch và thẩm định mọi cặp đôi theo cùng tiêu chí. Đan Mạch chỉ đơn giản giữ thủ tục gọn nhẹ và mở cho các cặp đôi từ nước ngoài. Nếu có băn khoăn pháp lý riêng, các bạn nên hỏi ý kiến một luật sư có giấy phép hành nghề tại quốc gia của mình.",
        },
        {
          q: "Hôn nhân đăng ký tại Đan Mạch có được công nhận hợp pháp tại Đức, Áo và Thụy Sĩ không?",
          a: "Có. Tại Đức và Áo, giấy chứng nhận kết hôn Đan Mạch thậm chí không cần Apostille, nhờ Quy định EU 2016/1191. Tại Thụy Sĩ, các bạn trình báo việc kết hôn qua cơ quan đại diện của Thụy Sĩ (Trung tâm Lãnh sự Khu vực tại Stockholm); tờ hướng dẫn của cơ quan này không nhắc đến Apostille mà yêu cầu bản gốc giấy chứng nhận kết hôn có dấu và chữ ký, cấp không quá sáu tháng. Nếu các bạn nộp trực tiếp cho cơ quan giám sát cấp bang, hãy hỏi trước xem cơ quan đó có cần Apostille không. Nếu một cơ quan yêu cầu Apostille, dịch vụ Apostille của chúng tôi (dịch vụ bổ sung, €149,99) sẽ xin chứng nhận này từ Bộ Ngoại giao Đan Mạch.",
          link: { href: "/ratgeber/anerkennung-daenische-ehe-deutschland", label: "Quy trình công nhận, từng bước một" },
        },
        {
          q: "Làm cùng Danish Wedding thì toàn bộ quy trình mất bao lâu?",
          a: "Với giấy tờ đầy đủ, thường mất từ ba đến sáu tuần đến ngày cưới, tùy theo kommune. Familieretshuset thường ra quyết định đối với hồ sơ đầy đủ trong vòng 5 ngày làm việc; sau đó tùy thuộc vào ngày trống gần nhất: Tønder tổ chức lễ vào thứ Ba và thứ Năm, Ærø có lịch trong vòng 10 tuần, tại Copenhagen phải chờ tới 4 tháng (tính đến tháng 10/2026).",
          link: { href: "/ratgeber/daenemark-standesamt-wartezeiten-2026", label: "Thời gian chờ hiện tại năm 2026" },
        },
        {
          q: "Bản thân buổi lễ tốn bao nhiêu?",
          a: "Lễ kết hôn tại tòa thị chính không mất phí: Tønder và Aabenraa nêu rõ điều này, và Copenhagen không thu phí từ tháng 8/2025. Khoản phí bắt buộc duy nhất là phí của Familieretshuset, DKK 2.100 (năm 2026, khoảng €281), không được hoàn lại nếu các bạn hủy. Gói Essential của chúng tôi có giá €249,99 chưa gồm khoản phí này, gói Complete €649,99 đã gồm khoản phí này.",
          link: { href: "/preise", label: "Tất cả giá và dịch vụ" },
        },
        {
          q: "Chúng tôi cần những giấy tờ gì?",
          a: "Sau bài kiểm tra miễn phí, chúng tôi sẽ liên hệ trực tiếp và gửi các bạn danh sách giấy tờ dành riêng cho hoàn cảnh của mình. Thường gồm hộ chiếu hoặc căn cước và, nếu hai bạn sống chung ở nước ngoài, giấy xác nhận nơi cư trú; tùy trường hợp còn có bản án ly hôn, giấy chứng tử, giấy tờ chứng minh lưu trú hợp pháp, giấy khai sinh của con chung hoặc, khi được yêu cầu, bằng chứng về mối quan hệ. Familieretshuset chỉ yêu cầu giấy xác nhận tình trạng hôn nhân trong từng trường hợp cụ thể, và tại Đan Mạch không cần giấy xác nhận đủ điều kiện kết hôn.",
          link: { href: "/ratgeber/dokumente-hochzeit-daenemark-2026", label: "Tổng quan đầy đủ về giấy tờ" },
        },
        {
          q: "Cả hai chúng tôi có phải đến Đan Mạch không?",
          a: "Có, cả hai bạn phải có mặt trực tiếp tại buổi lễ. Trước đó, hồ sơ, giấy tờ và việc sắp xếp lịch hẹn đều có thể thực hiện từ xa. Tønder và Aabenraa không yêu cầu trình diện vào hôm trước, nên ở đó các bạn có thể làm mọi thứ trong một ngày.",
          link: { href: "/ratgeber/grenznahe-standesaemter-daenemark", label: "So sánh các cơ quan hộ tịch gần biên giới" },
        },
        {
          q: "Chúng tôi có cần thị thực để kết hôn không?",
          a: "Tùy vào hộ chiếu. Với hộ chiếu EU, EEA hoặc Thụy Sĩ thì không. Những người khác phải lưu trú hợp pháp tại Đan Mạch vào ngày cưới (Điều 11 a Luật Hôn nhân Đan Mạch): miễn thị thực, có thị thực Schengen (thị thực du lịch là đủ) hoặc có giấy phép cư trú, tùy theo quốc tịch. Familieretshuset yêu cầu giấy tờ chứng minh; giấy tờ này có trong danh sách giấy tờ dành riêng cho các bạn, và chúng tôi kiểm tra trước.",
          link: { href: "/ratgeber/heiraten-daenemark-ohne-wohnsitz", label: "Kết hôn tại Đan Mạch khi là người nước ngoài" },
        },
        {
          q: "Chúng tôi có cần người làm chứng không?",
          a: "Có, hai người. Nhiều kommune bố trí người làm chứng miễn phí, nhưng không phải lúc nào cũng có: Tønder chỉ vào buổi sáng (thứ Ba và thứ Năm, 9:30–12:00), Aabenraa từ thứ Hai đến thứ Năm, Copenhagen thì luôn có. Nếu tự mời người làm chứng ở Tønder, hãy gửi họ tên và địa chỉ của họ trước ít nhất một tuần.",
          link: { href: "/ratgeber/trauzeugen-daenemark", label: "Người làm chứng: quy định theo từng kommune" },
        },
        {
          q: "Buổi lễ có thể được tổ chức bằng tiếng Đức không?",
          a: "Có, điều này có thể thực hiện được. Theo trang chính thức của họ, Tønder, Aabenraa, Haderslev, Kolding và Copenhagen cũng tổ chức lễ bằng tiếng Đức; ở những nơi khác thì bằng tiếng Anh hoặc tiếng Đan Mạch. Nếu một trong hai bạn không hiểu ngôn ngữ nào trong số này, các bạn cần phiên dịch viên; với tiếng Ả Rập, tiếng Nga, tiếng Tây Ban Nha hoặc tiếng Bồ Đào Nha, chúng tôi có thể sắp xếp theo yêu cầu.",
          link: { href: "/ratgeber/dolmetscher-hochzeit-daenemark", label: "Phiên dịch viên và ngôn ngữ buổi lễ" },
        },
        {
          q: "Điều gì xảy ra nếu thiếu giấy tờ?",
          a: "Khi đó mọi việc sẽ chậm lại: nếu Familieretshuset yêu cầu bổ sung, hồ sơ sẽ chuyển sang một hàng chờ riêng. Vào giữa tháng 10/2026, hàng chờ này đang xử lý hồ sơ từ tháng 8, tức chậm khoảng 2 tháng. Vì thế chúng tôi kiểm tra từng giấy tờ trước khi nộp.",
        },
        {
          q: "Giấy phép kết hôn có hiệu lực bao lâu?",
          a: "Bốn tháng kể từ ngày cấp. Ngày cưới của các bạn phải nằm trong khoảng thời gian này, nếu không các bạn phải nộp hồ sơ mới và đóng phí mới.",
        },
        {
          q: "Chúng tôi có nhận được giấy chứng nhận kết hôn ngay không?",
          a: "Có. Ngay sau buổi lễ, các bạn nhận được giấy chứng nhận kết hôn quốc tế bằng nhiều ngôn ngữ; ví dụ tại Tønder là hai bản bằng tiếng Đan Mạch, Đức, Anh, Pháp và Tây Ban Nha.",
        },
        {
          q: "Chúng tôi có thể kết hôn vào thứ Bảy hoặc trên bãi biển không?",
          a: "Có, nhưng không phải ở đâu cũng được. Tønder và Aabenraa không tổ chức lễ vào thứ Bảy cho các cặp đôi không cư trú tại Đan Mạch; vào thứ Bảy, các bạn có thể kết hôn chẳng hạn tại Copenhagen (10:00–12:40), Kolding (9:00–12:00, tự mời người làm chứng) và trên đảo Fanø (thứ Bảy tuần thứ ba hằng tháng). Đám cưới trên bãi biển có thể tổ chức chẳng hạn ở Aabenraa, với người làm chứng do các bạn tự mời.",
          link: { href: "/ratgeber/samstag-strand-hochzeit-daenemark", label: "Đám cưới vào thứ Bảy và trên bãi biển" },
        },
        {
          q: "Chúng tôi có thể đổi họ tại buổi lễ không?",
          a: "Không, nếu không cư trú tại Đan Mạch. Giấy chứng nhận ghi họ tên như trong hộ chiếu của các bạn vào ngày cưới; các bạn khai báo việc đổi họ sau đó tại cơ quan hộ tịch (Standesamt) nơi mình sinh sống.",
          link: { href: "/ratgeber/namensaenderung-nach-hochzeit-daenemark", label: "Đổi họ sau đám cưới" },
        },
        {
          q: "Các cặp đôi đồng giới có thể kết hôn tại Đan Mạch không?",
          a: "Có. Đan Mạch tổ chức kết hôn cho các cặp đôi đồng giới từ năm 2012, theo cùng quy định như mọi cặp đôi khác. Đức, Áo và Thụy Sĩ công nhận cuộc hôn nhân này; hôn nhân bình đẳng được áp dụng tại các nước này lần lượt từ năm 2017, 2019 và 2022.",
        },
      ],
      es: [
        {
          q: "¿Podemos casarnos en Dinamarca aunque ninguno de los dos viva allí?",
          a: "Sí. Las parejas sin residencia en Dinamarca solicitan primero en línea a Familieretshuset, la Agencia danesa de Derecho de Familia, la revisión de su capacidad matrimonial. Con la aprobación reservan la cita en el municipio que elijan, por ejemplo en Tønder, Aabenraa o Copenhague.",
          link: { href: "/eignungs-check", label: "Comprueben en 60 segundos si su caso encaja" },
        },
        {
          q: "¿Es el procedimiento danés jurídicamente serio o un “resquicio legal”?",
          a: "No, no es ningún resquicio legal. Familieretshuset decide según la ley danesa de matrimonio y revisa a cada pareja con los mismos criterios. Dinamarca simplemente ha mantenido un procedimiento ágil y lo ha abierto a parejas del extranjero. Si tienen dudas concretas sobre su caso, les recomendamos consultar a una abogada o un abogado local.",
        },
        {
          q: "¿Se reconoce realmente un matrimonio danés en Alemania, Austria y Suiza?",
          a: "Sí. En Alemania y Austria, el certificado de matrimonio danés ni siquiera necesita apostilla, gracias al Reglamento (UE) 2016/1191. En Suiza, notifican el matrimonio a través de la representación suiza (el Centro Consular Regional de Estocolmo); su hoja informativa no menciona ninguna apostilla, sino que exige el certificado de matrimonio original, sellado, firmado y con una antigüedad máxima de seis meses. Si lo presentan directamente ante la autoridad de supervisión cantonal, pregunten allí antes si requiere una apostilla. Si una autoridad exige una apostilla, nuestro servicio de apostilla (servicio adicional, €149,99) la tramita ante el Ministerio de Asuntos Exteriores de Dinamarca.",
          link: { href: "/ratgeber/anerkennung-daenische-ehe-deutschland", label: "El reconocimiento, paso a paso" },
        },
        {
          q: "¿Cuánto dura todo el proceso con ustedes?",
          a: "Con los documentos completos, normalmente de tres a seis semanas hasta la boda, según el municipio. Familieretshuset suele resolver las solicitudes completas en un plazo de 5 días hábiles; después, lo que cuenta es la fecha libre: Tønder celebra bodas los martes y jueves, Ærø da citas en un plazo de 10 semanas y en Copenhague la espera es de hasta 4 meses (datos de octubre de 2026).",
          link: { href: "/ratgeber/daenemark-standesamt-wartezeiten-2026", label: "Tiempos de espera actuales 2026" },
        },
        {
          q: "¿Cuánto cuesta la ceremonia en sí?",
          a: "En el ayuntamiento no se paga ninguna tasa por la ceremonia: Tønder y Aabenraa lo indican expresamente, y Copenhague ya no la cobra desde agosto de 2025. Lo único obligatorio es la tasa de Familieretshuset, 2.100 DKK (2026, unos €281), que no se reembolsa aunque cancelen. Nuestro paquete Essential cuesta €249,99 más esta tasa; Complete, €649,99 con la tasa incluida.",
          link: { href: "/preise", label: "Todos los precios y servicios" },
        },
        {
          q: "¿Qué documentos necesitamos?",
          a: "Después de la comprobación gratuita nos ponemos en contacto con ustedes personalmente y les enviamos una lista de documentos adaptada exactamente a su situación. Por lo general bastan el pasaporte o el documento de identidad y, si viven juntos en el extranjero, un comprobante de domicilio; según el caso se añaden la sentencia de divorcio, el certificado de defunción, un comprobante de estancia legal, los certificados de nacimiento de hijos en común o, si se solicitan, pruebas de la relación. Familieretshuset pide un certificado de soltería solo en casos concretos, y el certificado de capacidad matrimonial no lo pide nunca.",
          link: { href: "/ratgeber/dokumente-hochzeit-daenemark-2026", label: "Resumen completo de documentos" },
        },
        {
          q: "¿Tenemos que viajar los dos a Dinamarca?",
          a: "Sí, para la ceremonia ambos tienen que estar presentes en persona. Todo lo anterior se hace en línea y con acompañamiento personal: la solicitud, la revisión de documentos, la comunicación con las autoridades y la coordinación de la cita. En Tønder y Aabenraa no hay que registrarse el día anterior, así que allí pueden hacerlo todo en un solo día.",
          link: { href: "/ratgeber/grenznahe-standesaemter-daenemark", label: "Ayuntamientos cerca de la frontera: comparativa" },
        },
        {
          q: "¿Necesitamos un visado para la boda?",
          a: "Depende del pasaporte. Con un pasaporte de la UE, del EEE o de Suiza, no. Todos los demás deben estar legalmente en Dinamarca el día de la boda (§ 11 a de la ley danesa de matrimonio): según la nacionalidad, sin visado, con visado Schengen (basta un visado de turista) o con permiso de residencia. Familieretshuset exige el comprobante; figura en su lista personal de documentos y nosotros lo revisamos con antelación.",
          link: { href: "/ratgeber/heiraten-daenemark-ohne-wohnsitz", label: "Casarse en Dinamarca siendo extranjeros" },
        },
        {
          q: "¿Necesitamos testigos?",
          a: "Sí, dos. Muchos municipios los proporcionan gratis, pero no a cualquier hora: Tønder solo por la mañana (martes y jueves, 9:30–12:00), Aabenraa de lunes a jueves y Copenhague siempre. Si en Tønder llevan sus propios testigos, comuniquen sus nombres y direcciones al menos una semana antes.",
          link: { href: "/ratgeber/trauzeugen-daenemark", label: "Testigos: las reglas de cada municipio" },
        },
        {
          q: "¿Se celebra la ceremonia en alemán?",
          a: "Es posible. Según sus páginas oficiales, Tønder, Aabenraa, Haderslev, Kolding y Copenhague también celebran bodas en alemán; si no, en inglés o danés. Si alguno de ustedes no entiende ninguno de estos idiomas, necesitan un intérprete; para árabe, ruso, español o portugués lo organizamos a petición.",
          link: { href: "/ratgeber/dolmetscher-hochzeit-daenemark", label: "Intérpretes e idioma de la ceremonia" },
        },
        {
          q: "¿Qué pasa si faltan documentos?",
          a: "Entonces todo se ralentiza: si Familieretshuset pide documentación adicional, la solicitud pasa a una cola aparte. A mediados de octubre de 2026 allí se estaban tramitando solicitudes de agosto, es decir, unos 2 meses de retraso. Por eso revisamos cada documento antes de presentarlo.",
        },
        {
          q: "¿Cuánto tiempo es válida la aprobación?",
          a: "Cuatro meses desde la fecha de emisión. La fecha de la boda tiene que caer dentro de ese plazo; si no, necesitan una nueva solicitud con una nueva tasa.",
        },
        {
          q: "¿Recibimos el certificado de matrimonio en el acto?",
          a: "Sí. Justo después de la ceremonia reciben certificados de matrimonio internacionales en varios idiomas; en Tønder, por ejemplo, dos ejemplares en danés, alemán, inglés, francés y español.",
        },
        {
          q: "¿Podemos casarnos un sábado o en la playa?",
          a: "Sí, pero no en todas partes. Tønder y Aabenraa no ofrecen sábados a las parejas sin residencia en Dinamarca; los sábados celebran bodas, por ejemplo, Copenhague (10:00–12:40), Kolding (9:00–12:00, con testigos propios) y Fanø (cada tercer sábado del mes). En la playa pueden casarse, por ejemplo, en Aabenraa, con testigos propios.",
          link: { href: "/ratgeber/samstag-strand-hochzeit-daenemark", label: "Bodas en sábado y en la playa" },
        },
        {
          q: "¿Podemos cambiarnos el apellido en la ceremonia?",
          a: "No, sin residencia en Dinamarca no. El certificado muestra los nombres que figuran en sus pasaportes el día de la boda; el cambio de apellido lo declaran después en el registro civil de su lugar de residencia.",
          link: { href: "/ratgeber/namensaenderung-nach-hochzeit-daenemark", label: "Cambio de apellido después de la boda" },
        },
        {
          q: "¿Pueden casarse en Dinamarca las parejas del mismo sexo?",
          a: "Sí. Dinamarca casa a parejas del mismo sexo desde 2012, con las mismas reglas que a todas las demás parejas. Alemania, Austria y Suiza reconocen el matrimonio; allí el matrimonio igualitario rige desde 2017, 2019 y 2022, respectivamente.",
        },
      ],
    },
  },

  // ─────────────────────────────────────────── FOOTER / DOCK
  footer: {
    tagline: {
      de: "Wir begleiten Paare aus DACH und international zu ihrer Eheschliessung in Dänemark. Persönlich, ruhig und klar.",
      en: "We guide couples from DACH and worldwide to their marriage in Denmark, personally and calmly.",
      da: "Vi vejleder par fra DACH og internationalt til deres vielse i Danmark, personligt og roligt.",
      ar: "نرافق الأزواج من دول DACH (ألمانيا والنمسا وسويسرا) ومن حول العالم حتى زواجهم في الدنمارك، بشكل شخصي وهادئ.",
      uk: "Ми особисто й спокійно супроводжуємо пари з країн DACH і з усього світу до шлюбу в Данії.",
      ru: "Мы сопровождаем пары из стран DACH и со всего мира на пути к браку в Дании — лично и спокойно.",
      vi: "Chúng tôi đồng hành trực tiếp và từ tốn cùng các cặp đôi từ các nước DACH (Đức, Áo, Thụy Sĩ) và khắp thế giới đến hôn lễ tại Đan Mạch.",
      es: "Acompañamos a parejas de la región DACH (Alemania, Austria y Suiza) y de todo el mundo hasta su boda en Dinamarca. De forma personal, tranquila y clara.",
    },
    services: { de: "Leistungen", en: "Services", da: "Ydelser", ar: "الخدمات", uk: "Послуги", ru: "Услуги", vi: "Dịch vụ", es: "Servicios" },
    // One line in the footer column, so every list starts at the same height.
    locations: {
      de: "Trauungsorte",
      en: "Venues",
      da: "Vielsessteder",
      ar: "الأماكن",
      uk: "Місця церемоній",
      ru: "Места регистрации",
      vi: "Địa điểm",
      es: "Lugares de boda",
    },
    locationsHint: {
      de: "Weitere Kommunen auf Anfrage.",
      en: "More municipalities on request.",
      da: "Flere kommuner efter aftale.",
      ar: "بلديات أخرى عند الطلب.",
      uk: "Інші комуни — за запитом.",
      ru: "Другие коммуны — по запросу.",
      vi: "Các kommune khác theo yêu cầu.",
      es: "Otros municipios a petición.",
    },
    company: { de: "Unternehmen", en: "Company", da: "Virksomhed", ar: "الشركة", uk: "Компанія", ru: "Компания", vi: "Công ty", es: "Empresa" },
    // Footer link to /partner (partner programme, 12.10.2026).
    partnerProgram: { de: "Partnerprogramm", en: "Partner programme", da: "Partnerprogram", ar: "برنامج الشركاء", uk: "Партнерська програма", ru: "Партнёрская программа", vi: "Chương trình đối tác", es: "Programa de colaboradores" },
    // Footer link to /fallbeispiele (our cases, 01.11.2026).
    casesLink: { de: "Fallbeispiele", en: "Case studies", da: "Kundecases", ar: "تجارب الأزواج", uk: "Історії пар", ru: "Истории пар", vi: "Câu chuyện khách hàng", es: "Casos reales" },
    legal: { de: "Rechtliches", en: "Legal", da: "Juridisk", ar: "الشؤون القانونية", uk: "Правова інформація", ru: "Правовая информация", vi: "Pháp lý", es: "Información legal" },
    contact: { de: "Kontakt", en: "Contact", da: "Kontakt", ar: "التواصل", uk: "Контакти", ru: "Контакты", vi: "Liên hệ", es: "Contacto" },
    // aria-label of the footer's link columns.
    navLabel: { de: "Footer-Navigation", en: "Footer navigation", da: "Sidefodsnavigation", ar: "تنقل التذييل", uk: "Навігація у футері", ru: "Навигация в подвале сайта", vi: "Điều hướng chân trang", es: "Navegación del pie de página" },
    // Subject line of the footer's e-mail link.
    mailSubject: { de: "Anfrage", en: "Enquiry", da: "Forespørgsel", ar: "استفسار", uk: "Запит", ru: "Запрос", vi: "Yêu cầu tư vấn", es: "Consulta" },
    svcComplete: {
      de: "Complete: Rundum-Begleitung",
      en: "Complete: full service",
      da: "Complete: fuld service",
      ar: "Complete: خدمة متكاملة",
      uk: "Complete: повний супровід",
      ru: "Complete: полное сопровождение",
      vi: "Complete: dịch vụ trọn gói",
      es: "Complete: acompañamiento completo",
    },
    svcEssential: {
      de: "Essential: Selbst organisieren",
      en: "Essential: do it yourself",
      da: "Essential: gør det selv",
      ar: "Essential: تنفيذ ذاتي",
      uk: "Essential: самостійно",
      ru: "Essential: всё сами",
      vi: "Essential: tự thực hiện",
      es: "Essential: por su cuenta",
    },
    // The phone's narrow package tab: one word that still says what it is.
    svcApostilleTab: {
      de: "Apostille",
      en: "Apostille",
      da: "Apostille",
      ar: "الأبوستيل",
      uk: "Апостиль",
      ru: "Апостиль",
      vi: "Apostille",
      es: "Apostilla",
    },
    svcApostille: {
      de: "Apostille-Service",
      en: "Apostille Service",
      da: "Apostille-Service",
      ar: "خدمة الأبوستيل",
      uk: "Послуга апостилю",
      ru: "Услуга апостиля",
      vi: "Dịch vụ Apostille",
      es: "Servicio de apostilla",
    },
    // The inquiry form keeps a link on every page: since 03.10.2026 the
    // header's "Jetzt buchen" goes to the packages instead of /anfrage.
    // The per-nationality rules page (visa + document authentication), 17.10.2026.
    svcDates: { de: "Trautermine", en: "Wedding dates", da: "Vielsestider", ar: "مواعيد الزواج", uk: "Дати весіль", ru: "Даты регистрации", vi: "Ngày cưới", es: "Fechas de boda" },
    svcCountryRules: { de: "Länderregeln", en: "Rules by country", da: "Regler pr. land", ar: "قواعد كل بلد", uk: "Правила країн", ru: "Правила стран", vi: "Quy định theo nước", es: "Reglas por país" },
    svcInquiry: {
      de: "Unverbindlich anfragen",
      en: "Send an enquiry",
      da: "Send en uforpligtende forespørgsel",
      ar: "أرسلوا استفسارًا دون التزام",
      uk: "Надіслати запит без зобов’язань",
      ru: "Отправить запрос без обязательств",
      vi: "Gửi yêu cầu, không ràng buộc",
      es: "Consultar sin compromiso",
    },
    svcBeratung: {
      de: "Video-Beratung",
      en: "Video consultation",
      da: "Videorådgivning",
      ar: "استشارة عبر الفيديو",
      uk: "Відеоконсультація",
      ru: "Видеоконсультация",
      vi: "Tư vấn qua video",
      es: "Asesoría por videollamada",
    },
    contactPageLabel: {
      de: "Alle Kontaktwege",
      en: "All ways to reach us",
      da: "Alle kontaktmuligheder",
      ar: "جميع طرق التواصل معنا",
      uk: "Усі способи зв’язку",
      ru: "Все способы связи",
      vi: "Tất cả cách liên hệ",
      es: "Todas las vías de contacto",
    },
    locationCopenhagen: { de: "Kopenhagen", en: "Copenhagen", da: "København", ar: "كوبنهاغن", uk: "Копенгаген", ru: "Копенгаген", vi: "Copenhagen", es: "Copenhague" },
    locationToender: { de: "Tønder", en: "Tønder", da: "Tønder", ar: "Tønder", uk: "Теннер (Tønder)", ru: "Тённер (Tønder)", vi: "Tønder", es: "Tønder" },
    locationAeroe: { de: "Ærø", en: "Ærø", da: "Ærø", ar: "Ærø", uk: "Ере (Ærø)", ru: "Эрё (Ærø)", vi: "Ærø", es: "Ærø" },
    // The border towns with their own guide (footer, since 04.10.2026).
    locationAabenraa: { de: "Aabenraa", en: "Aabenraa", da: "Aabenraa", ar: "Aabenraa", uk: "Обенро (Aabenraa)", ru: "Обенро (Aabenraa)", vi: "Aabenraa", es: "Aabenraa" },
    locationSoenderborg: { de: "Sønderborg", en: "Sønderborg", da: "Sønderborg", ar: "Sønderborg", uk: "Сеннерборг (Sønderborg)", ru: "Сённерборг (Sønderborg)", vi: "Sønderborg", es: "Sønderborg" },
    locationHaderslev: { de: "Haderslev", en: "Haderslev", da: "Haderslev", ar: "Haderslev", uk: "Гадерслев (Haderslev)", ru: "Хадерслев (Haderslev)", vi: "Haderslev", es: "Haderslev" },
    legalImpressum: { de: "Impressum", en: "Imprint", da: "Kolofon", ar: "بيانات الناشر", uk: "Вихідні дані", ru: "Выходные данные", vi: "Thông tin pháp lý", es: "Aviso legal" },
    legalCookies: { de: "Cookie-Einstellungen", en: "Cookie settings", da: "Cookie-indstillinger", ar: "إعدادات ملفات تعريف الارتباط", uk: "Налаштування cookie", ru: "Настройки cookie", vi: "Cài đặt cookie", es: "Configuración de cookies" },
    legalDatenschutz: { de: "Datenschutz", en: "Privacy", da: "Privatlivspolitik", ar: "سياسة الخصوصية", uk: "Конфіденційність", ru: "Конфиденциальность", vi: "Chính sách bảo mật", es: "Privacidad" },
    legalAGB: { de: "AGB", en: "Terms", da: "Vilkår", ar: "الشروط والأحكام", uk: "Умови надання послуг", ru: "Условия оказания услуг", vi: "Điều khoản dịch vụ", es: "Términos y condiciones" },
    legalWiderruf: { de: "Widerruf", en: "Withdrawal", da: "Fortrydelse", ar: "حق الانسحاب", uk: "Відмова від договору", ru: "Отказ от договора", vi: "Quyền hủy hợp đồng", es: "Desistimiento" },
    // Entry point of the electronic withdrawal function (§ 356a BGB): the
    // label must say "Vertrag widerrufen" or something just as clear.
    legalWithdrawNow: { de: "Vertrag widerrufen", en: "Withdraw from contract", da: "Fortryd aftalen", ar: "الانسحاب من العقد", uk: "Відмовитися від договору", ru: "Отказаться от договора", vi: "Hủy hợp đồng", es: "Desistir del contrato" },
    legalVollmacht: { de: "Vollmacht", en: "Power of attorney", da: "Fuldmagt", ar: "التوكيل", uk: "Довіреність", ru: "Доверенность", vi: "Giấy ủy quyền", es: "Poder de representación" },
    securityLine: {
      de: "SSL · DSGVO · Stripe",
      en: "SSL · GDPR · Stripe",
      da: "SSL · GDPR · Stripe",
      ar: "SSL · GDPR · Stripe",
      uk: "SSL · GDPR · Stripe",
      ru: "SSL · GDPR · Stripe",
      vi: "SSL · GDPR · Stripe",
      es: "SSL · RGPD · Stripe",
    },
    paymentLine: {
      de: "Akzeptierte Zahlungsmethoden",
      en: "Accepted payment methods",
      da: "Accepterede betalingsmetoder",
      ar: "طرق الدفع المقبولة",
      uk: "Способи оплати",
      ru: "Принимаемые способы оплаты",
      vi: "Phương thức thanh toán được chấp nhận",
      es: "Métodos de pago aceptados",
    },
    replyWithin24h: {
      de: "Antwort innerhalb 24 h",
      en: "Reply within 24 h",
      da: "Svar inden 24 t",
      ar: "رد خلال 24 ساعة",
      uk: "Відповідь протягом 24 год",
      ru: "Ответ в течение 24 ч",
      vi: "Phản hồi trong 24 giờ",
      es: "Respuesta en 24 h",
    },
    hoursLine: {
      de: "Mo–So 8–22 Uhr · Schweizer Team",
      en: "Mon–Sun 8 am–10 pm · Swiss team",
      da: "Man–søn 8–22 · schweizisk team",
      ar: "من الاثنين إلى الأحد، 8 صباحًا–10 مساءً · فريق سويسري",
      uk: "Пн–Нд 8:00–22:00 · команда зі Швейцарії",
      ru: "Пн–вс 8:00–22:00 · команда из Швейцарии",
      vi: "T2–CN 8:00–22:00 · đội ngũ Thụy Sĩ",
      es: "Lun–dom 8:00–22:00 · equipo suizo",
    },
    contactBookingLabel: {
      de: "Beratungsgespräch buchen",
      en: "Book a consultation",
      da: "Book en konsultation",
      ar: "احجزوا استشارة",
      uk: "Записатися на консультацію",
      ru: "Записаться на консультацию",
      vi: "Đặt lịch tư vấn",
      es: "Reservar una asesoría",
    },
    copyright: {
      de: "© {year} Danish Wedding · Alle Rechte vorbehalten",
      en: "© {year} Danish Wedding · All rights reserved",
      da: "© {year} Danish Wedding · Alle rettigheder forbeholdes",
      ar: "© {year} Danish Wedding · جميع الحقوق محفوظة",
      uk: "© {year} Danish Wedding · Усі права захищено",
      ru: "© {year} Danish Wedding · Все права защищены",
      vi: "© {year} Danish Wedding · Bảo lưu mọi quyền",
      es: "© {year} Danish Wedding · Todos los derechos reservados",
    },
    regionLine: {
      de: "Schweiz · DACH · International",
      en: "Switzerland · DACH · worldwide",
      da: "Schweiz · DACH · international",
      ar: "سويسرا · DACH · حول العالم",
      uk: "Швейцарія · DACH · по всьому світу",
      ru: "Швейцария · DACH · по всему миру",
      vi: "Thụy Sĩ · DACH · toàn cầu",
      es: "Suiza · DACH · Internacional",
    },
    /** Statement of origin only — not a Swissness product designation. */
    madeWithPrefix: {
      de: "Aus der Schweiz mit",
      en: "From Switzerland with",
      da: "Fra Schweiz med",
      ar: "من سويسرا بكل",
      uk: "Зі Швейцарії з",
      ru: "Из Швейцарии с",
      vi: "Từ Thụy Sĩ với",
      es: "Desde Suiza con",
    },
    madeWithAria: {
      de: "Aus der Schweiz mit Liebe",
      en: "From Switzerland with love",
      da: "Fra Schweiz med kærlighed",
      ar: "من سويسرا بكل حب",
      uk: "Зі Швейцарії з любов’ю",
      ru: "Из Швейцарии с любовью",
      vi: "Từ Thụy Sĩ với tình yêu",
      es: "Desde Suiza con amor",
    },
  },

  dock: {
    // The phone's bar (→ /eignungs-check). Canonical label for this funnel
    // target; the hero button leads to the same check.
    checkNow: { de: "Kostenloser Check", en: "Free check", da: "Gratis tjek", ar: "الفحص المجاني", uk: "Безкоштовна перевірка", ru: "Бесплатная проверка", vi: "Kiểm tra miễn phí", es: "Comprobación gratuita" },
    /** Phone header pill: two pills share one row, so long labels get a short form. */
    checkShort: { de: "Kostenloser Check", en: "Free check", da: "Gratis tjek", ar: "الفحص المجاني", uk: "Перевірка", ru: "Проверка", vi: "Kiểm tra miễn phí", es: "Comprobar gratis" },
  },

  // ─────────────────────────────────────────── BREADCRUMB / HERO SHARED
  crumbs: {
    home: { de: "Start", en: "Home", da: "Forside", ar: "الرئيسية", uk: "Головна", ru: "Главная", vi: "Trang chủ", es: "Inicio" },
    inquiry: { de: "Anfrage", en: "Enquiry", da: "Forespørgsel", ar: "الاستفسار", uk: "Запит", ru: "Запрос", vi: "Yêu cầu tư vấn", es: "Consulta" },
    check: { de: "Eignungs-Check", en: "Free check", da: "Egnethedstjek", ar: "الفحص المجاني", uk: "Безкоштовна перевірка", ru: "Бесплатная проверка", vi: "Kiểm tra miễn phí", es: "Comprobación de requisitos" },
    docs: {
      de: "Dokumenten-Checkliste",
      en: "Document checklist",
      da: "Dokument-tjekliste",
      ar: "قائمة التحقق من الوثائق",
      uk: "Чек-лист документів",
      ru: "Чек-лист документов",
      vi: "Danh sách giấy tờ",
      es: "Lista de documentos",
    },
    about: { de: "Über uns", en: "About us", da: "Om os", ar: "من نحن", uk: "Про нас", ru: "О нас", vi: "Về chúng tôi", es: "Sobre nosotros" },
    einblicke: { de: "Einblicke", en: "Insights", da: "Indblik", ar: "لمحات", uk: "Враження", ru: "Взгляд изнутри", vi: "Góc nhìn", es: "Impresiones" },
    login: { de: "Login", en: "Login", da: "Login", ar: "تسجيل الدخول", uk: "Вхід", ru: "Вход", vi: "Đăng nhập", es: "Iniciar sesión" },
    thanks: { de: "Danke", en: "Thank you", da: "Tak", ar: "شكرًا لكم", uk: "Дякуємо", ru: "Спасибо", vi: "Cảm ơn", es: "Gracias" },
    legal: { de: "Rechtliches", en: "Legal", da: "Juridisk", ar: "الشؤون القانونية", uk: "Правова інформація", ru: "Правовая информация", vi: "Pháp lý", es: "Información legal" },
  },

  // ─────────────────────────────────────────── INQUIRY WIZARD (/anfrage)
  inquiry: {
    heroEyebrow: {
      de: "Unverbindlich · 5 Minuten · Antwort binnen 24\u00A0Stunden",
      en: "No commitment · 5 minutes · reply within 24\u00A0hours",
      da: "Uforpligtende · 5 minutter · svar inden for 24\u00A0timer",
      ar: "دون التزام · 5 دقائق · رد خلال 24 ساعة",
      uk: "Без зобов’язань · 5 хвилин · відповідь протягом 24\u00A0годин",
      ru: "Без обязательств · 5 минут · ответ в течение 24 часов",
      vi: "Không ràng buộc · 5 phút · phản hồi trong 24 giờ",
      es: "Sin compromiso · 5 minutos · respuesta en 24 horas",
    },
    title: {
      de: "Anfrage starten",
      en: "Start your enquiry",
      da: "Start jeres forespørgsel",
      ar: "أرسلوا استفساركم",
      uk: "Надішліть свій запит",
      ru: "Отправьте запрос",
      vi: "Gửi yêu cầu tư vấn",
      es: "Iniciar consulta",
    },
    lede: {
      de: "Fünf Minuten. Keine Verpflichtung. Wir melden uns innerhalb von 24\u00A0Stunden persönlich.",
      en: "Five minutes. No commitment. We'll get back to you personally within 24\u00A0hours.",
      da: "Fem minutter. Ingen forpligtelse. Vi vender personligt tilbage inden for 24\u00A0timer.",
      ar: "خمس دقائق. دون التزام. نعود إليكم شخصيًا خلال 24 ساعة.",
      uk: "П’ять хвилин. Без зобов’язань. Ми особисто відповімо протягом 24\u00A0годин.",
      ru: "Пять минут. Без обязательств. Мы лично свяжемся с вами в течение 24 часов.",
      vi: "Năm phút. Không ràng buộc. Chúng tôi sẽ trực tiếp phản hồi trong vòng 24 giờ.",
      es: "Cinco minutos. Sin compromiso. Les respondemos personalmente en un plazo de 24 horas.",
    },
    step1: { de: "Paket", en: "Package", da: "Pakke", ar: "الباقة", uk: "Пакет", ru: "Пакет", vi: "Gói dịch vụ", es: "Paquete" },
    step2: { de: "Partner A", en: "Partner A", da: "Partner A", ar: "الطرف الأول", uk: "Партнер A", ru: "Партнёр А", vi: "Người A", es: "Persona A" },
    step3: { de: "Partner B", en: "Partner B", da: "Partner B", ar: "الطرف الثاني", uk: "Партнер B", ru: "Партнёр Б", vi: "Người B", es: "Persona B" },
    step4: { de: "Hochzeit", en: "Wedding", da: "Bryllup", ar: "الزواج", uk: "Весілля", ru: "Свадьба", vi: "Đám cưới", es: "Boda" },
    step5: { de: "Kontakt", en: "Contact", da: "Kontakt", ar: "التواصل", uk: "Контакти", ru: "Контакт", vi: "Liên hệ", es: "Contacto" },
    // Label of the wizard's last step (the confirmation), so the stepper names
    // what each step really asks for: Paket · Kontakt · Absenden.
    stepSend: { de: "Absenden", en: "Send", da: "Send", ar: "الإرسال", uk: "Надіслати", ru: "Отправить", vi: "Gửi", es: "Enviar" },
    pickPkg: {
      de: "Was interessiert euch?",
      en: "What are you interested in?",
      da: "Hvad er I interesserede i?",
      ar: "ما الذي يهمّكم؟",
      uk: "Що вас цікавить?",
      ru: "Что вас интересует?",
      vi: "Các bạn quan tâm đến gói nào?",
      es: "¿Qué les interesa?",
    },
    pickPkgSub: {
      de: "Ihr könnt das später jederzeit ändern. Der Apostille-Service ist ein Zusatz zu Essential oder Complete.",
      en: "You can change this later any time. The Apostille Service is an add-on to Essential or Complete.",
      da: "I kan ændre det når som helst senere. Apostille-Service er et tilvalg til Essential eller Complete.",
      ar: "يمكنكم تغيير ذلك لاحقًا في أي وقت. وخدمة الأبوستيل خدمة إضافية إلى Essential أو Complete.",
      uk: "Ви будь-коли зможете це змінити. Послуга апостилю — це доповнення до Essential або Complete.",
      ru: "Это можно изменить позже в любой момент. Услуга апостиля — дополнение к Essential или Complete.",
      vi: "Các bạn có thể thay đổi lựa chọn này bất cứ lúc nào. Dịch vụ Apostille là dịch vụ bổ sung cho gói Essential hoặc Complete.",
      es: "Pueden cambiarlo más adelante en cualquier momento. El servicio de apostilla es un servicio adicional a Essential o Complete.",
    },
    person1: { de: "Person 1", en: "Person 1", da: "Person 1", ar: "الشخص 1", uk: "Особа 1", ru: "Партнёр 1", vi: "Người 1", es: "Persona 1" },
    person2: { de: "Person 2", en: "Person 2", da: "Person 2", ar: "الشخص 2", uk: "Особа 2", ru: "Партнёр 2", vi: "Người 2", es: "Persona 2" },
    firstName: { de: "Vorname", en: "First name", da: "Fornavn", ar: "الاسم الأول", uk: "Ім’я", ru: "Имя", vi: "Tên", es: "Nombre" },
    lastName: { de: "Nachname", en: "Last name", da: "Efternavn", ar: "اسم العائلة", uk: "Прізвище", ru: "Фамилия", vi: "Họ", es: "Apellidos" },
    required: { de: "Pflichtfeld", en: "required", da: "skal udfyldes", ar: "إلزامي", uk: "обов’язково", ru: "обязательно", vi: "bắt buộc", es: "obligatorio" },
    gender: { de: "Geschlecht", en: "Gender", da: "Køn", ar: "الجنس", uk: "Стать", ru: "Пол", vi: "Giới tính", es: "Género" },
    genderHint: {
      de: "Pflicht-Angabe auf dem AFL-Antrag (optional jetzt, kann später ergänzt werden).",
      en: "Required on the AFL application (optional now, can be added later).",
      da: "Påkrævet på AFL-ansøgningen (valgfrit nu, kan tilføjes senere).",
      ar: "مطلوب في طلب AFL (اختياري الآن، ويمكن إضافته لاحقًا).",
      uk: "Обов’язкове поле в заяві до AFL (зараз необов’язково, можна додати пізніше).",
      ru: "Обязательное поле в заявлении в AFL (сейчас необязательно, можно добавить позже).",
      vi: "Bắt buộc trong hồ sơ AFL (hiện chưa bắt buộc, có thể bổ sung sau).",
      es: "Dato obligatorio en la solicitud a la AFL (ahora es opcional, se puede añadir más tarde).",
    },
    genderFemale: { de: "weiblich", en: "female", da: "kvinde", ar: "أنثى", uk: "жіноча", ru: "женский", vi: "nữ", es: "femenino" },
    genderMale: { de: "männlich", en: "male", da: "mand", ar: "ذكر", uk: "чоловіча", ru: "мужской", vi: "nam", es: "masculino" },
    genderDiverse: { de: "divers", en: "non-binary", da: "ikke-binær", ar: "غير ثنائي الجنس", uk: "небінарна", ru: "небинарный", vi: "phi nhị nguyên giới", es: "no binario" },
    genderPrefer: { de: "Keine Angabe", en: "Prefer not to say", da: "Vil ikke oplyse", ar: "أفضّل عدم الإفصاح", uk: "Не вказувати", ru: "Не указывать", vi: "Không muốn cho biết", es: "Sin especificar" },
    nationality: {
      de: "Staatsangehörigkeit",
      en: "Nationality",
      da: "Statsborgerskab",
      ar: "الجنسية",
      uk: "Громадянство",
      ru: "Гражданство",
      vi: "Quốc tịch",
      es: "Nacionalidad",
    },
    residence: {
      de: "Wohnsitz-Land",
      en: "Country of residence",
      da: "Bopælsland",
      ar: "بلد الإقامة",
      uk: "Країна проживання",
      ru: "Страна проживания",
      vi: "Quốc gia cư trú",
      es: "País de residencia",
    },
    marital: {
      de: "Familienstand",
      en: "Marital status",
      da: "Civilstand",
      ar: "الحالة الاجتماعية",
      uk: "Сімейний стан",
      ru: "Семейное положение",
      vi: "Tình trạng hôn nhân",
      es: "Estado civil",
    },
    birthDate: {
      de: "Geburtsdatum",
      en: "Date of birth",
      da: "Fødselsdato",
      ar: "تاريخ الميلاد",
      uk: "Дата народження",
      ru: "Дата рождения",
      vi: "Ngày sinh",
      es: "Fecha de nacimiento",
    },
    birthDateOpt: {
      de: "(optional in der Erst-Anfrage)",
      en: "(optional in the initial enquiry)",
      da: "(valgfri i første forespørgsel)",
      ar: "(اختياري في الاستفسار الأولي)",
      uk: "(необов’язково в першому запиті)",
      ru: "(в первом запросе необязательно)",
      vi: "(không bắt buộc trong yêu cầu tư vấn ban đầu)",
      es: "(opcional en la primera consulta)",
    },
    weddingTitle: {
      de: "Wann und wo soll's losgehen?",
      en: "When and where shall it happen?",
      da: "Hvornår og hvor skal det ske?",
      ar: "متى وأين تريدون الزواج؟",
      uk: "Коли і де це має відбутися?",
      ru: "Когда и где всё должно состояться?",
      vi: "Khi nào và ở đâu?",
      es: "¿Cuándo y dónde será?",
    },
    wishLocation: {
      de: "Wunschkommune in Dänemark",
      en: "Preferred Danish municipality",
      da: "Foretrukken dansk kommune",
      ar: "البلدية الدنماركية المفضّلة",
      uk: "Бажана комуна в Данії",
      ru: "Желаемая коммуна в Дании",
      vi: "Chính quyền địa phương (kommune) mong muốn tại Đan Mạch",
      es: "Municipio deseado en Dinamarca",
    },
    wishDate: {
      de: "Wunsch­datum (optional, ungefähr reicht)",
      en: "Preferred date (optional, approximate is fine)",
      da: "Ønsket dato (valgfri, cirka er fint)",
      ar: "الموعد المفضّل (اختياري، ويكفي موعد تقريبي)",
      uk: "Бажана дата (необов’язково, можна приблизно)",
      ru: "Желаемая дата (необязательно, можно приблизительно)",
      vi: "Ngày mong muốn (không bắt buộc, áng chừng cũng được)",
      es: "Fecha deseada (opcional, basta con una aproximada)",
    },
    contactTitle: {
      de: "Wie erreichen wir euch?",
      en: "How can we reach you?",
      da: "Hvordan når vi jer?",
      ar: "كيف يمكننا التواصل معكم؟",
      uk: "Як з вами зв’язатися?",
      ru: "Как с вами связаться?",
      vi: "Chúng tôi có thể liên hệ với các bạn bằng cách nào?",
      es: "¿Cómo los contactamos?",
    },
    email: { de: "E-Mail", en: "Email", da: "E-mail", ar: "البريد الإلكتروني", uk: "Електронна пошта", ru: "Электронная почта", vi: "Email", es: "Correo electrónico" },
    phone: {
      de: "Telefon / WhatsApp",
      en: "Phone / WhatsApp",
      da: "Telefon / WhatsApp",
      ar: "الهاتف / WhatsApp",
      uk: "Телефон / WhatsApp",
      ru: "Телефон / WhatsApp",
      vi: "Điện thoại / WhatsApp",
      es: "Teléfono / WhatsApp",
    },
    notes: {
      de: "Was sollen wir wissen? (optional)",
      en: "Anything we should know? (optional)",
      da: "Noget vi bør vide? (valgfrit)",
      ar: "هل هناك ما ينبغي أن نعرفه؟ (اختياري)",
      uk: "Щось, що нам варто знати? (необов’язково)",
      ru: "Что нам стоит знать? (необязательно)",
      vi: "Có điều gì chúng tôi nên biết không? (không bắt buộc)",
      es: "¿Algo que debamos saber? (opcional)",
    },
    notesPh: {
      de: "z.B. wir haben ein konkretes Datum, oder eine besondere Situation",
      en: "e.g. we have a specific date or a special situation",
      da: "f.eks. vi har en konkret dato eller en særlig situation",
      ar: "مثلًا: لدينا موعد محدد أو وضع خاص",
      uk: "напр., у нас є конкретна дата або особлива ситуація",
      ru: "например, у нас есть конкретная дата или особая ситуация",
      vi: "ví dụ: chúng tôi có ngày cụ thể hoặc hoàn cảnh đặc biệt",
      es: "p. ej., tenemos una fecha concreta o una situación especial",
    },
    namesContactSub: {
      de: "Nur die Basics, den Rest klären wir im persönlichen Gespräch.",
      en: "Just the basics, we’ll cover the rest in a personal call.",
      da: "Bare det grundlæggende, resten tager vi i en personlig samtale.",
      ar: "الأساسيات فقط، وسنتناول الباقي في مكالمة شخصية.",
      uk: "Лише основне — решту обговоримо під час особистої розмови.",
      ru: "Только самое основное — остальное обсудим в личном разговоре.",
      vi: "Chỉ những thông tin cơ bản, phần còn lại chúng tôi sẽ trao đổi trong một cuộc gọi riêng.",
      es: "Solo lo básico; el resto lo aclaramos en una conversación personal.",
    },
    finalTitle: {
      de: "Fast geschafft, eine Bestätigung noch",
      en: "Almost done, just one confirmation",
      da: "Næsten i mål, én bekræftelse mangler",
      ar: "أوشكتم على الانتهاء، تبقّى تأكيد واحد فقط",
      uk: "Майже готово, залишилося одне підтвердження",
      ru: "Почти готово, осталось одно подтверждение",
      vi: "Sắp xong rồi, chỉ cần một xác nhận",
      es: "Casi listo, solo falta una confirmación",
    },
    finalSub: {
      de: "Optional: wann passt euch ein Rückruf?",
      en: "Optional: when would suit you for a callback?",
      da: "Valgfrit: hvornår passer det med et tilbagekald?",
      ar: "اختياري: متى يناسبكم أن نعاود الاتصال بكم؟",
      uk: "Необов’язково: коли вам зручно, щоб ми передзвонили?",
      ru: "Необязательно: когда вам удобно, чтобы мы перезвонили?",
      vi: "Không bắt buộc: khi nào thuận tiện để chúng tôi gọi lại?",
      es: "Opcional: ¿cuándo les conviene que los llamemos?",
    },
    notesCallback: {
      de: "Wann passt euch ein Rückruf? (optional)",
      en: "When would suit you for a callback? (optional)",
      da: "Hvornår passer det med et tilbagekald? (valgfrit)",
      ar: "متى يناسبكم أن نعاود الاتصال بكم؟ (اختياري)",
      uk: "Коли вам зручно, щоб ми передзвонили? (необов’язково)",
      ru: "Когда вам удобно, чтобы мы перезвонили? (необязательно)",
      vi: "Khi nào thuận tiện để chúng tôi gọi lại? (không bắt buộc)",
      es: "¿Cuándo les conviene que los llamemos? (opcional)",
    },
    notesCallbackPh: {
      de: "z. B. werktags ab 18 Uhr, Mo–Fr nachmittags …",
      en: "e.g. weekdays after 6 pm, Mon–Fri afternoons …",
      da: "f.eks. hverdage efter kl. 18, man–fre eftermiddage …",
      ar: "مثلًا: أيام العمل بعد 6 مساءً، أو من الاثنين إلى الجمعة بعد الظهر …",
      uk: "напр., у робочі дні після 18:00, Пн–Пт у другій половині дня …",
      ru: "например, в будни после 18:00, пн–пт во второй половине дня …",
      vi: "ví dụ: ngày thường sau 18:00, chiều thứ Hai–thứ Sáu …",
      es: "p. ej., entre semana a partir de las 18:00, lun–vie por la tarde …",
    },
    phFirstName: {
      de: "z. B. Maria",
      en: "e.g. Maria",
      da: "f.eks. Maria",
      ar: "مثلًا: مريم",
      uk: "напр., Марія",
      ru: "например, Мария",
      vi: "ví dụ: Maria",
      es: "p. ej., María",
    },
    phLastName: {
      de: "z. B. Muster",
      en: "e.g. Smith",
      da: "f.eks. Hansen",
      ar: "مثلًا: الخطيب",
      uk: "напр., Шевченко",
      ru: "например, Иванова",
      vi: "ví dụ: Smith",
      es: "p. ej., García",
    },
    phEmail: {
      de: "eure@email.de",
      en: "you@email.com",
      da: "jer@email.dk",
      ar: "you@email.com",
      uk: "you@email.com",
      ru: "you@email.com",
      vi: "you@email.com",
      es: "nombre@email.com",
    },
    phPhone: {
      de: "z. B. +41 79 000 00 00",
      en: "e.g. +44 7… (for follow-up)",
      da: "f.eks. +45… (til opfølgning)",
      ar: "مثلًا: ‎+44 7… (للمتابعة)",
      uk: "напр., +380 … (для уточнень)",
      ru: "например, +44 7… (для уточнений)",
      vi: "ví dụ: +49 1… (để liên hệ lại)",
      es: "p. ej., +41 79 000 00 00",
    },
    hintWeddingDate: {
      de: "Tipp: ungefähr reicht. Ein Standesamt wird erst sinnvoll, wenn die Bescheinigung greifbar ist.",
      en: "Tip: approximate is fine, we don’t book a ceremony before the permit is in place.",
      da: "Tip: cirka er fint, vi booker ikke vielse, før tilladelsen er klar.",
      ar: "نصيحة: يكفي موعد تقريبي، فنحن لا نحجز موعد المراسم قبل صدور الموافقة.",
      uk: "Порада: приблизної дати достатньо — церемонію ми не бронюємо, доки немає дозволу.",
      ru: "Совет: можно указать приблизительно — церемонию мы бронируем только после получения разрешения.",
      vi: "Mẹo: ngày áng chừng cũng được, chúng tôi không đặt lịch lễ trước khi có giấy phép kết hôn.",
      es: "Consejo: basta con una fecha aproximada. La cita en el ayuntamiento solo tiene sentido cuando la aprobación está a la vista.",
    },
    consent: {
      de: "Ich stimme zu, dass meine Angaben zur Bearbeitung der Anfrage gespeichert werden. Details siehe",
      en: "I agree that my details will be stored to process this enquiry. For details, see our",
      da: "Jeg accepterer, at mine oplysninger gemmes for at behandle forespørgslen. Læs mere i vores",
      ar: "أوافق على حفظ بياناتي لمعالجة هذا الاستفسار. التفاصيل في",
      uk: "Я погоджуюся, що мої дані зберігатимуться для опрацювання цього запиту. Докладніше — у нашій",
      ru: "Я даю согласие на хранение моих данных для обработки этого запроса. Подробнее — в нашей",
      vi: "Tôi đồng ý để thông tin của mình được lưu trữ nhằm xử lý yêu cầu tư vấn này. Chi tiết xem tại",
      es: "Acepto que mis datos se guarden para tramitar la consulta. Más detalles en la",
    },
    privacyLink: {
      de: "Datenschutz­erklärung",
      en: "privacy policy",
      da: "privatlivspolitik",
      ar: "سياسة الخصوصية",
      uk: "політиці конфіденційності",
      ru: "политике конфиденциальности",
      vi: "chính sách bảo mật",
      es: "política de privacidad",
    },
    immediateStartTitle: {
      de: "Sofortiger Beginn der Dienstleistung (optional)",
      en: "Immediate start of service (optional)",
      da: "Øjeblikkelig start af ydelsen (valgfrit)",
      ar: "البدء الفوري بتقديم الخدمة (اختياري)",
      uk: "Негайний початок надання послуг (необов’язково)",
      ru: "Немедленное начало оказания услуг (необязательно)",
      vi: "Bắt đầu dịch vụ ngay (không bắt buộc)",
      es: "Inicio inmediato del servicio (opcional)",
    },
    immediateStartBody: {
      de: "Ich verlange ausdrücklich, dass ihr mit der Leistung bereits vor Ablauf der 14-tägigen Widerrufsfrist beginnt. Mir ist bekannt, dass mein Widerrufs­recht bei vollständiger Vertragserfüllung erlischt. Bei Widerruf vor vollständiger Erbringung schulde ich einen angemessenen Anteil der bis dahin erbrachten Leistung. Details:",
      en: "I expressly request that you begin the service before the 14-day withdrawal period ends. I understand that my right of withdrawal lapses upon full performance. If I withdraw before full performance, I owe a proportionate share of the service already rendered. Details:",
      da: "Jeg anmoder udtrykkeligt om, at I påbegynder ydelsen inden udløbet af den 14-dages fortrydelsesfrist. Jeg er bekendt med, at min fortrydelsesret bortfalder ved fuldstændig levering. Ved fortrydelse inden fuldstændig levering skylder jeg et rimeligt beløb for den hidtil leverede ydelse. Detaljer:",
      ar: "أطلب صراحةً أن تبدؤوا تقديم الخدمة قبل انتهاء مهلة الانسحاب البالغة 14 يومًا. وأعلم أن حقي في الانسحاب من العقد يسقط عند تنفيذ العقد بالكامل. وإذا انسحبت قبل التنفيذ الكامل، يترتب عليّ دفع حصة متناسبة من قيمة الخدمة المقدّمة حتى ذلك الحين. التفاصيل:",
      uk: "Я прямо вимагаю, щоб ви почали надавати послугу до спливу 14-денного строку на відмову від договору. Мені відомо, що моє право на відмову від договору припиняється після його повного виконання. Якщо я відмовлюся від договору до його повного виконання, я сплачую пропорційну частину вартості вже наданих послуг. Подробиці:",
      ru: "Настоящим я прямо прошу начать оказание услуг до истечения 14-дневного срока для отказа от договора. Мне известно, что право на отказ от договора утрачивается после его полного исполнения. Если я откажусь от договора до его полного исполнения, я обязан(а) оплатить соразмерную часть уже оказанных услуг. Подробнее:",
      vi: "Tôi yêu cầu rõ ràng rằng dịch vụ được bắt đầu thực hiện trước khi thời hạn hủy hợp đồng 14 ngày kết thúc. Tôi hiểu rằng quyền hủy hợp đồng của tôi sẽ chấm dứt khi hợp đồng đã được thực hiện đầy đủ. Nếu tôi hủy hợp đồng trước khi hợp đồng được thực hiện đầy đủ, tôi phải trả một khoản tương ứng với phần dịch vụ đã được cung cấp. Chi tiết:",
      es: "Solicito expresamente que comiencen a prestar el servicio antes de que finalice el plazo de desistimiento de 14 días. Sé que mi derecho de desistimiento se extingue una vez cumplido íntegramente el contrato. Si desisto antes de la prestación completa, deberé pagar una parte proporcional del servicio prestado hasta ese momento. Detalles:",
    },
    withdrawalLink: {
      de: "Widerrufs­belehrung",
      en: "Withdrawal policy",
      da: "Fortrydelsesret",
      ar: "تعليمات حق الانسحاب",
      uk: "Інформація про право на відмову",
      ru: "Уведомление о праве на отказ",
      vi: "Hướng dẫn về quyền hủy hợp đồng",
      es: "Información sobre el desistimiento",
    },
    trustNote: {
      de: "Private Beziehungsnachweise wie Paarfotos, Chatverläufe oder Social Media fragen wir nicht pauschal ab. Falls sie euren Fall stärken oder die dänische Behörde sie im Einzelfall verlangt, prüfen wir vorher, was minimal und passend ist.",
      en: "We do not collect private relationship evidence such as couple photos, chat history or social media by default. If the Danish authority asks for it in an individual case, we first review what is minimal and appropriate.",
      da: "Vi indsamler ikke private relationsbeviser som parbilleder, chatforløb eller sociale medier som standard. Hvis den danske myndighed beder om det i en konkret sag, vurderer vi først, hvad der er minimalt og relevant.",
      ar: "لا نجمع بشكل افتراضي أي أدلة خاصة على العلاقة، مثل صوركم معًا أو سجل المحادثات أو حسابات التواصل الاجتماعي. وإذا طلبتها الجهة الدنماركية في حالة بعينها، نراجع أولًا ما هو الحد الأدنى المناسب.",
      uk: "Ми не збираємо за замовчуванням приватні докази стосунків, як-от спільні фото, листування чи соцмережі. Якщо данський орган влади запитає їх у конкретному випадку, ми спершу визначимо, що є мінімально необхідним і доречним.",
      ru: "По умолчанию мы не собираем личные доказательства отношений, такие как совместные фото, переписка или соцсети. Если датское ведомство запросит их в конкретном случае, мы сначала оценим, что минимально необходимо и уместно.",
      vi: "Theo mặc định, chúng tôi không thu thập bằng chứng riêng tư về mối quan hệ như ảnh chụp chung, lịch sử trò chuyện hay mạng xã hội. Nếu cơ quan chức năng Đan Mạch yêu cầu trong một trường hợp cụ thể, trước hết chúng tôi xem xét điều gì là tối thiểu và phù hợp.",
      es: "No pedimos de forma generalizada pruebas privadas de la relación, como fotos de pareja, historiales de chat o redes sociales. Si fortalecen su caso o si la autoridad danesa las exige en un caso concreto, revisamos antes qué es lo mínimo y adecuado.",
    },
    trustNoteHeadline: {
      de: "Datensparsamkeit:",
      en: "Data minimisation:",
      da: "Dataminimering:",
      ar: "تقليل البيانات إلى الحد الأدنى:",
      uk: "Мінімізація даних:",
      ru: "Минимизация данных:",
      vi: "Tối thiểu hóa dữ liệu:",
      es: "Minimización de datos:",
    },
    back: { de: "Zurück", en: "Back", da: "Tilbage", ar: "رجوع", uk: "Назад", ru: "Назад", vi: "Quay lại", es: "Atrás" },
    next: { de: "Weiter", en: "Continue", da: "Videre", ar: "متابعة", uk: "Далі", ru: "Далее", vi: "Tiếp tục", es: "Continuar" },
    submit: {
      de: "Anfrage absenden",
      en: "Send enquiry",
      da: "Send forespørgsel",
      ar: "إرسال الاستفسار",
      uk: "Надіслати запит",
      ru: "Отправить запрос",
      vi: "Gửi yêu cầu",
      es: "Enviar consulta",
    },
    submitting: {
      de: "Wird gesendet …",
      en: "Sending…",
      da: "Sender …",
      ar: "جارٍ الإرسال…",
      uk: "Надсилаємо…",
      ru: "Отправка…",
      vi: "Đang gửi…",
      es: "Enviando…",
    },
    success: {
      de: "Danke! Eure Anfrage {id} ist bei uns. Wir melden uns binnen 24\u00A0Stunden.",
      en: "Thank you! Your enquiry {id} is with us. We'll be in touch within 24\u00A0hours.",
      da: "Tak! Jeres forespørgsel {id} er modtaget. Vi vender tilbage inden for 24\u00A0timer.",
      ar: "شكرًا لكم! وصلنا استفساركم {id}. سنتواصل معكم خلال 24 ساعة.",
      uk: "Дякуємо! Ваш запит {id} отримано. Ми зв’яжемося з вами протягом 24\u00A0годин.",
      ru: "Спасибо! Ваш запрос {id} у нас. Мы свяжемся с вами в течение 24 часов.",
      vi: "Cảm ơn các bạn! Chúng tôi đã nhận được yêu cầu {id}. Chúng tôi sẽ liên hệ trong vòng 24 giờ.",
      es: "¡Gracias! Hemos recibido su consulta {id}. Nos pondremos en contacto con ustedes en un plazo de 24 horas.",
    },
    // Validation messages of the inquiry form. lib/schemas.ts returns the German
    // text; InquiryWizard maps it to these keys so EN/DA show their language.
    errFirstName: { de: "Vorname erforderlich", en: "First name required", da: "Fornavn påkrævet", ar: "الاسم الأول مطلوب", uk: "Вкажіть ім’я", ru: "Укажите имя", vi: "Vui lòng nhập tên", es: "El nombre es obligatorio" },
    errLastName: { de: "Nachname erforderlich", en: "Last name required", da: "Efternavn påkrævet", ar: "اسم العائلة مطلوب", uk: "Вкажіть прізвище", ru: "Укажите фамилию", vi: "Vui lòng nhập họ", es: "Los apellidos son obligatorios" },
    errEmail: { de: "Bitte gültige E-Mail angeben", en: "Please enter a valid email address", da: "Angiv venligst en gyldig e-mailadresse", ar: "يرجى إدخال عنوان بريد إلكتروني صالح", uk: "Будь ласка, введіть дійсну адресу електронної пошти", ru: "Пожалуйста, укажите корректный адрес электронной почты", vi: "Vui lòng nhập địa chỉ email hợp lệ", es: "Por favor, indiquen un correo electrónico válido" },
    errPhone: { de: "Telefonnummer erforderlich", en: "Phone number required", da: "Telefonnummer påkrævet", ar: "رقم الهاتف مطلوب", uk: "Вкажіть номер телефону", ru: "Укажите номер телефона", vi: "Vui lòng nhập số điện thoại", es: "El teléfono es obligatorio" },
    errConsent: { de: "Datenschutz-Zustimmung erforderlich", en: "Please agree to the privacy terms", da: "Samtykke til privatlivspolitikken er påkrævet", ar: "يرجى الموافقة على شروط الخصوصية", uk: "Будь ласка, погодьтеся з умовами конфіденційності", ru: "Пожалуйста, дайте согласие на обработку данных", vi: "Vui lòng đồng ý với điều khoản bảo mật", es: "Es necesario aceptar la política de privacidad" },
    errNotes: { de: "Bitte kürzt den Text auf höchstens 2000 Zeichen.", en: "Please keep it to 2,000 characters or fewer.", da: "Forkort venligst teksten til højst 2.000 tegn.", ar: "يرجى اختصار النص إلى 2000 حرف كحدّ أقصى.", uk: "Будь ласка, скоротіть текст до 2000 символів.", ru: "Пожалуйста, сократите текст до 2000 символов.", vi: "Vui lòng rút gọn nội dung còn tối đa 2.000 ký tự.", es: "Por favor, acorten el texto a 2000 caracteres como máximo." },
    // country / marital / package / location labels
    countryDE: { de: "Deutschland", en: "Germany", da: "Tyskland", ar: "ألمانيا", uk: "Німеччина", ru: "Германия", vi: "Đức", es: "Alemania" },
    countryAT: { de: "Österreich", en: "Austria", da: "Østrig", ar: "النمسا", uk: "Австрія", ru: "Австрия", vi: "Áo", es: "Austria" },
    countryCH: { de: "Schweiz", en: "Switzerland", da: "Schweiz", ar: "سويسرا", uk: "Швейцарія", ru: "Швейцария", vi: "Thụy Sĩ", es: "Suiza" },
    countryOTHER: {
      de: "Anderes Land",
      en: "Another country",
      da: "Andet land",
      ar: "بلد آخر",
      uk: "Інша країна",
      ru: "Другая страна",
      vi: "Quốc gia khác",
      es: "Otro país",
    },
    maritalSingle: { de: "Ledig", en: "Single", da: "Ugift", ar: "عازب(ة)", uk: "Не в шлюбі", ru: "Холост / не замужем", vi: "Độc thân", es: "Soltero/\u2060a" },
    maritalDivorced: {
      de: "Geschieden",
      en: "Divorced",
      da: "Skilt",
      ar: "مطلّق(ة)",
      uk: "Розлучений(-а)",
      ru: "В разводе",
      vi: "Đã ly hôn",
      es: "Divor\u00ADciado/\u2060a",
    },
    maritalWidowed: {
      de: "Verwitwet",
      en: "Widowed",
      da: "Enke/Enkemand",
      ar: "أرمل(ة)",
      uk: "Вдівець / вдова",
      ru: "Вдовец / вдова",
      vi: "Góa",
      es: "Viudo/\u2060a",
    },
    maritalAnnulled: {
      de: "Annulliert",
      en: "Annulled",
      da: "Annulleret",
      ar: "زواج مُبطَل",
      uk: "Шлюб визнано недійсним",
      ru: "Брак признан недействительным",
      vi: "Hôn nhân đã bị hủy",
      es: "Matrimonio anulado",
    },
    locAero: {
      de: "Ærø (Insel, sehr beliebt)",
      en: "Ærø (island, very popular)",
      da: "Ærø (ø, meget populær)",
      ar: "Ærø (جزيرة، تحظى بإقبال كبير)",
      uk: "Ере (Ærø) — острів, дуже популярний",
      ru: "Эрё (Ærø) — остров, очень популярный",
      vi: "Ærø (đảo, rất được ưa chuộng)",
      es: "Ærø (isla, muy popular)",
    },
    locCopenhagen: {
      de: "Kopenhagen",
      en: "Copenhagen",
      da: "København",
      ar: "كوبنهاغن",
      uk: "Копенгаген",
      ru: "Копенгаген",
      vi: "Copenhagen",
      es: "Copenhague",
    },
    locTonder: {
      de: "Tønder (nahe DE-Grenze)",
      en: "Tønder (near the German border)",
      da: "Tønder (tæt på den tyske grænse)",
      ar: "Tønder (قرب الحدود الألمانية)",
      uk: "Теннер (Tønder) — біля кордону з Німеччиною",
      ru: "Тённер (Tønder) — у границы с Германией",
      vi: "Tønder (gần biên giới Đức)",
      es: "Tønder (cerca de la frontera alemana)",
    },
    locRibe: {
      de: "Ribe (älteste Stadt Dänemarks)",
      en: "Ribe (oldest town in Denmark)",
      da: "Ribe (Danmarks ældste by)",
      ar: "Ribe (أقدم مدينة في الدنمارك)",
      uk: "Рібе (Ribe) — найстаріше місто Данії",
      ru: "Рибе (Ribe) — старейший город Дании",
      vi: "Ribe (thị trấn lâu đời nhất Đan Mạch)",
      es: "Ribe (la ciudad más antigua de Dinamarca)",
    },
    locFrederiksberg: {
      de: "Frederiksberg",
      en: "Frederiksberg",
      da: "Frederiksberg",
      ar: "Frederiksberg",
      uk: "Фредеріксберг (Frederiksberg)",
      ru: "Фредериксберг (Frederiksberg)",
      vi: "Frederiksberg",
      es: "Frederiksberg",
    },
    locBornholm: {
      de: "Bornholm",
      en: "Bornholm",
      da: "Bornholm",
      ar: "Bornholm",
      uk: "Борнгольм (Bornholm)",
      ru: "Борнхольм (Bornholm)",
      vi: "Bornholm",
      es: "Bornholm",
    },
    locAabenraa: {
      de: "Aabenraa (nahe DE-Grenze)",
      en: "Aabenraa (near the German border)",
      da: "Aabenraa (tæt på den tyske grænse)",
      ar: "Aabenraa (قرب الحدود الألمانية)",
      uk: "Обенро (Aabenraa) — біля кордону з Німеччиною",
      ru: "Обенро (Aabenraa) — у границы с Германией",
      vi: "Aabenraa (gần biên giới Đức)",
      es: "Aabenraa (cerca de la frontera alemana)",
    },
    locSonderborg: {
      de: "Sønderborg (nahe DE-Grenze)",
      en: "Sønderborg (near the German border)",
      da: "Sønderborg (tæt på den tyske grænse)",
      ar: "Sønderborg (قرب الحدود الألمانية)",
      uk: "Сеннерборг (Sønderborg) — біля кордону з Німеччиною",
      ru: "Сённерборг (Sønderborg) — у границы с Германией",
      vi: "Sønderborg (gần biên giới Đức)",
      es: "Sønderborg (cerca de la frontera alemana)",
    },
    locHaderslev: {
      de: "Haderslev",
      en: "Haderslev",
      da: "Haderslev",
      ar: "Haderslev",
      uk: "Гадерслев (Haderslev)",
      ru: "Хадерслев (Haderslev)",
      vi: "Haderslev",
      es: "Haderslev",
    },
    locAny: {
      de: "Empfehlt uns die beste Option",
      en: "Recommend the best option",
      da: "Anbefal den bedste mulighed",
      ar: "اقترحوا علينا الخيار الأفضل",
      uk: "Порадьте найкращий варіант",
      ru: "Порекомендуйте лучший вариант",
      vi: "Nhờ tư vấn lựa chọn tốt nhất",
      es: "Recomiéndennos la mejor opción",
    },
    pkgFree: { de: "Free", en: "Free", da: "Free", ar: "مجاني", uk: "Безкоштовно", ru: "Бесплатно", vi: "Miễn phí", es: "Gratis" },
    pkgEssential: { de: "Essential", en: "Essential", da: "Essential", ar: "Essential", uk: "Essential", ru: "Essential", vi: "Essential", es: "Essential" },
    pkgComplete: { de: "Complete", en: "Complete", da: "Complete", ar: "Complete", uk: "Complete", ru: "Complete", vi: "Complete", es: "Complete" },
    pkgApostille: {
      de: "Nur Apostille-Zusatz (zu einem Paket)",
      en: "Apostille add-on only (with a package)",
      da: "Kun apostille-tilvalg (til en pakke)",
      ar: "خدمة الأبوستيل الإضافية فقط (مع باقة)",
      uk: "Лише апостиль як доповнення (до пакета)",
      ru: "Только апостиль (дополнительно к пакету)",
      vi: "Chỉ dịch vụ bổ sung Apostille (kèm một gói)",
      es: "Solo apostilla como servicio adicional (con un paquete)",
    },
    from: { de: "ab", en: "from", da: "fra", ar: "ابتداءً من", uk: "від", ru: "от", vi: "từ", es: "desde" },
    // Prices are fixed prices; do not prefix them with "from".
    fixedPrice: { de: "Festpreis", en: "fixed price", da: "fast pris", ar: "سعر ثابت", uk: "фіксована ціна", ru: "фиксированная цена", vi: "giá cố định", es: "precio fijo" },
    directLead: { de: "Lieber direkt?", en: "Prefer direct contact?", da: "Foretrækker I direkte kontakt?", ar: "تفضّلون التواصل المباشر؟", uk: "Бажаєте зв’язатися напряму?", ru: "Предпочитаете связаться напрямую?", vi: "Muốn liên hệ trực tiếp?", es: "¿Prefieren el contacto directo?" },
    addonApostilleSub: {
      de: "Apostille auf eurer dänischen Heiratsurkunde, Zusatz zu Essential oder Complete",
      en: "Apostille on your Danish marriage certificate, add-on to Essential or Complete",
      da: "Apostille på jeres danske vielsesattest, tilvalg til Essential eller Complete",
      ar: "الأبوستيل على شهادة زواجكم الدنماركية، خدمة إضافية إلى Essential أو Complete",
      uk: "Апостиль на вашому данському свідоцтві про шлюб, доповнення до Essential або Complete",
      ru: "Апостиль на ваше датское свидетельство о браке, дополнение к Essential или Complete",
      vi: "Chứng nhận Apostille cho giấy chứng nhận kết hôn Đan Mạch, bổ sung cho gói Essential hoặc Complete",
      es: "Apostilla sobre su certificado de matrimonio danés, servicio adicional a Essential o Complete",
    },
    // Add-ons and all municipalities in the form (operator, 11.10.2026).
    extrasTitle: {
      de: "Zusätzlich anfragen (optional)",
      en: "Also ask about (optional)",
      da: "Spørg også om (valgfrit)",
      ar: "استفسروا أيضًا عن (اختياري)",
      uk: "Також запитати про (необов’язково)",
      ru: "Также узнать о (необязательно)",
      vi: "Hỏi thêm về (không bắt buộc)",
      es: "Consultar también (opcional)",
    },
    // Wishes instead of one municipality (operator, 11.10.2026: "they can
    // also say: you decide, or close to Germany").
    locWishGroup: {
      de: "Nach Wunsch",
      en: "By preference",
      da: "Efter ønske",
      ar: "حسب الرغبة",
      uk: "За побажанням",
      ru: "По пожеланию",
      vi: "Theo mong muốn",
      es: "Según preferencia",
    },
    locWishNearGermany: {
      de: "Nahe der deutschen Grenze (Südjütland)",
      en: "Close to the German border (South Jutland)",
      da: "Tæt på den tyske grænse (Sønderjylland)",
      ar: "قرب الحدود الألمانية (جنوب يوتلاند)",
      uk: "Біля кордону з Німеччиною (Південна Ютландія)",
      ru: "Рядом с границей Германии (Южная Ютландия)",
      vi: "Gần biên giới Đức (Nam Jutland)",
      es: "Cerca de la frontera alemana (Jutlandia del Sur)",
    },
    locWishNearCopenhagen: {
      de: "In oder bei Kopenhagen",
      en: "In or near Copenhagen",
      da: "I eller nær København",
      ar: "في كوبنهاغن أو بالقرب منها",
      uk: "У Копенгагені або поблизу",
      ru: "В Копенгагене или рядом",
      vi: "Ở hoặc gần Copenhagen",
      es: "En Copenhague o cerca",
    },
    locWishIsland: {
      de: "Auf einer Insel (z. B. Ærø, Fanø)",
      en: "On an island (e.g. Ærø, Fanø)",
      da: "På en ø (f.eks. Ærø, Fanø)",
      ar: "على جزيرة (مثل Ærø وFanø)",
      uk: "На острові (напр. Ærø, Fanø)",
      ru: "На острове (например, Ærø, Fanø)",
      vi: "Trên một hòn đảo (ví dụ Ærø, Fanø)",
      es: "En una isla (p. ej., Ærø, Fanø)",
    },
    locWishFastest: {
      de: "Hauptsache schnell: frühester freier Termin",
      en: "As soon as possible: earliest free date",
      da: "Hurtigst muligt: første ledige dato",
      ar: "في أقرب وقت: أول موعد متاح",
      uk: "Якнайшвидше: перша вільна дата",
      ru: "Как можно скорее: первая свободная дата",
      vi: "Càng sớm càng tốt: ngày trống sớm nhất",
      es: "Lo importante es la rapidez: la primera fecha libre",
    },
    locGroupPopular: {
      de: "Beliebt bei internationalen Paaren",
      en: "Popular with international couples",
      da: "Populære hos internationale par",
      ar: "الأكثر شعبية لدى الأزواج من جنسيات مختلفة",
      uk: "Популярні серед міжнародних пар",
      ru: "Популярны у международных пар",
      vi: "Được các cặp đôi quốc tế ưa chuộng",
      es: "Populares entre parejas internacionales",
    },
    locGroupAll: {
      de: "Alle weiteren Kommunen (A–Z)",
      en: "All other municipalities (A–Z)",
      da: "Alle øvrige kommuner (A–Å)",
      ar: "جميع البلديات الأخرى (أبجديًا)",
      uk: "Усі інші комуни (A–Z)",
      ru: "Все остальные коммуны (A–Z)",
      vi: "Tất cả các kommune khác (A–Z)",
      es: "Todos los demás municipios (A–Z)",
    },
    locHint: {
      de: "Nicht jede Kommune traut auswärtige Paare an jedem Tag. Wir prüfen eure Wunschkommune und schlagen bei Bedarf Alternativen vor.",
      en: "Not every municipality marries couples from abroad on every day. We check your preferred one and suggest alternatives if needed.",
      da: "Ikke alle kommuner vier par udefra hver dag. Vi tjekker jeres ønskekommune og foreslår alternativer, hvis det er nødvendigt.",
      ar: "لا تعقد كل البلديات الزواج للأزواج القادمين من الخارج في كل يوم. نتحقق من البلدية التي تفضّلونها ونقترح بدائل عند الحاجة.",
      uk: "Не кожна комуна щодня реєструє шлюби пар з-за кордону. Ми перевіримо бажану комуну й за потреби запропонуємо альтернативи.",
      ru: "Не каждая коммуна ежедневно регистрирует браки пар из-за рубежа. Мы проверим желаемую коммуну и при необходимости предложим альтернативы.",
      vi: "Không phải kommune nào cũng tổ chức kết hôn cho các cặp đôi từ nước ngoài vào mọi ngày. Chúng tôi kiểm tra kommune các bạn mong muốn và đề xuất phương án khác nếu cần.",
      es: "No todos los municipios casan a parejas de fuera todos los días. Revisamos el municipio que desean y, si hace falta, les proponemos alternativas.",
    },
  },

  // ─────────────────────────────────────────── ELIGIBILITY WIZARD
  eligibility: {
    step1: { de: "Person 1", en: "Person 1", da: "Person 1", ar: "الشخص 1", uk: "Особа 1", ru: "Партнёр 1", vi: "Người 1", es: "Persona 1" },
    step2: { de: "Person 2", en: "Person 2", da: "Person 2", ar: "الشخص 2", uk: "Особа 2", ru: "Партнёр 2", vi: "Người 2", es: "Persona 2" },
    step3: {
      de: "Aufenthalt",
      en: "Residence",
      da: "Ophold",
      ar: "الإقامة",
      uk: "Проживання",
      ru: "Пребывание",
      vi: "Cư trú",
      es: "Residencia",
    },
    standardCaseTitle: {
      de: "Standardfall: EU/EWR/CH",
      en: "Standard case: EU/EEA/CH",
      da: "Standardtilfælde: EU/EØS/CH",
      ar: "حالة اعتيادية: الاتحاد الأوروبي/المنطقة الاقتصادية الأوروبية/سويسرا",
      uk: "Стандартний випадок: ЄС/ЄЕЗ/Швейцарія",
      ru: "Стандартный случай: ЕС/ЕЭЗ/Швейцария",
      vi: "Trường hợp tiêu chuẩn: EU/EEA/CH",
      es: "Caso estándar: UE/EEE/CH",
    },
    standardCaseBody: {
      de: "Mit gültigem EU/EWR/Schweizer Pass gibt es keine zusätzlichen Aufenthaltsanforderungen. Klickt direkt auf „Auswerten“.",
      en: "With a valid EU/EEA/Swiss passport no additional residence requirements apply. Click “Evaluate” to continue.",
      da: "Med gyldigt EU/EØS/CH-pas gælder ingen yderligere opholdskrav. Klik på »Evaluér« for at fortsætte.",
      ar: "مع جواز سفر ساري المفعول من الاتحاد الأوروبي أو المنطقة الاقتصادية الأوروبية أو سويسرا، لا تنطبق أي شروط إضافية تتعلق بالإقامة. انقروا على «تقييم» للمتابعة.",
      uk: "З дійсним паспортом ЄС/ЄЕЗ/Швейцарії додаткових вимог щодо перебування немає. Натисніть «Оцінити», щоб продовжити.",
      ru: "С действительным паспортом ЕС/ЕЭЗ/Швейцарии дополнительных требований к пребыванию нет. Нажмите «Оценить», чтобы продолжить.",
      vi: "Với hộ chiếu EU/EEA/Thụy Sĩ còn hiệu lực, không có yêu cầu bổ sung nào về cư trú. Bấm “Đánh giá” để tiếp tục.",
      es: "Con un pasaporte válido de la UE, del EEE o de Suiza no hay requisitos adicionales de residencia. Hagan clic directamente en “Evaluar”.",
    },
    evaluate: {
      de: "Auswerten",
      en: "Evaluate",
      da: "Evaluér",
      ar: "تقييم",
      uk: "Оцінити",
      ru: "Оценить",
      vi: "Đánh giá",
      es: "Evaluar",
    },
    pickOne: {
      de: "Bitte wählt eine Antwort.",
      en: "Please choose an answer.",
      da: "Vælg venligst et svar.",
      ar: "يرجى اختيار إجابة.",
      uk: "Будь ласка, оберіть відповідь.",
      ru: "Пожалуйста, выберите ответ.",
      vi: "Vui lòng chọn một câu trả lời.",
      es: "Elijan una respuesta.",
    },
    noEmailNote: {
      de: "Ergebnis sofort im Browser – ohne Angabe einer E-Mail-Adresse.",
      en: "Result instantly in your browser — no email address required.",
      da: "Resultat med det samme i browseren – ingen e-mailadresse påkrævet.",
      ar: "النتيجة فورًا في متصفحكم، دون الحاجة إلى عنوان بريد إلكتروني.",
      uk: "Результат одразу у вашому браузері — без адреси електронної пошти.",
      ru: "Результат сразу в браузере — без указания адреса электронной почты.",
      vi: "Xem kết quả ngay trên trình duyệt — không cần địa chỉ email.",
      es: "Resultado al instante en el navegador – sin indicar ningún correo electrónico.",
    },
    nationality: {
      de: "Staatsangehörigkeit",
      en: "Nationality",
      da: "Statsborgerskab",
      ar: "الجنسية",
      uk: "Громадянство",
      ru: "Гражданство",
      vi: "Quốc tịch",
      es: "Nacionalidad",
    },
    marital: {
      de: "Aktueller Familienstand",
      en: "Current marital status",
      da: "Nuværende civilstand",
      ar: "الحالة الاجتماعية الحالية",
      uk: "Поточний сімейний стан",
      ru: "Текущее семейное положение",
      vi: "Tình trạng hôn nhân hiện tại",
      es: "Estado civil actual",
    },
    residenceTitle: {
      de: "Aufenthalt zum Trauungs­zeitpunkt",
      en: "Residence at time of marriage",
      da: "Ophold på vielsestidspunktet",
      ar: "الإقامة وقت الزواج",
      uk: "Проживання на момент укладення шлюбу",
      ru: "Пребывание на момент регистрации брака",
      vi: "Tình trạng lưu trú vào thời điểm kết hôn",
      es: "Residencia en el momento de la boda",
    },
    thirdCountryAutoTitle: {
      de: "Eine Frage noch zum Aufenthalt",
      en: "One more question about your stay",
      da: "Ét spørgsmål mere om opholdet",
      ar: "سؤال أخير عن الإقامة",
      uk: "Ще одне запитання про перебування",
      ru: "Ещё один вопрос о пребывании",
      vi: "Thêm một câu hỏi về việc lưu trú",
      es: "Una pregunta más sobre la estancia",
    },
    thirdCountryAutoBody: {
      de: "Mindestens eine Person hat keinen EU-, EWR- oder Schweizer Pass. Dann will die dänische Behörde wissen, mit welchem Aufenthaltsrecht sie am Hochzeitstag in Dänemark ist.",
      en: "At least one of you has no EU, EEA or Swiss passport. The Danish authority then wants to know on what basis that person stays in Denmark on the wedding day.",
      da: "Mindst én af jer har ikke et EU-, EØS- eller schweizisk pas. Så vil den danske myndighed vide, på hvilket grundlag personen opholder sig i Danmark på bryllupsdagen.",
      ar: "أحدكما على الأقل لا يحمل جواز سفر من الاتحاد الأوروبي أو المنطقة الاقتصادية الأوروبية أو سويسرا. لذلك تريد الجهة الدنماركية أن تعرف على أي أساس يقيم هذا الشخص في الدنمارك يوم الزواج.",
      uk: "Щонайменше один із вас не має паспорта ЄС, ЄЕЗ чи Швейцарії. Тоді данський орган хоче знати, на якій підставі ця особа перебуває в Данії в день весілля.",
      ru: "Хотя бы у одного из вас нет паспорта ЕС, ЕЭЗ или Швейцарии. Тогда датское ведомство хочет знать, на каком основании этот человек находится в Дании в день свадьбы.",
      vi: "Ít nhất một người không có hộ chiếu EU, EEA hoặc Thụy Sĩ. Khi đó cơ quan Đan Mạch muốn biết người này lưu trú tại Đan Mạch vào ngày cưới theo diện nào.",
      es: "Al menos una persona no tiene pasaporte de la UE, del EEE ni de Suiza. En ese caso, la autoridad danesa quiere saber con qué derecho de estancia estará en Dinamarca el día de la boda.",
    },
    // "yes" skips the § 11 a condition in evaluate() (lib/eligibility.ts). A
    // visa is a short stay, not a residence right, so Schengen-visa holders
    // belong to "no": they must prove lawful stay on the wedding day.
    residencyQ: {
      de: "Hat die Person ohne EU-Pass eine Aufenthaltserlaubnis in einem EU- oder Schengen-Land?",
      en: "Does the person without an EU passport have a residence permit in an EU or Schengen country?",
      da: "Har personen uden EU-pas en opholdstilladelse i et EU- eller Schengenland?",
      ar: "هل لدى الشخص الذي لا يحمل جواز سفر أوروبيًا تصريح إقامة في دولة من الاتحاد الأوروبي أو منطقة شنغن؟",
      uk: "Чи має особа без паспорта ЄС посвідку на проживання в країні ЄС або Шенгену?",
      ru: "Есть ли у человека без паспорта ЕС вид на жительство в стране ЕС или Шенгенской зоны?",
      vi: "Người không có hộ chiếu EU có giấy phép cư trú tại một nước EU hoặc Schengen không?",
      es: "¿La persona sin pasaporte de la UE tiene un permiso de residencia en un país de la UE o del espacio Schengen?",
    },
    residencyYes: {
      de: "Ja (Aufenthaltstitel oder Aufenthaltskarte, kein Visum)",
      en: "Yes (residence permit or residence card, not a visa)",
      da: "Ja (opholdstilladelse eller opholdskort, ikke et visum)",
      ar: "نعم (تصريح إقامة أو بطاقة إقامة، وليس تأشيرة)",
      uk: "Так (посвідка на проживання або картка перебування, а не віза)",
      ru: "Да (вид на жительство или карта пребывания, не виза)",
      vi: "Có (giấy phép cư trú hoặc thẻ cư trú, không phải thị thực)",
      es: "Sí (permiso o tarjeta de residencia, no un visado)",
    },
    residencyNo: {
      de: "Nein, nur Besuch (visumfrei oder mit Schengen-Visum) / unklar",
      en: "No, visiting only (visa-free or with a Schengen visa) / unsure",
      da: "Nej, kun besøg (visumfrit eller med Schengen-visum) / uklart",
      ar: "لا، زيارة فقط (دون تأشيرة أو بتأشيرة شنغن) / غير متأكدين",
      uk: "Ні, лише візит (без візи або зі шенгенською візою) / не впевнені",
      ru: "Нет, только визит (без визы или по шенгенской визе) / не уверены",
      vi: "Không, chỉ đến thăm (miễn thị thực hoặc có thị thực Schengen) / không chắc",
      es: "No, solo de visita (sin visado o con visado Schengen) / no está claro",
    },
    back: { de: "Zurück", en: "Back", da: "Tilbage", ar: "رجوع", uk: "Назад", ru: "Назад", vi: "Quay lại", es: "Atrás" },
    next: { de: "Weiter", en: "Continue", da: "Videre", ar: "متابعة", uk: "Далі", ru: "Далее", vi: "Tiếp tục", es: "Continuar" },
    cntDE: { de: "Deutschland", en: "Germany", da: "Tyskland", ar: "ألمانيا", uk: "Німеччина", ru: "Германия", vi: "Đức", es: "Alemania" },
    cntAT: { de: "Österreich", en: "Austria", da: "Østrig", ar: "النمسا", uk: "Австрія", ru: "Австрия", vi: "Áo", es: "Austria" },
    cntCH: { de: "Schweiz", en: "Switzerland", da: "Schweiz", ar: "سويسرا", uk: "Швейцарія", ru: "Швейцария", vi: "Thụy Sĩ", es: "Suiza" },
    cntEU: {
      de: "Anderes EU-/EWR-Land",
      en: "Other EU/EEA country",
      da: "Andet EU/EØS-land",
      ar: "دولة أخرى في الاتحاد الأوروبي/المنطقة الاقتصادية الأوروبية",
      uk: "Інша країна ЄС/ЄЕЗ",
      ru: "Другая страна ЕС/ЕЭЗ",
      vi: "Nước EU/EEA khác",
      es: "Otro país de la UE/EEE",
    },
    cntPick: {
      de: "Anderes Land",
      en: "Another country",
      da: "Andet land",
      ar: "دولة أخرى",
      uk: "Інша країна",
      ru: "Другая страна",
      vi: "Nước khác",
      es: "Otro país",
    },
    cntChoose: {
      de: "Land auswählen",
      en: "Choose the country",
      da: "Vælg land",
      ar: "اختاروا الدولة",
      uk: "Оберіть країну",
      ru: "Выберите страну",
      vi: "Chọn quốc gia",
      es: "Elegir país",
    },
    cntOTHER: {
      de: "Anderes Land (Drittstaat)",
      en: "Other country (third country)",
      da: "Andet land (tredjeland)",
      ar: "دولة أخرى (دولة ثالثة)",
      uk: "Інша країна (третя країна)",
      ru: "Другая страна (третья страна)",
      vi: "Nước khác (nước thứ ba)",
      es: "Otro país (tercer país)",
    },
    marSingle: { de: "Ledig", en: "Single", da: "Ugift", ar: "عازب(ة)", uk: "Не в шлюбі", ru: "Холост / не замужем", vi: "Độc thân", es: "Soltero/\u2060a" },
    marDivorced: { de: "Geschieden", en: "Divorced", da: "Skilt", ar: "مطلّق(ة)", uk: "Розлучений(-а)", ru: "В разводе", vi: "Đã ly hôn", es: "Divor\u00ADciado/\u2060a" },
    marWidowed: {
      de: "Verwitwet",
      en: "Widowed",
      da: "Enke/Enkemand",
      ar: "أرمل(ة)",
      uk: "Вдівець / вдова",
      ru: "Вдовец / вдова",
      vi: "Góa",
      es: "Viudo/\u2060a",
    },
    marAnnulled: {
      de: "Ehe annulliert",
      en: "Marriage annulled",
      da: "Ægteskab annulleret",
      ar: "زواج مُبطَل",
      uk: "Шлюб визнано недійсним",
      ru: "Брак признан недействительным",
      vi: "Hôn nhân đã bị hủy",
      es: "Matrimonio anulado",
    },
    marMarried: {
      de: "Aktuell verheiratet / Scheidung nicht rechtskräftig",
      en: "Currently married / divorce not final",
      da: "Gift i øjeblikket / skilsmisse ikke endelig",
      ar: "متزوج(ة) حاليًا / الطلاق غير نهائي",
      uk: "Зараз у шлюбі / рішення про розлучення ще не набрало законної сили",
      ru: "Сейчас в браке / развод не вступил в силу",
      vi: "Hiện đang kết hôn / ly hôn chưa có hiệu lực pháp luật",
      es: "Actualmente casado/\u2060a / divorcio no firme",
    },
  },

  // ─────────────────────────────────────────── PRESS STRIP
  press: {
    eyebrow: {
      de: "Erwähnt in",
      en: "Featured in",
      da: "Omtalt i",
      ar: "كما ورد في",
      uk: "Про нас писали",
      ru: "О нас писали",
      vi: "Được nhắc đến trên",
      es: "Mencionados en",
    },
    // Outlet names deliberately do NOT live here. They were five invented
    // newspapers, which is misleading advertising plus a trademark issue.
    // The single source of truth is src/lib/pressMentions.ts, where every
    // entry carries a real URL and has to be verifiable.
  },

  // ─────────────────────────────────────────── QUIZ, PAKET-EMPFEHLUNG
  quiz: {
    eyebrow: {
      de: "In 20 Sekunden",
      en: "In 20 seconds",
      da: "På 20 sekunder",
      ar: "في 20 ثانية",
      uk: "За 20 секунд",
      ru: "За 20 секунд",
      vi: "Trong 20 giây",
      es: "En 20 segundos",
    },
    title: {
      de: "Welches Paket passt zu euch?",
      en: "Which package fits you?",
      da: "Hvilken pakke passer til jer?",
      ar: "أي باقة تناسبكم؟",
      uk: "Який пакет вам підходить?",
      ru: "Какой пакет вам подходит?",
      vi: "Gói nào phù hợp với các bạn?",
      es: "¿Qué paquete encaja con ustedes?",
    },
    lede: {
      de: "Drei Fragen, eine ehrliche Empfehlung. Kein Login, keine Mail.",
      en: "Three questions, one honest recommendation. No login, no email.",
      da: "Tre spørgsmål, én ærlig anbefaling. Ingen login, ingen e-mail.",
      ar: "ثلاثة أسئلة، وتوصية صادقة واحدة. دون تسجيل دخول، ودون بريد إلكتروني.",
      uk: "Три запитання — одна чесна рекомендація. Без входу, без електронної пошти.",
      ru: "Три вопроса — одна честная рекомендация. Без входа и без электронной почты.",
      vi: "Ba câu hỏi, một đề xuất trung thực. Không cần đăng nhập, không cần email.",
      es: "Tres preguntas, una recomendación honesta. Sin registro, sin correo.",
    },
    start: {
      de: "Empfehlung starten",
      en: "Start recommendation",
      da: "Start anbefaling",
      ar: "احصلوا على توصية",
      uk: "Отримати рекомендацію",
      ru: "Подобрать пакет",
      vi: "Bắt đầu nhận đề xuất",
      es: "Iniciar recomendación",
    },
    progress: {
      de: "Frage {n} von {total}",
      en: "Question {n} of {total}",
      da: "Spørgsmål {n} af {total}",
      ar: "السؤال {n} من {total}",
      uk: "Запитання {n} з {total}",
      ru: "Вопрос {n} из {total}",
      vi: "Câu hỏi {n}/{total}",
      es: "Pregunta {n} de {total}",
    },
    q1: {
      de: "Wie viel Zeit wollt ihr selbst investieren?",
      en: "How much time do you want to invest yourselves?",
      da: "Hvor meget tid vil I selv bruge?",
      ar: "كم من الوقت تريدون أن تخصّصوا بأنفسكم؟",
      uk: "Скільки часу ви готові витратити самі?",
      ru: "Сколько времени вы готовы потратить сами?",
      vi: "Các bạn muốn tự bỏ ra bao nhiêu thời gian?",
      es: "¿Cuánto tiempo quieren invertir ustedes mismos?",
    },
    q1a1: { de: "So wenig wie möglich", en: "As little as possible", da: "Så lidt som muligt", ar: "أقل قدر ممكن", uk: "Якомога менше", ru: "Как можно меньше", vi: "Càng ít càng tốt", es: "Lo menos posible" },
    q1a2: { de: "Ein paar Stunden sind ok", en: "A few hours is fine", da: "Et par timer er fint", ar: "لا بأس ببضع ساعات", uk: "Кілька годин — нормально", ru: "Несколько часов — нормально", vi: "Vài giờ thì được", es: "Unas horas está bien" },
    q1a3: { de: "Wir machen gerne selbst", en: "Happy to do it ourselves", da: "Vi gør det gerne selv", ar: "يسعدنا القيام بذلك بأنفسنا", uk: "Охоче зробимо все самі", ru: "С удовольствием сделаем сами", vi: "Sẵn lòng tự làm", es: "Nos gusta hacerlo nosotros" },
    q2: {
      de: "Habt ihr Urkunden aus einem Nicht-EU-Land?",
      en: "Any certificates from a non-EU country?",
      da: "Har I attester fra et ikke-EU-land?",
      ar: "هل لديكم شهادات من دولة خارج الاتحاد الأوروبي؟",
      uk: "Чи є документи з країни поза ЄС?",
      ru: "Есть ли документы из страны вне ЕС?",
      vi: "Có giấy tờ nào từ một nước ngoài EU không?",
      es: "¿Tienen certificados de un país fuera de la UE?",
    },
    q2a1: { de: "Ja", en: "Yes", da: "Ja", ar: "نعم", uk: "Так", ru: "Да", vi: "Có", es: "Sí" },
    q2a2: { de: "Nein", en: "No", da: "Nej", ar: "لا", uk: "Ні", ru: "Нет", vi: "Không", es: "No" },
    q2a3: { de: "Weiss nicht", en: "Not sure", da: "Ved ikke", ar: "غير متأكدين", uk: "Не впевнені", ru: "Не уверены", vi: "Không chắc", es: "No lo sé" },
    q3: {
      de: "Wie wichtig ist euch eine persönliche Ansprechperson?",
      en: "How important is a personal point of contact?",
      da: "Hvor vigtigt er en personlig kontaktperson?",
      ar: "ما مدى أهمية وجود جهة اتصال شخصية؟",
      uk: "Наскільки важлива для вас особиста контактна особа?",
      ru: "Насколько важно личное контактное лицо?",
      vi: "Một người liên hệ riêng quan trọng với các bạn đến mức nào?",
      es: "¿Qué importancia tiene para ustedes una persona de contacto propia?",
    },
    q3a1: { de: "Sehr wichtig", en: "Very important", da: "Meget vigtigt", ar: "مهمة جدًا", uk: "Дуже важлива", ru: "Очень важно", vi: "Rất quan trọng", es: "Muy importante" },
    q3a2: { de: "Nice to have", en: "Nice to have", da: "Godt at have", ar: "أمر جيد إن توفّر", uk: "Було б добре", ru: "Было бы неплохо", vi: "Có thì tốt", es: "Estaría bien" },
    q3a3: { de: "Nicht so wichtig", en: "Not really", da: "Ikke så vigtigt", ar: "ليست مهمة حقًا", uk: "Не дуже", ru: "Не особенно", vi: "Không hẳn", es: "No tan importante" },
    resultTitle: {
      de: "Unsere Empfehlung für euch",
      en: "Our recommendation for you",
      da: "Vores anbefaling til jer",
      ar: "توصيتنا لكم",
      uk: "Наша рекомендація для вас",
      ru: "Наша рекомендация для вас",
      vi: "Đề xuất của chúng tôi dành cho các bạn",
      es: "Nuestra recomendación para ustedes",
    },
    resultWhy: {
      de: "Weil ihr angegeben habt",
      en: "Because you said",
      da: "Fordi I har angivet",
      ar: "لأنكم قلتم",
      uk: "Бо ви вказали",
      ru: "Потому что вы указали",
      vi: "Vì các bạn đã trả lời",
      es: "Porque nos indicaron",
    },
    resultCta: {
      de: "Dieses Paket anfragen",
      en: "Request this package",
      da: "Forespørg denne pakke",
      ar: "اطلبوا هذه الباقة",
      uk: "Запитати про цей пакет",
      ru: "Запросить этот пакет",
      vi: "Yêu cầu gói này",
      es: "Consultar este paquete",
    },
    resultRestart: {
      de: "Antworten ändern",
      en: "Change answers",
      da: "Skift svar",
      ar: "تغيير الإجابات",
      uk: "Змінити відповіді",
      ru: "Изменить ответы",
      vi: "Thay đổi câu trả lời",
      es: "Cambiar respuestas",
    },
    recoComplete: {
      de: "Das Complete-Paket ist für euch die ruhigste Option. Wir übernehmen das ganze AFL-Verfahren, Behördenkommunikation und Terminabstimmung in eurer Wunschkommune. Apostille bucht ihr bei Bedarf als Add-on (€149,99) dazu.",
      en: "Complete is your calmest option, we handle the entire AFL process, authority communication and the appointment in your preferred municipality. Apostille is bookable as an add-on (€149.99) if needed.",
      da: "Complete er den roligste løsning, vi klarer hele AFL-forløbet, myndighedskontakt og tid i jeres ønskekommune. Apostille bestiller I som tillæg (€149,99) ved behov.",
      ar: "باقة Complete هي الخيار الأكثر راحة لكم: نتولّى إجراءات AFL كاملة، والتواصل مع الجهات الرسمية، والموعد في البلدية التي تفضّلونها. ويمكن حجز الأبوستيل كخدمة إضافية (€149.99) عند الحاجة.",
      uk: "Complete — найспокійніший варіант для вас: ми беремо на себе всю процедуру AFL, спілкування з органами влади та дату в бажаній комуні. Апостиль за потреби можна замовити як додаткову послугу (€149,99).",
      ru: "Complete — самый спокойный для вас вариант: мы берём на себя всю процедуру в AFL, общение с ведомствами и бронирование даты в выбранной вами коммуне. Апостиль при необходимости можно заказать дополнительно (€149,99).",
      vi: "Complete là lựa chọn an tâm nhất cho các bạn: chúng tôi lo toàn bộ thủ tục AFL, việc liên lạc với cơ quan chức năng và lịch hẹn tại kommune các bạn mong muốn. Có thể đặt thêm dịch vụ Apostille (€149,99) nếu cần.",
      es: "El paquete Complete es la opción más tranquila para ustedes. Nos encargamos de todo el procedimiento ante la AFL, de la comunicación con las autoridades y de coordinar la cita en el municipio que deseen. Si la necesitan, pueden añadir la apostilla como servicio adicional (€149,99).",
    },
    recoEssential: {
      de: "Das Essential-Paket passt euch gut. Ihr bekommt klare Anleitung, Vorprüfung und WhatsApp-Support, macht die Schritte aber selbst.",
      en: "Essential fits you well, you get a clear guide, pre-check and WhatsApp support, but you do the steps yourselves.",
      da: "Essential passer godt, I får en klar vejledning, forcheck og WhatsApp-support, men tager selv skridtene.",
      ar: "باقة Essential تناسبكم جيدًا: تحصلون على دليل واضح وفحص مسبق ودعم عبر WhatsApp، لكنكم تنفّذون الخطوات بأنفسكم.",
      uk: "Вам добре підходить Essential: ви отримуєте зрозумілу інструкцію, попередню перевірку та підтримку у WhatsApp, але всі кроки робите самі.",
      ru: "Вам хорошо подходит Essential: вы получаете понятную инструкцию, предварительную проверку и поддержку в WhatsApp, но шаги выполняете сами.",
      vi: "Essential phù hợp với các bạn: các bạn có hướng dẫn rõ ràng, bước kiểm tra trước và hỗ trợ qua WhatsApp, nhưng tự thực hiện các bước.",
      es: "El paquete Essential les encaja bien. Reciben una guía clara, una revisión previa y asistencia por WhatsApp, pero los pasos los dan ustedes mismos.",
    },
    recoApostille: {
      de: "Euer Kern-Bedarf ist die Apostille-Organisation. Das Add-on deckt genau das. Den Rest macht ihr unkompliziert selbst.",
      en: "Your core need is apostille logistics, this add-on covers exactly that. The rest you handle yourselves.",
      da: "Jeres kerne-behov er apostille-logistik, dette tilvalg dækker netop det. Resten klarer I selv.",
      ar: "حاجتكم الأساسية هي ترتيبات الأبوستيل، وهذه الخدمة الإضافية تغطي ذلك تحديدًا. أما الباقي فتتولّونه بأنفسكم.",
      uk: "Ваша головна потреба — логістика апостилю, і ця додаткова послуга покриває саме її. Решту ви робите самі.",
      ru: "Ваша главная потребность — логистика апостиля, и эта дополнительная услуга покрывает именно её. Остальное вы делаете сами.",
      vi: "Nhu cầu chính của các bạn là khâu Apostille, dịch vụ bổ sung này đáp ứng đúng điều đó. Phần còn lại các bạn tự lo.",
      es: "Lo que más necesitan es organizar la apostilla. El servicio adicional cubre exactamente eso. Lo demás lo hacen ustedes mismos sin complicaciones.",
    },
  },

  // ─────────────────────────────────────────── BEFORE / AFTER
  beforeAfter: {
    eyebrow: {
      de: "Der Unterschied",
      en: "The difference",
      da: "Forskellen",
      ar: "الفرق",
      uk: "Різниця",
      ru: "Разница",
      vi: "Sự khác biệt",
      es: "La diferencia",
    },
    title: {
      de: "Behördenmarathon oder ruhiger Weg?",
      en: "Paperwork marathon or calm path?",
      da: "Papirmaraton eller en rolig vej?",
      ar: "ماراثون أوراق أم طريق هادئ؟",
      uk: "Паперовий марафон чи спокійний шлях?",
      ru: "Бумажный марафон или спокойный путь?",
      vi: "Chạy marathon giấy tờ hay con đường thong thả?",
      es: "¿Maratón de trámites o un camino tranquilo?",
    },
    lede: {
      de: "Zieht den Slider und seht selbst, was sich verändert.",
      en: "Drag the slider, see for yourself what changes.",
      da: "Træk skyderen, se selv, hvad der ændrer sig.",
      ar: "اسحبوا شريط التمرير وشاهدوا بأنفسكم ما الذي يتغير.",
      uk: "Потягніть повзунок — і самі побачите, що змінюється.",
      ru: "Передвиньте ползунок и посмотрите сами, что меняется.",
      vi: "Kéo thanh trượt, tự mình thấy điều gì thay đổi.",
      es: "Deslicen el control y vean ustedes mismos lo que cambia.",
    },
    mobileLede: {
      de: "Zwei Wege im direkten Vergleich, kompakt für unterwegs.",
      en: "Two paths compared directly, compact for mobile.",
      da: "To veje sammenlignet direkte, kompakt på mobilen.",
      ar: "مقارنة مباشرة بين طريقين، بشكل مختصر للهاتف.",
      uk: "Пряме порівняння двох шляхів — компактно для мобільних.",
      ru: "Два пути в прямом сравнении — компактно для мобильных.",
      vi: "Hai con đường so sánh trực tiếp, gọn gàng cho điện thoại.",
      es: "Dos caminos frente a frente, en versión compacta para el teléfono.",
    },
    beforeLabel: {
      de: "Standesamt zu Hause",
      en: "Registry office at home",
      da: "Borgerservice hjemme",
      ar: "مكتب الأحوال المدنية في بلدكم",
      uk: "РАЦС удома",
      ru: "ЗАГС (Standesamt) дома",
      vi: "Cơ quan hộ tịch nơi đang sống",
      es: "Registro civil en su país",
    },
    afterLabel: {
      de: "Mit uns in Dänemark",
      en: "With us in Denmark",
      da: "Med os i Danmark",
      ar: "معنا في الدنمارك",
      uk: "З нами в Данії",
      ru: "С нами в Дании",
      vi: "Cùng chúng tôi tại Đan Mạch",
      es: "Con nosotros en Dinamarca",
    },
    beforeItems: {
      de: [
        "8–14\u00A0Wochen Wartezeit",
        "Beglaubigte Übersetzungen selbst organisieren",
        "Apostille für jedes Dokument",
        "Ämter schicken euch im Kreis",
        "Keine feste Ansprechperson",
        "€2.800–€4.500 Gesamtkosten",
      ],
      en: [
        "8–14\u00A0weeks waiting time",
        "Organise certified translations yourself",
        "Apostille for every document",
        "Authorities send you in circles",
        "No single point of contact",
        "€2,800–€4,500 total cost",
      ],
      da: [
        "8–14 ugers ventetid",
        "I skal selv organisere autoriserede oversættelser",
        "Apostille for hvert dokument",
        "Myndigheder sender jer rundt",
        "Ingen fast kontaktperson",
        "€2.800–€4.500 i alt",
      ],
      ar: [
        "8–14 أسبوعًا من الانتظار",
        "تنظيم الترجمات المعتمدة بأنفسكم",
        "أبوستيل لكل وثيقة",
        "الجهات الرسمية تجعلكم تدورون في حلقة مفرغة",
        "لا توجد جهة اتصال واحدة",
        "تكلفة إجمالية €2,800–€4,500",
      ],
      uk: [
        "8–14\u00A0тижнів очікування",
        "Засвідчені переклади — організовуйте самі",
        "Апостиль на кожен документ",
        "Установи ганяють вас по колу",
        "Немає єдиної контактної особи",
        "€2\u00A0800–€4\u00A0500 загальних витрат",
      ],
      ru: [
        "8–14 недель ожидания",
        "Самим организовывать заверенные переводы",
        "Апостиль на каждый документ",
        "Ведомства гоняют вас по кругу",
        "Нет единого контактного лица",
        "€2 800–€4 500 общих расходов",
      ],
      vi: [
        "Chờ 8–14 tuần",
        "Tự lo bản dịch công chứng",
        "Apostille cho từng giấy tờ",
        "Các cơ quan cứ đẩy qua đẩy lại",
        "Không có một đầu mối liên hệ duy nhất",
        "Tổng chi phí €2.800–€4.500",
      ],
      es: [
        "8–14 semanas de espera",
        "Organizar ustedes mismos las traducciones juradas",
        "Apostilla para cada documento",
        "Las oficinas los mandan de un lado a otro",
        "Sin una persona de contacto fija",
        "€2.800–€4.500 de costo total",
      ],
    },
    afterItems: {
      de: [
        "5–10\u00A0Werktage bis zum Termin",
        "Übersetzung und Apostille erledigen wir",
        "Ein WhatsApp-Chat, eine Ansprechperson",
        "Ihr reicht gar nichts selbst ein",
        "Termin in eurer Wunschkommune",
        "Festpreis €649,99. Alles inklusive",
      ],
      en: [
        "5–10 business days to the appointment",
        "We handle translation and apostille",
        "One WhatsApp chat, one contact person",
        "You don't file anything yourselves",
        "Date in your preferred municipality",
        "Fixed price €649.99. Everything included",
      ],
      da: [
        "5–10 hverdage til vielsen",
        "Vi klarer oversættelse og apostille",
        "Én WhatsApp-chat, én kontaktperson",
        "I indleverer intet selv",
        "Dato i jeres foretrukne kommune",
        "Fast pris €649,99. Alt inkluderet",
      ],
      ar: [
        "5–10 أيام عمل حتى الموعد",
        "نتولّى الترجمة والأبوستيل",
        "محادثة WhatsApp واحدة، وشخص واحد للتواصل",
        "لا تقدّمون أي شيء بأنفسكم",
        "موعد في البلدية التي تفضّلونها",
        "سعر ثابت €649.99. كل شيء مشمول",
      ],
      uk: [
        "5–10 робочих днів до дати",
        "Переклад і апостиль беремо на себе",
        "Один чат у WhatsApp, одна контактна особа",
        "Ви самі нічого не подаєте",
        "Дата в бажаній комуні",
        "Фіксована ціна €649,99. Усе включено",
      ],
      ru: [
        "5–10 рабочих дней до даты",
        "Перевод и апостиль берём на себя мы",
        "Один чат в WhatsApp, одно контактное лицо",
        "Вам не нужно ничего подавать самим",
        "Дата в выбранной вами коммуне",
        "Фиксированная цена €649,99. Всё включено",
      ],
      vi: [
        "5–10 ngày làm việc đến lịch hẹn",
        "Chúng tôi lo bản dịch và Apostille",
        "Một cuộc trò chuyện WhatsApp, một người liên hệ",
        "Các bạn không phải tự nộp gì",
        "Ngày cưới tại kommune các bạn mong muốn",
        "Giá cố định €649,99. Đã bao gồm tất cả",
      ],
      es: [
        "5–10 días hábiles hasta la cita",
        "Nos encargamos de la traducción y la apostilla",
        "Un chat de WhatsApp, una persona de contacto",
        "Ustedes no presentan nada por su cuenta",
        "Cita en el municipio que deseen",
        "Precio fijo €649,99. Todo incluido",
      ],
    },
    dragHint: {
      de: "ziehen",
      en: "drag",
      da: "træk",
      ar: "اسحبوا",
      uk: "потягніть",
      ru: "тяните",
      vi: "kéo",
      es: "arrastrar",
    },
  },

  // ─────────────────────────────────────────── STICKY MINI CTA
  stickyCta: {
    eyebrow: {
      de: "Empfohlenes Paket",
      en: "Recommended package",
      da: "Anbefalet pakke",
      ar: "الباقة الموصى بها",
      uk: "Рекомендований пакет",
      ru: "Рекомендуемый пакет",
      vi: "Gói được đề xuất",
      es: "Paquete recomendado",
    },
    label: {
      de: "Complete: alles aus einer Hand",
      en: "Complete: everything from one team",
      da: "Complete: alt fra ét team",
      ar: "Complete: كل شيء من فريق واحد",
      uk: "Complete: усе від однієї команди",
      ru: "Complete: всё от одной команды",
      vi: "Complete: mọi thứ từ một đội ngũ",
      es: "Complete: todo en un solo lugar",
    },
    price: {
      de: "€649,99 Service-Festpreis",
      en: "€649.99 service flat fee",
      da: "€649,99 service-fastpris",
      ar: "رسوم ثابتة للخدمة €649.99",
      uk: "€649,99 — фіксована вартість послуг",
      ru: "€649,99 — фиксированная стоимость услуг",
      vi: "Phí dịch vụ trọn gói €649,99",
      es: "€649,99 de precio fijo del servicio",
    },
    cta: {
      de: "Jetzt anfragen",
      en: "Start enquiry",
      da: "Start forespørgsel",
      ar: "أرسلوا استفسارًا",
      uk: "Надіслати запит",
      ru: "Отправить запрос",
      vi: "Gửi yêu cầu tư vấn",
      es: "Consultar ahora",
    },
  },

  // ─────────────────────────────────────────── WHATSAPP-PREVIEW-CHAT
  waPreview: {
    eyebrow: {
      de: "So läuft ein Erstkontakt",
      en: "What a first chat looks like",
      da: "Sådan ser en første chat ud",
      ar: "كيف تبدو المحادثة الأولى",
      uk: "Як відбувається перший контакт",
      ru: "Как выглядит первый чат",
      vi: "Một cuộc trò chuyện đầu tiên trông như thế nào",
      es: "Así es un primer contacto",
    },
    // The heading is title + italic accent, like every other section.
    title: {
      de: "Schreibt uns. Meist antworten wir",
      en: "Write to us. We usually reply",
      da: "Skriv til os. Vi svarer normalt",
      ar: "راسلونا. نرد عادةً",
      uk: "Напишіть нам. Зазвичай ми відповідаємо",
      ru: "Напишите нам. Обычно мы отвечаем",
      vi: "Hãy nhắn cho chúng tôi. Chúng tôi thường trả lời",
      es: "Escríbannos. Normalmente respondemos",
    },
    titleAccent: {
      de: "innerhalb weniger Stunden.",
      en: "within a few hours.",
      da: "inden for få timer.",
      ar: "خلال بضع ساعات.",
      uk: "протягом кількох годин.",
      ru: "в течение нескольких часов.",
      vi: "trong vài giờ.",
      es: "en pocas horas.",
    },
    online: {
      de: "online",
      en: "online",
      da: "online",
      ar: "متصل الآن",
      uk: "онлайн",
      ru: "в сети",
      vi: "trực tuyến",
      es: "en línea",
    },
    // The sample chat stays English on the Arabic and Ukrainian pages: we
    // answer in German, English or Russian, and a chat in Arabic or
    // Ukrainian would promise a service we do not offer (UWG).
    them1: {
      de: "Hi! Wir überlegen, in Dänemark zu heiraten. Wann wäre realistisch?",
      en: "Hi! We're thinking about marrying in Denmark, when would be realistic?",
      da: "Hej! Vi overvejer at blive gift i Danmark, hvornår er realistisk?",
      ar: "Hi! We're thinking about marrying in Denmark, when would be realistic?",
      uk: "Hi! We're thinking about marrying in Denmark, when would be realistic?",
      ru: "Здравствуйте! Мы думаем пожениться в Дании. Когда это реально?",
      vi: "Xin chào! Chúng tôi đang tính chuyện kết hôn tại Đan Mạch, khi nào thì khả thi?",
      es: "Hi! We're thinking about marrying in Denmark, when would be realistic?",
    },
    us1: {
      de: "Hallo! Schön, dass ihr schreibt 🌷 Wenn ihr diese Woche startet und bei der Kommune flexibel seid, sind 3–6\u00A0Wochen machbar.",
      en: "Hello! Lovely to hear from you 🌷 If you start this week and are flexible about the municipality, 3–6\u00A0weeks is doable.",
      da: "Hej! Dejligt at høre fra jer 🌷 Hvis I starter i denne uge og er fleksible med kommunen, er 3–6\u00A0uger realistisk.",
      ar: "Hello! Lovely to hear from you 🌷 If you start this week and are flexible about the municipality, 3–6 weeks is doable.",
      uk: "Hello! Lovely to hear from you 🌷 If you start this week and are flexible about the municipality, 3–6\u00A0weeks is doable.",
      ru: "Здравствуйте! Рады, что вы написали 🌷 Если начнёте на этой неделе и будете гибки в выборе коммуны, 3–6 недель — вполне реальный срок.",
      vi: "Xin chào! Rất vui khi nhận được tin nhắn của các bạn 🌷 Nếu các bạn bắt đầu trong tuần này và linh hoạt về kommune, 3–6 tuần là có thể.",
      es: "Hello! Lovely to hear from you 🌷 If you start this week and are flexible about the municipality, 3–6 weeks is doable.",
    },
    them2: {
      de: "Klingt super. Was müssen wir vorbereiten?",
      en: "Sounds great. What do we need to prepare?",
      da: "Lyder godt. Hvad skal vi forberede?",
      ar: "Sounds great. What do we need to prepare?",
      uk: "Sounds great. What do we need to prepare?",
      ru: "Звучит отлично. Что нам нужно подготовить?",
      vi: "Nghe tuyệt quá. Chúng tôi cần chuẩn bị những gì?",
      es: "Sounds great. What do we need to prepare?",
    },
    us2: {
      de: "Ich schicke euch gleich die Doku-Liste. Kurze Frage vorab: Wohnsitz in DE/AT/CH?",
      en: "I'll send you the doc checklist now. Quick question: residence in DE/AT/CH?",
      da: "Jeg sender jer dokumentlisten med det samme. Et hurtigt spørgsmål: bopæl i DE/AT/CH?",
      ar: "I'll send you the doc checklist now. Quick question: residence in DE/AT/CH?",
      uk: "I'll send you the doc checklist now. Quick question: residence in DE/AT/CH?",
      ru: "Сейчас пришлю вам чек-лист документов. Короткий вопрос: вы живёте в Германии, Австрии или Швейцарии?",
      vi: "Mình gửi các bạn danh sách giấy tờ ngay đây. Cho mình hỏi nhanh: các bạn có cư trú ở Đức, Áo hoặc Thụy Sĩ không?",
      es: "I'll send you the doc checklist now. Quick question: residence in DE/AT/CH?",
    },
    typing: {
      de: "schreibt…",
      en: "typing…",
      da: "skriver…",
      ar: "يكتب…",
      uk: "друкує…",
      ru: "печатает…",
      vi: "đang soạn tin…",
      es: "escribiendo…",
    },
    cta: {
      de: "Chat starten",
      en: "Start chat",
      da: "Start chat",
      ar: "ابدأوا المحادثة",
      uk: "Почати чат",
      ru: "Начать чат",
      vi: "Bắt đầu trò chuyện",
      es: "Iniciar chat",
    },
  },

  // ─────────────────────────────────────────── SLOT-KALENDER
  slots: {
    statusOpen: {
      de: "Jetzt buchbar",
      en: "Booking open",
      da: "Kan bookes nu",
      ar: "متاح للحجز الآن",
      uk: "Можна бронювати",
      ru: "Можно бронировать",
      vi: "Đang nhận đặt lịch",
      es: "Ya se puede reservar",
    },
    statusFew: {
      de: "Wenige Plätze frei",
      en: "Few spots left",
      da: "Få pladser tilbage",
      ar: "أماكن قليلة متبقية",
      uk: "Залишилося кілька місць",
      ru: "Осталось мало мест",
      vi: "Còn ít suất",
      es: "Quedan pocas plazas",
    },
  },

  // ─────────────────────────────────────────── PRICE-ANCHOR
  anchor: {
    eyebrow: {
      de: "Was es woanders kostet",
      en: "What it costs elsewhere",
      da: "Hvad det koster andre steder",
      ar: "كم يكلّف في أماكن أخرى",
      uk: "Скільки це коштує деінде",
      ru: "Сколько это стоит в других местах",
      vi: "Chi phí ở nơi khác",
      es: "Lo que cuesta en otros lugares",
    },
    usLabel: {
      de: "Mit uns in Dänemark",
      en: "With us in Denmark",
      da: "Med os i Danmark",
      ar: "معنا في الدنمارك",
      uk: "З нами в Данії",
      ru: "С нами в Дании",
      vi: "Cùng chúng tôi tại Đan Mạch",
      es: "Con nosotros en Dinamarca",
    },
    usPrice: {
      de: "€649,99",
      en: "€649.99",
      da: "€649,99",
      ar: "€649.99",
      uk: "€649,99",
      ru: "€649,99",
      vi: "€649,99",
      es: "€649,99",
    },
    usSub: {
      de: "klar kalkuliert, 5–10\u00A0Werktage",
      en: "transparent pricing, 5–10 business days",
      da: "gennemsigtig pris, 5–10 hverdage",
      ar: "أسعار شفافة، 5–10 أيام عمل",
      uk: "прозора ціна, 5–10 робочих днів",
      ru: "прозрачная цена, 5–10 рабочих дней",
      vi: "giá minh bạch, 5–10 ngày làm việc",
      es: "precio claro, 5–10 días hábiles",
    },
    altLabel: {
      de: "Standesamt in DE/AT/CH",
      en: "Registry office in DE/AT/CH",
      da: "Borgerservice i DE/AT/CH",
      ar: "مكتب الأحوال المدنية في ألمانيا/النمسا/سويسرا",
      uk: "РАЦС у Німеччині, Австрії чи Швейцарії",
      ru: "ЗАГС (Standesamt) в Германии, Австрии, Швейцарии",
      vi: "Cơ quan hộ tịch (Standesamt) tại Đức/Áo/Thụy Sĩ",
      es: "Registro civil en DE/AT/CH",
    },
    altPrice: {
      de: "€2.800–€4.500",
      en: "€2,800–€4,500",
      da: "€2.800–€4.500",
      ar: "€2,800–€4,500",
      uk: "€2\u00A0800–€4\u00A0500",
      ru: "€2 800–€4 500",
      vi: "€2.800–€4.500",
      es: "€2.800–€4.500",
    },
    altSub: {
      de: "Übersetzer, Apostille, 8–14\u00A0Wochen Wartezeit",
      en: "translators, apostille, 8–14\u00A0weeks waiting",
      da: "oversættere, apostille, 8–14 ugers ventetid",
      ar: "مترجمون، وأبوستيل، وانتظار 8–14 أسبوعًا",
      uk: "перекладачі, апостиль, 8–14\u00A0тижнів очікування",
      ru: "переводчики, апостиль, 8–14 недель ожидания",
      vi: "dịch thuật, Apostille, chờ 8–14 tuần",
      es: "traductores, apostilla, 8–14 semanas de espera",
    },
    savingsLabel: {
      de: "Ihr spart bis zu",
      en: "You save up to",
      da: "I sparer op til",
      ar: "توفّرون حتى",
      uk: "Ви заощаджуєте до",
      ru: "Вы экономите до",
      vi: "Các bạn tiết kiệm đến",
      es: "Ahorran hasta",
    },
    savingsValue: {
      de: "€4.200 + 10\u00A0Wochen",
      en: "€4,200 + 10\u00A0weeks",
      da: "€4.200 + 10\u00A0uger",
      ar: "€4,200 + 10 أسابيع",
      uk: "€4\u00A0200 + 10\u00A0тижнів",
      ru: "€4 200 + 10 недель",
      vi: "€4.200 + 10 tuần",
      es: "€4.200 + 10 semanas",
    },
  },

  // ─────────────────────────────────────────── TRUST-STRIP

  // ─────────────────────────────────────────── VIDEO STORY SPOTLIGHT
  videoStory: {
    eyebrow: {
      de: "Video-Einblick",
      en: "Video insight",
      da: "Video-indblik",
      ar: "لمحة بالفيديو",
      uk: "Відеоогляд",
      ru: "Видео изнутри",
      vi: "Video thực tế",
      es: "Un vistazo en video",
    },
    title: {
      de: "Seht selbst, wie ein Tag in Dänemark aussieht",
      en: "See for yourself what a day in Denmark looks like",
      da: "Se selv, hvordan en dag i Danmark ser ud",
      ar: "شاهدوا بأنفسكم كيف يبدو يوم في الدنمارك",
      uk: "Подивіться самі, який вигляд має день у Данії",
      ru: "Посмотрите сами, как проходит день в Дании",
      vi: "Tự mình xem một ngày tại Đan Mạch diễn ra thế nào",
      es: "Vean ustedes mismos cómo es un día en Dinamarca",
    },
    lede: {
      de: "Erste echte Video-Einblicke folgen, sobald die ersten begleiteten Paare in Dänemark verheiratet sind und ihr Einverständnis vorliegt.",
      en: "Real video insights will appear here as soon as our first accompanied couples are married in Denmark and have given their consent.",
      da: "Rigtige video-indblik vises her, så snart de første par er gift i Danmark og har givet deres samtykke.",
      ar: "ستظهر هنا لمحات حقيقية بالفيديو حالما يتزوج أول الأزواج الذين نرافقهم في الدنمارك ويمنحون موافقتهم.",
      uk: "Справжні відеоогляди з’являться тут, щойно перші пари, яких ми супроводжуємо, одружаться в Данії й дадуть свою згоду.",
      ru: "Настоящие видео появятся здесь, как только первые пары, которых мы сопровождали, поженятся в Дании и дадут своё согласие.",
      vi: "Video thực tế sẽ xuất hiện tại đây ngay khi những cặp đôi đầu tiên do chúng tôi đồng hành đã kết hôn tại Đan Mạch và đồng ý chia sẻ.",
      es: "Los primeros videos reales llegarán en cuanto las primeras parejas que acompañamos se hayan casado en Dinamarca y nos hayan dado su consentimiento.",
    },
    play: {
      de: "Video abspielen",
      en: "Play video",
      da: "Afspil video",
      ar: "تشغيل الفيديو",
      uk: "Відтворити відео",
      ru: "Воспроизвести видео",
      vi: "Phát video",
      es: "Reproducir video",
    },
    readStory: {
      de: "Mehr zum Ablauf",
      en: "More about the process",
      da: "Mere om processen",
      ar: "المزيد عن الإجراءات",
      uk: "Більше про процес",
      ru: "Подробнее о процессе",
      vi: "Tìm hiểu thêm về quy trình",
      es: "Más sobre el proceso",
    },
    comingSoon: {
      de: "Echtes Video folgt, keine Stock-Inszenierung",
      en: "Real video coming, no stock footage",
      da: "Rigtig video kommer, ingen stock-optagelser",
      ar: "فيديو حقيقي قريبًا، دون لقطات جاهزة",
      uk: "Справжнє відео незабаром, жодних стокових кадрів",
      ru: "Скоро настоящее видео, без стоковых кадров",
      vi: "Video thật sắp ra mắt, không dùng cảnh quay có sẵn",
      es: "Pronto un video real, sin escenas de archivo",
    },
    coupleName: {
      de: "Heiraten in Dänemark",
      en: "Getting married in Denmark",
      da: "Bryllup i Danmark",
      ar: "الزواج في الدنمارك",
      uk: "Одруження в Данії",
      ru: "Брак в Дании",
      vi: "Kết hôn tại Đan Mạch",
      es: "Casarse en Dinamarca",
    },
    coupleMeta: {
      de: "Echter Trauungstag · Inhalte folgen mit Einverständnis",
      en: "Real wedding day · content with consent only",
      da: "Rigtig bryllupsdag · indhold med samtykke",
      ar: "يوم زواج حقيقي · المحتوى بموافقة أصحابه فقط",
      uk: "Справжній день весілля · контент лише за згодою",
      ru: "Настоящий день свадьбы · материалы только с согласия",
      vi: "Ngày cưới thật · nội dung chỉ đăng khi được đồng ý",
      es: "Día de boda real · contenido pronto, con consentimiento",
    },
    duration: {
      de: "ca. 4 Min.",
      en: "approx. 4 min.",
      da: "ca. 4 min.",
      ar: "نحو 4 دقائق",
      uk: "близько 4 хв",
      ru: "ок. 4 мин",
      vi: "khoảng 4 phút",
      es: "aprox. 4 min",
    },
  },

  // ─────────────────────────────────────────── LIVE-ACTIVITY-TOAST
  liveActivity: {
    eyebrow: {
      de: "Aktuelle Aktivität",
      en: "Recent activity",
      da: "Seneste aktivitet",
      ar: "آخر النشاطات",
      uk: "Остання активність",
      ru: "Недавняя активность",
      vi: "Hoạt động gần đây",
      es: "Actividad reciente",
    },
    item1Title: {
      de: "Anna und Tom · Complete gebucht",
      en: "Anna and Tom · booked Complete",
      da: "Anna og Tom · booket Complete",
      ar: "Anna وTom · حجزا Complete",
      uk: "Анна й Том · забронювали Complete",
      ru: "Анна и Том · забронировали Complete",
      vi: "Anna và Tom · đã đặt gói Complete",
      es: "Anna y Tom · reservaron Complete",
    },
    item1Time: {
      de: "vor 2 Std · München",
      en: "2 hrs ago · Munich",
      da: "for 2 t siden · München",
      ar: "قبل ساعتين · ميونخ",
      uk: "2 год тому · Мюнхен",
      ru: "2 ч назад · Мюнхен",
      vi: "2 giờ trước · Munich",
      es: "hace 2 h · Múnich",
    },
    item2Title: {
      de: "Lisa und Mark · Eignungs-Check",
      en: "Lisa and Mark · compatibility check",
      da: "Lisa og Mark · egnethedstjek",
      ar: "Lisa وMark · فحص الأهلية",
      uk: "Ліза й Марк · перевірка можливості шлюбу",
      ru: "Лиза и Марк · проверка",
      vi: "Lisa và Mark · kiểm tra điều kiện",
      es: "Lisa y Mark · comprobación de requisitos",
    },
    item2Time: {
      de: "vor 18 Min · Wien",
      en: "18 min ago · Vienna",
      da: "for 18 min siden · Wien",
      ar: "قبل 18 دقيقة · فيينا",
      uk: "18 хв тому · Відень",
      ru: "18 мин назад · Вена",
      vi: "18 phút trước · Viên",
      es: "hace 18 min · Viena",
    },
    item3Title: {
      de: "Julia und Selin · Essential gebucht",
      en: "Julia and Selin · booked Essential",
      da: "Julia og Selin · booket Essential",
      ar: "Julia وSelin · حجزتا Essential",
      uk: "Юлія та Селін · забронювали Essential",
      ru: "Юлия и Селин · забронировали Essential",
      vi: "Julia và Selin · đã đặt gói Essential",
      es: "Julia y Selin · reservaron Essential",
    },
    item3Time: {
      de: "vor 46 Min · Zürich",
      en: "46 min ago · Zurich",
      da: "for 46 min siden · Zürich",
      ar: "قبل 46 دقيقة · زيورخ",
      uk: "46 хв тому · Цюрих",
      ru: "46 мин назад · Цюрих",
      vi: "46 phút trước · Zürich",
      es: "hace 46 min · Zúrich",
    },
  },
} as const;

export type TranslationKey = keyof typeof translations;

